import {
  buildDailyScheme,
  calculateCurrentIntake,
  calculateEnergy,
  calculateMacros,
  generateMenu,
  generateRecommendations,
  generateShoppingList,
  mealLabel,
  nafCatalog,
  objectiveRules,
  validatePatient,
} from "./lib/nutrition.js";
import {
  DEFAULT_ANTHROPOMETRY_SECTIONS,
  anthropometrySectionOptions,
  anthropometryInputFields,
  anthropometryMethodOptions,
  anthropometryGoalFieldNames,
  calculateAgeYears,
  calculateAnthropometry,
  getActiveAnthropometrySections,
  leanMassSourceOptions,
  nutritionalDiagnosisOptions,
  normalizeAnthropometrySections,
  resolvePatientAge,
} from "./lib/antropometria.js";
import {
  ASSESSMENT_REPORT_VERSION,
  buildAssessmentComparison,
  selectAssessmentReportConfig,
} from "./lib/assessmentReports.js";

const app = document.querySelector("#app");
const dataUrl = new URL("./data/base_nutricion.json", import.meta.url);

const STORAGE_PATIENT_FILES = "sistema-digital-nutricion:patientFiles";
const STORAGE_ACTIVE_FILE = "sistema-digital-nutricion:activePatientFile";
const STORAGE_NUTRITION_DATA = "sistema-digital-nutricion:nutritionDataOverride";
const STORAGE_PROFESSIONAL_VALIDATION = "sistema-digital-nutricion:professionalValidationDecisions";
const STORAGE_PRODUCT_ADMIN_TOKEN = "sistema-digital-nutricion:productAdminToken";
const STORAGE_PATIENT_CLOUD_TOKEN = "sistema-digital-nutricion:patientCloudToken";
const STORAGE_PATIENT_WORKSPACE_ID = "sistema-digital-nutricion:patientWorkspaceId";
const STORAGE_PATIENT_WORKSPACE_KEY = "sistema-digital-nutricion:patientWorkspaceKey";
const WORKSPACE_DB_NAME = "sistema-digital-nutricion-workspace";
const WORKSPACE_DB_VERSION = 1;
const WORKSPACE_KV_STORE = "workspace";
const DEFAULT_AUTO_SAVE_MINUTES = 1;
const DEFAULT_AUTO_SAVE_INTERVAL_MS = DEFAULT_AUTO_SAVE_MINUTES * 60 * 1000;
const CLOUD_AUTO_SAVE_DEBOUNCE_MS = 1800;
const MAX_STORED_FILES = 500;
const DEFAULT_FILE_TYPE = "Plan inicial";
const FILE_TYPE_OPTIONS = [DEFAULT_FILE_TYPE, "Seguimiento", "Ajuste", "Evento especial", "Seguimiento mensual", "Emergencia"];
const FILE_TYPE_CUSTOM_VALUE = "__custom_file_type__";
const PATIENT_FILE_EXPORT_KIND = "sistema_nutricion_patient_file";
const PATIENT_CLOUD_HASH_PREFIX = "nube=";
const REQUIRED_DATA_COLUMNS = ["id", "nombre", "categoria", "descripcion", "estado", "fuente", "ultima_actualizacion", "notas"];
const INTERNAL_PATIENT_PREFIX = "pac_ficticio_";
const PROFESSIONAL_VALIDATION_COMPLETED_STATES = new Set(["aprobado", "validado", "validado_no_aplica", "no_aplica", "cerrado"]);
const PROFESSIONAL_VALIDATION_DECISIONS = [
  ["", "Pendiente"],
  ["aprobado", "Aprobado"],
  ["aprobado_con_ajustes", "Aprobado con ajustes"],
  ["rechazado", "Rechazado"],
  ["requiere_fuente", "Requiere fuente"],
  ["no_aplica", "No aplica"],
];
const PROFESSIONAL_VALIDATION_CLOSING_DECISIONS = new Set(["aprobado", "no_aplica"]);
const PROFESSIONAL_VALIDATION_ACTION_DECISIONS = new Set(["aprobado_con_ajustes", "rechazado", "requiere_fuente"]);
const PROFESSIONAL_VALIDATION_CLOSED_STATES = new Set(["validado", "validado_no_aplica"]);
const PROFESSIONAL_VALIDATION_ACTION_STATES = new Set(["validado_con_ajustes", "rechazado", "requiere_fuente"]);
const DEFAULT_DELIVERABLE_OPTIONS = Object.freeze({
  title: "Plan de alimentación personalizado",
  finalNote: "Este plan es una herramienta de apoyo y debe ser revisado por un profesional de nutrición antes de entregarse o modificarse.",
  showCurrentIntake: true,
  showWarnings: true,
  showSupplements: true,
  showProducts: true,
  showRecipes: true,
  showShoppingList: true,
});
const DEFAULT_MEAL_TIME_IDS = ["desayuno", "media_manana", "almuerzo", "media_tarde", "cena"];
const MEAL_TIME_BY_ID = new Set([
  ...DEFAULT_MEAL_TIME_IDS,
  "racion_preentrenamiento",
  "racion_postentrenamiento",
]);
const MEAL_TIME_CATALOG = [...MEAL_TIME_BY_ID].map((id) => ({
  id,
  label: mealLabel(id),
}));
const DEFAULT_PATIENT_PROFILE = {
  nombre: "",
  edad: "",
  fecha_nacimiento: "",
  fecha_evaluacion: "",
  sexo: "",
  peso_kg: "",
  talla_cm: "",
  estatura_sentado_cm: "",
  envergadura_cm: "",
  peso_ideal_kg: "",
  peso_objetivo_kg: "",
  composicion_corporal: "",
  porcentaje_grasa: "",
  masa_muscular: "",
  masa_libre_grasa_kg: "",
  fuente_masa_libre_grasa: "rfm",
  metodo_antropometria: "yuhasz",
  population_profile: "adult",
  formula_profile: "legacy_excel_2026",
  numero_valoracion: "",
  assessment_date: "",
  etapa_entrenamiento: "",
  antropometria_secciones_activas: [...DEFAULT_ANTHROPOMETRY_SECTIONS],
  meta_yuhasz_suma_6_pliegues_mm: "",
  meta_yuhasz_cambio_muscular_kg: "",
  meta_durnin_suma_4_pliegues_mm: "",
  meta_durnin_cambio_muscular_kg: "",
  meta_rfm_cintura_ideal_cm: "",
  meta_5c_suma_pliegues_ideal_mm: "",
  meta_5c_objetivo_imo: "",
  pliegue_submandibular_mm: "",
  pliegue_triceps_mm: "",
  pliegue_biceps_mm: "",
  pliegue_subescapular_mm: "",
  pliegue_cresta_iliaca_mm: "",
  pliegue_supraespinal_mm: "",
  pliegue_abdominal_mm: "",
  pliegue_muslo_mm: "",
  pliegue_pantorrilla_mm: "",
  perimetro_cefalico_cm: "",
  perimetro_cuello_cm: "",
  perimetro_brazo_relajado_cm: "",
  perimetro_brazo_relajado_2_cm: "",
  perimetro_brazo_tenso_cm: "",
  perimetro_antebrazo_cm: "",
  perimetro_muneca_cm: "",
  perimetro_torax_cm: "",
  perimetro_cintura_cm: "",
  perimetro_abdomen_cm: "",
  perimetro_cadera_cm: "",
  perimetro_muslo_superior_cm: "",
  perimetro_muslo_medio_cm: "",
  perimetro_pantorrilla_cm: "",
  perimetro_tobillo_cm: "",
  longitud_acromiale_radiale_cm: "",
  longitud_radiale_stylion_cm: "",
  longitud_stylion_medio_dactylion_cm: "",
  longitud_ilioespinale_cm: "",
  longitud_trochanterion_cm: "",
  longitud_trochanterion_tibiale_laterale_cm: "",
  longitud_tibiale_laterale_cm: "",
  longitud_pie_cm: "",
  longitud_tibiale_mediale_sphyrion_tibiale_cm: "",
  diametro_biacromial_cm: "",
  diametro_biliocrestal_cm: "",
  diametro_bitrocantereo_cm: "",
  diametro_torax_transverso_cm: "",
  diametro_torax_ap_cm: "",
  diametro_biestiloideo_cm: "",
  diametro_humero_cm: "",
  diametro_femur_cm: "",
  diametro_bimaleolar_cm: "",
  diametro_pie_cm: "",
  diametro_abdomen_ap_cm: "",
  media_envergadura_cm: "",
  semi_brazada_cm: "",
  longitud_antebrazo_cm: "",
  rodilla_maleolo_cm: "",
  rodilla_talon_cm: "",
  consumo_calorias_actuales: "",
  consumo_proteina_actual_g: "",
  consumo_carbohidratos_actual_g: "",
  consumo_grasas_actual_g: "",
  meal_times: [],
  numero_tiempos_comida: "",
  objetivo_principal: "perdida_grasa",
  objetivo_secundario: "",
  estado_nutricional: "",
  actividad_ocupacional: "",
  actividad: "medio",
  naf_categoria: "moderada",
  naf_valor: "",
  deporte: "",
  entrenamientos_semana: "",
  duracion_entrenamiento_minutos: "",
  intensidad_entrenamiento: "",
  horarios_entrenamiento: "",
  habitos_alimentarios: "",
  preferencias_alimentarias: "",
  restricciones: "",
  intolerancias: "",
  alergias: "",
  condiciones: "",
  enfermedades_condiciones: "",
  presupuesto: "medio",
  alimentos_no_consume: "",
  alimentos_preferidos: "",
  restricciones_culturales: "",
  suplementos_actuales: "",
  observaciones_profesionales: "",
  kg_masa_adiposa_perder: "",
  dias_restriccion: "",
  kg_masa_muscular_ganar: "",
  dias_adicion: "",
  get_3: "",
  proteina_gkg: "",
  grasa_gkg: "",
  carbohidrato_gkg: "",
};

const sectionNavItems = [
  ["inicio", "Dashboard"],
  ["paciente", "Paciente"],
  ["antropometricos", "Antropometría y objetivos"],
  ["consumo_actual", "Consumo actual"],
  ["actividad", "Actividad y entrenamiento"],
  ["preferencias", "Preferencias y restricciones"],
  ["alertas", "Alertas"],
  ["calculos", "Cálculos"],
  ["esquema", "Esquema diario"],
  ["menu", "Menú 7 días"],
  ["recomendaciones", "Recomendaciones"],
  ["suplementos", "Suplementos"],
  ["productos", "Productos"],
  ["productos_admin", "Admin productos"],
  ["recetas", "Recetas"],
  ["base_datos", "Base de datos"],
  ["actualizacion", "Actualización"],
  ["entregable", "Personalización"],
  ["vista_previa", "Vista previa"],
];

const state = {
  data: null,
  defaultData: null,
  dataSourceLabel: "Base del proyecto",
  selectedDataTableName: "alimentos",
  dataHasPendingChanges: false,
  patient: null,
  scheme: [],
  menu: [],
  recommendations: [],
  selectedSupplements: new Set(["sup_whey"]),
  selectedProducts: new Set(["prd_yogurt_griego"]),
  selectedRecipes: new Set(["rec_avena_banano"]),
  planProducts: {
    search: "",
    retailer: "",
    mealId: "desayuno",
    day: "all",
    servingGrams: 100,
    results: [],
    links: [],
    loading: false,
    message: "",
    error: "",
  },
  sharedShoppingList: {
    creating: false,
    url: "",
    message: "",
    error: "",
  },
  supplementFormat: "destacado",
  patientFiles: [],
  currentFileId: null,
  currentPlanStartDate: formatDateForFile(),
  autoSaveIntervalId: null,
  autoSaveDebounceId: null,
  autoSaveIntervalMs: DEFAULT_AUTO_SAVE_INTERVAL_MS,
  newFileDraft: {
    name: "",
    type: DEFAULT_FILE_TYPE,
  },
  lastAutoSaveAt: null,
  saveStatus: "saved",
  storageEngine: "localStorage",
  isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  lastStorageError: "",
  patientCloud: {
    token: "",
    workspaceId: "",
    workspaceKey: "",
    syncing: false,
    message: "",
    error: "",
    lastSyncAt: "",
    available: false,
    status: "unknown",
    clinicalWorkspaceId: "",
    conflict: null,
  },
  assessments: [],
  activeAssessmentId: "",
  activeExportOptions: { includeAnthropometry: false, assessmentId: "" },
  exportDialog: null,
  activeSectionId: "inicio",
  hasPendingChanges: false,
  nextAutoSaveAt: null,
  autoSaveTickerId: null,
  shoppingChecklist: new Set(),
  deliverableOptions: normalizeDeliverableOptions(),
  scrollSpyEnabled: false,
  sidebarCollapsed: false,
  mobileNavOpen: false,
  professionalValidationDecisions: {},
  productAdmin: {
    products: [],
    selectedId: "",
    selected: null,
    filters: {
      q: "",
      dataStatus: "",
      active: "all",
      category: "",
      retailer: "",
    },
    form: defaultProductAdminForm(),
    loading: false,
    saving: false,
    error: "",
    message: "",
    adminToken: "",
    lastSyncAt: "",
  },
};

let workspaceDbPromise = null;

function formatDateTime(value = new Date()) {
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(value);
}

function formatSecondsLeft(ms) {
  const safeMs = Math.max(0, ms);
  const totalSeconds = Math.floor(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function buildDefaultFileName(patient) {
  const patientName = getVisiblePatientName(patient) || "Paciente";
  return `${patientName} ${formatDateForFile()}`;
}

function getAgeReferenceDate() {
  return state.currentPlanStartDate || state.patient?.fecha_evaluacion || formatDateForFile();
}

function syncCalculatedPatientAge(patient = state.patient) {
  if (!patient) return 0;
  const age = calculateAgeYears(patient.fecha_nacimiento, patient.fecha_evaluacion || getAgeReferenceDate());
  if (age) {
    patient.edad = age;
  }
  return age || Number(patient.edad) || 0;
}

function formatAgeLabel(age) {
  const numericAge = Number(age);
  if (!Number.isFinite(numericAge) || numericAge <= 0) return "Sin fecha de nacimiento";
  return `${Number.isInteger(numericAge) ? numericAge : numericAge.toFixed(1)} años`;
}

function refreshCalculatedAgeDisplay() {
  const age = syncCalculatedPatientAge();
  app?.querySelectorAll("[data-calculated-age]").forEach((element) => {
    element.textContent = formatAgeLabel(age);
  });
}

function getPatientIdentifier(patient) {
  if (!patient) return "";
  if (patient.id) return `id:${patient.id}`;
  if (patient.nombre) return `name:${patient.nombre.trim().toLowerCase()}`;
  return "";
}

function formatPatientSnapshot(patient) {
  const nombre = getVisiblePatientName(patient) || "Paciente sin nombre";
  const edad = formatAgeLabel(resolvePatientAge(patient, getAgeReferenceDate()));
  const objetivo = objectiveRules[patient?.objetivo_principal]?.label || patient?.objetivo_principal || "Sin objetivo";
  return `${nombre} · ${edad} · ${objetivo}`;
}

function isInternalPatientExample(patient) {
  return String(patient?.id ?? "").startsWith(INTERNAL_PATIENT_PREFIX);
}

function cleanVisibleName(value) {
  return String(value ?? "")
    .replace(/^Caso\s+ficticio\s*-\s*/i, "")
    .replace(/^Paciente\s+ficticio\s*-\s*/i, "")
    .replace(/\bfictici[oa]\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function getVisiblePatientName(patient) {
  const cleaned = cleanVisibleName(patient?.nombre);
  if (/^paciente\s+(nuevo|temporal)$/i.test(cleaned)) return "Nuevo paciente";
  if (cleaned) return cleaned;
  return patient?.id ? "Paciente sin nombre" : "Nuevo paciente";
}

function normalizeMealTimeSelection(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((item) => String(item ?? "").trim()).filter((item) => MEAL_TIME_BY_ID.has(item)))];
  }
  const source = String(value ?? "").trim();
  if (!source) return [];
  return [...new Set(source.split(/[;,]/).map((item) => item.trim()).filter((item) => MEAL_TIME_BY_ID.has(item)))];
}

function getPatientMealTimes(patient) {
  const manual = normalizeMealTimeSelection(patient?.meal_times);
  if (manual.length) return manual;
  const auto = [...DEFAULT_MEAL_TIME_IDS];
  if (Number(patient?.entrenamientos_semana) > 0) {
    auto.splice(2, 0, "racion_preentrenamiento");
    auto.splice(4, 0, "racion_postentrenamiento");
  }
  return auto;
}

function normalizePatientProfile(patient) {
  const normalized = { ...DEFAULT_PATIENT_PROFILE, ...(patient ?? {}) };
  const numericKeys = [
    "edad",
    "peso_kg",
    "talla_cm",
    "estatura_sentado_cm",
    "envergadura_cm",
    "peso_ideal_kg",
    "peso_objetivo_kg",
    "porcentaje_grasa",
    "masa_muscular",
    "masa_libre_grasa_kg",
    "numero_tiempos_comida",
    "numero_valoracion",
    "pliegue_submandibular_mm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_cresta_iliaca_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
    "perimetro_cefalico_cm",
    "perimetro_cuello_cm",
    "perimetro_brazo_relajado_cm",
    "perimetro_brazo_relajado_2_cm",
    "perimetro_brazo_tenso_cm",
    "perimetro_antebrazo_cm",
    "perimetro_muneca_cm",
    "perimetro_torax_cm",
    "perimetro_cintura_cm",
    "perimetro_abdomen_cm",
    "perimetro_cadera_cm",
    "perimetro_muslo_superior_cm",
    "perimetro_muslo_medio_cm",
    "perimetro_pantorrilla_cm",
    "perimetro_tobillo_cm",
    "longitud_acromiale_radiale_cm",
    "longitud_radiale_stylion_cm",
    "longitud_stylion_medio_dactylion_cm",
    "longitud_ilioespinale_cm",
    "longitud_trochanterion_cm",
    "longitud_trochanterion_tibiale_laterale_cm",
    "longitud_tibiale_laterale_cm",
    "longitud_pie_cm",
    "longitud_tibiale_mediale_sphyrion_tibiale_cm",
    "diametro_biacromial_cm",
    "diametro_biliocrestal_cm",
    "diametro_bitrocantereo_cm",
    "diametro_torax_transverso_cm",
    "diametro_torax_ap_cm",
    "diametro_biestiloideo_cm",
    "diametro_humero_cm",
    "diametro_femur_cm",
    "diametro_bimaleolar_cm",
    "diametro_pie_cm",
    "diametro_abdomen_ap_cm",
    "media_envergadura_cm",
    "semi_brazada_cm",
    "longitud_antebrazo_cm",
    "rodilla_maleolo_cm",
    "rodilla_talon_cm",
    "consumo_calorias_actuales",
    "consumo_proteina_actual_g",
    "consumo_carbohidratos_actual_g",
    "consumo_grasas_actual_g",
    "naf_valor",
    "entrenamientos_semana",
    "duracion_entrenamiento_minutos",
    "kg_masa_adiposa_perder",
    "dias_restriccion",
    "kg_masa_muscular_ganar",
    "dias_adicion",
    "get_3",
    "proteina_gkg",
    "grasa_gkg",
    "carbohidrato_gkg",
    ...anthropometryGoalFieldNames.filter((fieldName) => fieldName !== "fuente_masa_libre_grasa"),
  ];
  for (const key of numericKeys) {
    const rawValue = String(normalized[key] ?? "").trim();
    if (!rawValue) {
      normalized[key] = "";
      continue;
    }
    const value = normalizeNumericInput(rawValue);
    normalized[key] = Number.isFinite(value) ? value : "";
  }
  const calculatedAge = calculateAgeYears(normalized.fecha_nacimiento, normalized.fecha_evaluacion || formatDateForFile());
  if (calculatedAge) normalized.edad = calculatedAge;
  normalized.antropometria_secciones_activas = normalizeAnthropometrySections(normalized.antropometria_secciones_activas);
  normalized.meal_times = normalizeMealTimeSelection(normalized.meal_times);
  if (normalized.meal_times.length && !normalized.numero_tiempos_comida) {
    normalized.numero_tiempos_comida = normalized.meal_times.length;
  }
  return normalized;
}

function normalizeDeliverableOptions(options = {}) {
  const source = options && typeof options === "object" ? options : {};
  const normalizeToggle = (key) => source[key] === undefined ? DEFAULT_DELIVERABLE_OPTIONS[key] : source[key] === true || source[key] === "true";
  return {
    title: String(source.title ?? DEFAULT_DELIVERABLE_OPTIONS.title).trim() || DEFAULT_DELIVERABLE_OPTIONS.title,
    finalNote: String(source.finalNote ?? DEFAULT_DELIVERABLE_OPTIONS.finalNote).trim(),
    showCurrentIntake: normalizeToggle("showCurrentIntake"),
    showWarnings: normalizeToggle("showWarnings"),
    showSupplements: normalizeToggle("showSupplements"),
    showProducts: normalizeToggle("showProducts"),
    showRecipes: normalizeToggle("showRecipes"),
    showShoppingList: normalizeToggle("showShoppingList"),
  };
}

function normalizeCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.round(number)) : 0;
}

function formatValidationAreas(areas) {
  if (!Array.isArray(areas) || !areas.length) return "";
  return areas
    .map((area) => String(area ?? "").replace(/_/g, " ").trim())
    .filter(Boolean)
    .slice(0, 4)
    .join(", ");
}

function getProfessionalValidationStatus(data = state.data) {
  const source = data?.professionalValidation && typeof data.professionalValidation === "object"
    ? data.professionalValidation
    : {};
  const hasLocalDecisionOverrides = Object.keys(state.professionalValidationDecisions ?? {}).length > 0;
  const sourceItems = Array.isArray(source.items) ? source.items : [];
  const items = sourceItems.map((item) => ({
    ...item,
    ...(state.professionalValidationDecisions?.[item.id] ?? {}),
  }));
  const totalItems = items.length || normalizeCount(source.totalItems ?? source.total);
  const undecidedItems = items.length ? items.filter((item) => professionalValidationItemState(item) === "pending") : [];
  const actionItems = items.length ? items.filter((item) => professionalValidationItemState(item) === "action") : [];
  const pendingItems = [...undecidedItems, ...actionItems];
  const pendingCount = items.length ? pendingItems.length : normalizeCount(source.pendingCount);
  const reviewedCount = items.length ? Math.max(0, totalItems - undecidedItems.length) : normalizeCount(source.reviewedCount ?? Math.max(0, totalItems - pendingCount));
  const closureReadyCount = items.length ? Math.max(0, totalItems - pendingCount) : Math.max(0, totalItems - pendingCount);
  const actionRequiredCount = actionItems.length;
  const noDecisionCount = undecidedItems.length;
  const status = noDecisionCount
    ? "pendiente_revision"
    : actionRequiredCount
      ? "requiere_accion_profesional"
      : "decision_local_completa";
  const label = noDecisionCount
    ? "Pendiente de validacion profesional"
    : actionRequiredCount
      ? "Requiere accion antes del cierre"
      : "Decisiones profesionales completas en este navegador";
  const defaultMessage = noDecisionCount
    ? `Borrador pendiente de validacion profesional clinica: ${noDecisionCount} de ${totalItems || noDecisionCount} puntos siguen sin decision.`
    : actionRequiredCount
      ? `${actionRequiredCount} puntos ya tienen decision, pero requieren ajustes, retiro de bloques o fuentes antes de cerrar.`
      : "Revisar la matriz profesional antes de uso clinico real.";
  const pendingAreas = items.length
    ? [...new Set(pendingItems.map((item) => item.area).filter(Boolean))].sort()
    : Array.isArray(source.pendingAreas) ? source.pendingAreas : [];
  return {
    status,
    label,
    message: String(hasLocalDecisionOverrides ? defaultMessage : source.message ?? defaultMessage).trim(),
    totalItems,
    pendingCount,
    reviewedCount,
    noDecisionCount,
    actionRequiredCount,
    closureReadyCount,
    pendingAreas,
    items,
    source: String(source.source ?? "07_DOCUMENTACION/notas_clinicas_metodologicas/matriz_validacion_profesional.csv"),
    reviewPackage: String(source.reviewPackage ?? "07_DOCUMENTACION/notas_clinicas_metodologicas/paquete_revision_profesional.md"),
    updated: String(source.updated ?? ""),
  };
}

function renderProfessionalValidationNotice(context = "screen") {
  const validation = getProfessionalValidationStatus();
  const isPending = validation.pendingCount > 0;
  const countText = validation.totalItems
    ? `${validation.pendingCount} de ${validation.totalItems} puntos no listos para cierre`
    : "Matriz profesional pendiente de revisar";
  const areas = formatValidationAreas(validation.pendingAreas);
  const detail = [
    countText,
    validation.noDecisionCount ? `Sin decision: ${validation.noDecisionCount}` : "",
    validation.actionRequiredCount ? `Requieren accion: ${validation.actionRequiredCount}` : "",
    areas ? `Areas: ${areas}` : "",
    validation.reviewPackage ? `Guia: ${validation.reviewPackage}` : "",
  ].filter(Boolean).join(" · ");
  return `
    <div class="validation-status validation-status--${isPending ? "warning" : "ok"} validation-status--${context}" data-professional-validation-status role="note">
      <div>
        <span>Validacion profesional</span>
        <strong>${escapeHtml(validation.label)}</strong>
      </div>
      <p>${escapeHtml(validation.message)}</p>
      <small>${escapeHtml(detail)}</small>
    </div>
  `;
}

function renderClinicalDraftBanner() {
  const validation = getProfessionalValidationStatus();
  if (!validation.pendingCount) return "";
  const reason = validation.noDecisionCount
    ? `${validation.noDecisionCount} puntos siguen sin decision profesional.`
    : `${validation.actionRequiredCount} puntos requieren ajustes, retiro de bloques o fuentes.`;
  return `
    <div class="clinical-draft-banner" data-clinical-draft-banner>
      <span>Borrador clinico</span>
      <strong>No apto para entrega final a pacientes reales.</strong>
      <p>${escapeHtml(reason)} Este plan debe revisarse y cerrarse por una profesional de nutricion antes de usarse como prescripcion.</p>
    </div>
  `;
}

function getProfessionalClosureSummary(validation = getProfessionalValidationStatus()) {
  const items = Array.isArray(validation.items) ? validation.items : [];
  const closedItems = items.filter((item) => professionalValidationItemState(item) === "closed");
  const decisionCounts = closedItems.reduce((counts, item) => {
    const decision = String(item.decision_profesional ?? item.estado_actual ?? "validado").trim().toLowerCase();
    counts[decision] = (counts[decision] ?? 0) + 1;
    return counts;
  }, {});
  const responsables = [...new Set(closedItems.map((item) => String(item.responsable ?? "").trim()).filter(Boolean))];
  const fechas = [...new Set(closedItems.map((item) => String(item.fecha_revision ?? "").trim()).filter(Boolean))].sort();
  return {
    isClosed: validation.totalItems > 0 && validation.pendingCount === 0 && closedItems.length === validation.totalItems,
    totalItems: validation.totalItems,
    closedItems: closedItems.length,
    responsables,
    firstDate: fechas[0] ?? "",
    lastDate: fechas[fechas.length - 1] ?? "",
    decisionCounts,
  };
}

function formatProfessionalClosureDateRange(summary) {
  if (!summary.firstDate && !summary.lastDate) return "Fecha no registrada";
  if (summary.firstDate && summary.lastDate && summary.firstDate !== summary.lastDate) {
    return `${summary.firstDate} a ${summary.lastDate}`;
  }
  return summary.lastDate || summary.firstDate;
}

function renderProfessionalClosureCertificate(context = "preview") {
  const validation = getProfessionalValidationStatus();
  const summary = getProfessionalClosureSummary(validation);
  if (!summary.isClosed) return "";
  const responsables = summary.responsables.length ? summary.responsables.join(", ") : "Responsable registrado en matriz";
  const decisionDetail = Object.entries(summary.decisionCounts)
    .map(([decision, count]) => `${decision}: ${count}`)
    .join(" · ");
  return `
    <div class="clinical-closure-certificate clinical-closure-certificate--${context}" data-clinical-closure-certificate>
      <span>Cierre profesional registrado</span>
      <strong>Apto para entrega profesional segun decisiones registradas.</strong>
      <p>La matriz profesional tiene ${summary.closedItems} de ${summary.totalItems} puntos cerrados con decision, responsable y fecha.</p>
      <small>Responsable(s): ${escapeHtml(responsables)} · Fecha(s): ${escapeHtml(formatProfessionalClosureDateRange(summary))}${decisionDetail ? ` · ${escapeHtml(decisionDetail)}` : ""}</small>
    </div>
  `;
}

function normalizeNumericInput(value) {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) return "";
  const normalized = rawValue.replace(/\s/g, "").replace(/,(?=\d)/g, ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : "";
}

function isFileForPatient(file, patient) {
  if (!patient) return false;
  if (file.patientId) return String(file.patientId) === getPatientIdentifier(patient);
  if (!file.patientNombre || !patient.nombre) return false;
  return file.patientNombre === patient.nombre;
}

function getCurrentPatientFiles() {
  if (!state.patient) return state.patientFiles;
  return state.patientFiles.filter((file) => isFileForPatient(file, state.patient));
}

function normalizeFileType(type) {
  const normalized = String(type ?? "").trim();
  if (FILE_TYPE_OPTIONS.includes(normalized)) return normalized;
  if (normalized) return normalized;
  return DEFAULT_FILE_TYPE;
}

function syncDraftFromPatient(patient, force = false) {
  if (force || !state.currentFileId || !state.newFileDraft.name) {
    state.newFileDraft.name = buildDefaultFileName(patient);
  }
}

function generateEntityId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function ensurePatientInCatalog(patient) {
  if (!state.data?.patients || !patient?.id) return;
  const existingIndex = state.data.patients.findIndex((item) => item.id === patient.id);
  const snapshot = normalizePatientProfile(structuredClone(patient));
  if (existingIndex >= 0) {
    state.data.patients[existingIndex] = { ...state.data.patients[existingIndex], ...snapshot };
    return;
  }
  state.data.patients.push(snapshot);
}

function patientSelectOptions() {
  const basePatients = Array.isArray(state.data?.patients)
    ? state.data.patients.filter((patient) => !isInternalPatientExample(patient))
    : [];
  if (state.patient?.id && !basePatients.some((patient) => patient.id === state.patient.id)) {
    basePatients.push(state.patient);
  }
  return basePatients;
}

function canUseIndexedDb() {
  return typeof indexedDB !== "undefined";
}

function openWorkspaceDb() {
  if (!canUseIndexedDb()) return Promise.reject(new Error("IndexedDB no disponible"));
  if (workspaceDbPromise) return workspaceDbPromise;
  workspaceDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(WORKSPACE_DB_NAME, WORKSPACE_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(WORKSPACE_KV_STORE)) {
        db.createObjectStore(WORKSPACE_KV_STORE);
      }
    };
    request.onsuccess = () => {
      state.storageEngine = "IndexedDB + localStorage";
      resolve(request.result);
    };
    request.onerror = () => reject(request.error || new Error("No se pudo abrir IndexedDB"));
  });
  return workspaceDbPromise;
}

async function idbGet(key) {
  const db = await openWorkspaceDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(WORKSPACE_KV_STORE, "readonly").objectStore(WORKSPACE_KV_STORE).get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error(`No se pudo leer ${key}`));
  });
}

async function idbSet(key, value) {
  const db = await openWorkspaceDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(WORKSPACE_KV_STORE, "readwrite").objectStore(WORKSPACE_KV_STORE).put(value, key);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error || new Error(`No se pudo guardar ${key}`));
  });
}

async function idbDelete(key) {
  const db = await openWorkspaceDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(WORKSPACE_KV_STORE, "readwrite").objectStore(WORKSPACE_KV_STORE).delete(key);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error || new Error(`No se pudo borrar ${key}`));
  });
}

function localStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function localStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function localStorageRemove(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function parsePatientFiles(value) {
  if (!value) return [];
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_STORED_FILES);
  } catch {
    return [];
  }
}

function readPatientFilesFromStorage() {
  return parsePatientFiles(localStorageGet(STORAGE_PATIENT_FILES));
}

async function readPatientFilesFromPersistentStorage() {
  const fallback = readPatientFilesFromStorage();
  try {
    const stored = await idbGet(STORAGE_PATIENT_FILES);
    const indexedFiles = parsePatientFiles(stored);
    if (indexedFiles.length || !fallback.length) return indexedFiles;
    await idbSet(STORAGE_PATIENT_FILES, fallback);
    return fallback;
  } catch (error) {
    state.storageEngine = "localStorage";
    state.lastStorageError = error?.message || "IndexedDB no disponible";
    return fallback;
  }
}

async function readActiveFileIdFromPersistentStorage() {
  const fallback = localStorageGet(STORAGE_ACTIVE_FILE);
  try {
    const stored = await idbGet(STORAGE_ACTIVE_FILE);
    return stored || fallback || null;
  } catch {
    return fallback || null;
  }
}

function readProfessionalValidationDecisionsFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_PROFESSIONAL_VALIDATION);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function persistProfessionalValidationDecisions() {
  localStorageSet(STORAGE_PROFESSIONAL_VALIDATION, JSON.stringify(state.professionalValidationDecisions));
}

function validateNutritionDataPayload(payload) {
  const candidate = payload?.tables ? payload : payload?.data;
  if (!candidate || typeof candidate !== "object" || !candidate.tables || typeof candidate.tables !== "object") {
    throw new Error("El JSON no contiene una base nutricional compatible.");
  }
  const tableEntries = Object.entries(candidate.tables);
  if (!tableEntries.length) {
    throw new Error("La base nutricional no contiene tablas.");
  }
  for (const [name, table] of tableEntries) {
    if (!table || typeof table !== "object" || !Array.isArray(table.rows)) {
      throw new Error(`La tabla "${name}" no tiene una lista de registros valida.`);
    }
    if (table.columns && !Array.isArray(table.columns)) {
      throw new Error(`La tabla "${name}" tiene columnas en un formato no compatible.`);
    }
  }
  return {
    ...structuredClone(candidate),
    updated: candidate.updated || formatDateForFile(),
    patients: Array.isArray(candidate.patients) ? candidate.patients : [],
  };
}

function readNutritionDataOverrideFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_NUTRITION_DATA);
    if (!stored) return null;
    return validateNutritionDataPayload(JSON.parse(stored));
  } catch {
    return null;
  }
}

function persistNutritionDataOverride(data) {
  localStorageSet(STORAGE_NUTRITION_DATA, JSON.stringify(data));
}

function clearNutritionDataOverride() {
  localStorageRemove(STORAGE_NUTRITION_DATA);
}

function hasNutritionDataOverride() {
  try {
    return Boolean(localStorage.getItem(STORAGE_NUTRITION_DATA));
  } catch {
    return false;
  }
}

function dataVersionLabel(data) {
  return data?.updated || "Sin version";
}

function refreshGeneratedAfterDataChange() {
  if (state.patient) {
    ensurePatientInCatalog(state.patient);
    hydrateGenerated();
    markPendingChanges();
  }
}

function applyNutritionData(data, { persist = false, sourceLabel = "Base importada" } = {}) {
  state.data = validateNutritionDataPayload(data);
  state.dataSourceLabel = sourceLabel;
  state.dataHasPendingChanges = false;
  getSelectedDataTableName();
  refreshGeneratedAfterDataChange();
  if (persist) persistNutritionDataOverride(state.data);
  render();
}

function exportNutritionData() {
  const payload = {
    kind: "sistema_nutricion_base_nutricional",
    exportedAt: new Date().toISOString(),
    source: state.dataSourceLabel,
    data: state.data,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  downloadBlob(blob, `base_nutricion_${formatDateForFile()}_respaldo.json`);
}

async function importNutritionDataFromInput(input) {
  const file = input.files?.[0];
  if (!file) return;
  try {
    const text = await readFileAsText(file);
    const payload = JSON.parse(text);
    applyNutritionData(payload, { persist: true, sourceLabel: `Importada: ${file.name}` });
  } catch (error) {
    window.alert(error?.message || "No se pudo importar la base nutricional.");
  } finally {
    input.value = "";
  }
}

function restoreDefaultNutritionData() {
  if (!state.defaultData) return;
  const confirmed = window.confirm("Cargar la base oficial del proyecto y reemplazar la base personalizada guardada en este navegador?");
  if (!confirmed) return;
  clearNutritionDataOverride();
  applyNutritionData(structuredClone(state.defaultData), { persist: false, sourceLabel: "Base del proyecto" });
}

function clearSavedNutritionDataOverride() {
  const hasOverride = hasNutritionDataOverride();
  if (!hasOverride) return;
  const confirmed = window.confirm("Eliminar la base personalizada guardada en este navegador? La base oficial del proyecto quedara activa.");
  if (!confirmed) return;
  clearNutritionDataOverride();
  if (state.defaultData) {
    applyNutritionData(structuredClone(state.defaultData), { persist: false, sourceLabel: "Base del proyecto" });
    return;
  }
  state.dataSourceLabel = "Base del proyecto";
  state.dataHasPendingChanges = false;
  render();
}

function dataTableNames() {
  return Object.keys(state.data?.tables ?? {});
}

function getDataTableColumns(table) {
  if (Array.isArray(table?.columns) && table.columns.length) return table.columns;
  const columns = new Set();
  for (const row of table?.rows ?? []) {
    Object.keys(row ?? {}).forEach((key) => columns.add(key));
  }
  return [...columns];
}

function getSelectedDataTableName() {
  const names = dataTableNames();
  if (!names.length) return "";
  if (!names.includes(state.selectedDataTableName)) {
    state.selectedDataTableName = names[0];
  }
  return state.selectedDataTableName;
}

function getSelectedDataTable() {
  const name = getSelectedDataTableName();
  return name ? state.data.tables[name] : null;
}

function formatColumnLabel(column) {
  return String(column ?? "").replaceAll("_", " ");
}

function isLongDataField(column, value) {
  const key = String(column ?? "").toLowerCase();
  const text = String(value ?? "");
  return text.length > 60 || /(descripcion|notas|observaciones|recomendacion|contraindicaciones|ingredientes|alimentos|cantidades|opciones|ejemplo|formula|variables|criterio|alerta|uso|texto)/.test(key);
}

function normalizeDataColumnKey(column) {
  return String(column ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isNumericDataColumn(column) {
  const key = normalizeDataColumnKey(column);
  if (/(cantidad|cantidades|ejemplo|texto|descripcion|notas|observaciones|ingredientes|opciones)/.test(key)) return false;
  return /(^|_)(kcal|caloria|calorias|proteina|carbohidrato|carbohidratos|grasa|grasas|fibra|peso|factor|porcentaje|prioridad|orden|duracion|porciones|minutos)(_|$)/.test(key)
    || /(_100g|_g|_gkg|_kg|_cm)$/.test(key)
    || key === "kcal_por_gramo";
}

function coerceDataCellValue(column, value, previousValue) {
  const raw = String(value ?? "").trim();
  if (raw === "") return "";
  if (typeof previousValue === "number" || isNumericDataColumn(column)) {
    const numberValue = Number(raw);
    if (Number.isFinite(numberValue)) return numberValue;
  }
  if (typeof previousValue === "boolean") {
    return ["true", "1", "si", "sí", "activo"].includes(raw.toLowerCase());
  }
  return value;
}

function auditNutritionData(data) {
  const items = [];
  const tables = data?.tables ?? {};
  const pushItem = (level, table, message, detail = "") => {
    items.push({ level, table, message, detail });
  };

  for (const [tableName, table] of Object.entries(tables)) {
    const rows = Array.isArray(table?.rows) ? table.rows : [];
    const columns = getDataTableColumns(table);
    if (!columns.length) {
      pushItem("error", tableName, "La tabla no tiene columnas definidas.");
      continue;
    }
    if (!Array.isArray(table?.rows)) {
      pushItem("error", tableName, "La tabla no tiene una lista de registros valida.");
      continue;
    }

    const missingColumns = REQUIRED_DATA_COLUMNS.filter((column) => !columns.includes(column));
    if (missingColumns.length) {
      pushItem("warning", tableName, `Faltan columnas recomendadas: ${missingColumns.join(", ")}.`);
    }

    if (!rows.length) {
      pushItem("warning", tableName, "La tabla no contiene registros.");
      continue;
    }

    const ids = new Map();
    rows.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 1;
      const id = String(row?.id ?? "").trim();
      if (!id) {
        pushItem("error", tableName, `Fila ${rowNumber}: falta id.`);
      } else {
        ids.set(id, [...(ids.get(id) ?? []), rowNumber]);
      }

      if (columns.includes("nombre") && !String(row?.nombre ?? "").trim()) {
        pushItem("warning", tableName, `Fila ${rowNumber}: falta nombre.`);
      }
      if (columns.includes("estado") && !String(row?.estado ?? "").trim()) {
        pushItem("warning", tableName, `Fila ${rowNumber}: falta estado.`);
      }
      if (columns.includes("fuente") && !String(row?.fuente ?? "").trim()) {
        pushItem("info", tableName, `Fila ${rowNumber}: falta fuente.`);
      }
      if (columns.includes("ultima_actualizacion") && !String(row?.ultima_actualizacion ?? "").trim()) {
        pushItem("info", tableName, `Fila ${rowNumber}: falta ultima_actualizacion.`);
      }

      columns.forEach((column) => {
        const value = row?.[column];
        if (value === "" || value === null || value === undefined) return;
        if (isNumericDataColumn(column) && typeof value !== "number" && !Number.isFinite(Number(value))) {
          pushItem("warning", tableName, `Fila ${rowNumber}: "${column}" deberia ser numerico.`, String(value));
        }
      });
    });

    ids.forEach((positions, id) => {
      if (positions.length > 1) {
        pushItem("error", tableName, `ID duplicado "${id}" en filas ${positions.join(", ")}.`);
      }
    });

    if (columns.includes("estado")) {
      const activeRows = rows.filter((row) => String(row?.estado ?? "").toLowerCase() === "activo").length;
      if (!activeRows) {
        pushItem("warning", tableName, "No hay registros activos.");
      }
    }
  }

  const summary = {
    error: items.filter((item) => item.level === "error").length,
    warning: items.filter((item) => item.level === "warning").length,
    info: items.filter((item) => item.level === "info").length,
  };
  return { items, summary };
}

function updateDataEditorStatus() {
  const target = app?.querySelector("[data-data-editor-status]");
  if (!target) return;
  target.textContent = state.dataHasPendingChanges
    ? "Cambios pendientes: guarda la base para recalcular y conservarla en este navegador."
    : `Base sincronizada: ${state.dataSourceLabel}`;
}

function markDataPendingChanges() {
  state.dataHasPendingChanges = true;
  state.dataSourceLabel = "Base editada sin guardar";
  updateDataEditorStatus();
}

function touchDataRow(row) {
  if (row && Object.prototype.hasOwnProperty.call(row, "ultima_actualizacion")) {
    row.ultima_actualizacion = formatDateForFile();
  }
  if (state.data) state.data.updated = formatDateForFile();
}

function updateDataCell(tableName, rowIndex, column, value) {
  const table = state.data?.tables?.[tableName];
  const row = table?.rows?.[Number(rowIndex)];
  if (!row) return;
  row[column] = coerceDataCellValue(column, value, row[column]);
  touchDataRow(row);
  markDataPendingChanges();
}

function buildNewDataRow(tableName) {
  const table = state.data?.tables?.[tableName];
  const columns = getDataTableColumns(table);
  const row = Object.fromEntries(columns.map((column) => [column, ""]));
  if (columns.includes("id")) row.id = `${slugify(tableName).slice(0, 20)}_${Date.now().toString(36)}`;
  if (columns.includes("nombre")) row.nombre = "Nuevo registro";
  if (columns.includes("estado")) row.estado = "activo";
  if (columns.includes("fuente")) row.fuente = "Edicion web";
  if (columns.includes("ultima_actualizacion")) row.ultima_actualizacion = formatDateForFile();
  return row;
}

function addDataRow() {
  const tableName = getSelectedDataTableName();
  const table = state.data?.tables?.[tableName];
  if (!table) return;
  table.rows = [buildNewDataRow(tableName), ...(table.rows ?? [])];
  state.data.updated = formatDateForFile();
  markDataPendingChanges();
  render();
}

function duplicateDataRow(rowIndex) {
  const tableName = getSelectedDataTableName();
  const table = state.data?.tables?.[tableName];
  const row = table?.rows?.[Number(rowIndex)];
  if (!table || !row) return;
  const copy = structuredClone(row);
  if (copy.id) copy.id = `${copy.id}_copia_${Date.now().toString(36)}`;
  if (copy.nombre) copy.nombre = `${copy.nombre} copia`;
  touchDataRow(copy);
  table.rows.splice(Number(rowIndex) + 1, 0, copy);
  markDataPendingChanges();
  render();
}

function deleteDataRow(rowIndex) {
  const tableName = getSelectedDataTableName();
  const table = state.data?.tables?.[tableName];
  if (!table?.rows?.[Number(rowIndex)]) return;
  const confirmed = window.confirm("Eliminar este registro de la base activa?");
  if (!confirmed) return;
  table.rows.splice(Number(rowIndex), 1);
  if (state.data) state.data.updated = formatDateForFile();
  markDataPendingChanges();
  render();
}

function saveEditedNutritionData() {
  if (!state.data) return;
  state.data.updated = formatDateForFile();
  persistNutritionDataOverride(state.data);
  state.dataSourceLabel = "Base editada guardada en este navegador";
  state.dataHasPendingChanges = false;
  refreshGeneratedAfterDataChange();
  render();
}

function selectedTableToCsv() {
  const tableName = getSelectedDataTableName();
  const table = getSelectedDataTable();
  if (!table) return "";
  const columns = getDataTableColumns(table);
  const csvRows = [
    columns,
    ...(table.rows ?? []).map((row) => columns.map((column) => row?.[column] ?? "")),
  ];
  return csvRows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

function exportSelectedTableCsv() {
  const tableName = getSelectedDataTableName();
  if (!tableName) return;
  const csv = `\ufeff${selectedTableToCsv()}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `base_nutricion_${slugify(tableName)}_${formatDateForFile()}.csv`);
}

async function importSelectedTableCsvFromInput(input) {
  const file = input.files?.[0];
  const tableName = getSelectedDataTableName();
  const table = getSelectedDataTable();
  if (!file || !tableName || !table) return;
  try {
    const text = await readFileAsText(file);
    const parsed = csvRowsToObjects(parseCsv(text));
    const previousRows = table.rows ?? [];
    table.columns = parsed.headers;
    table.rows = parsed.rows.map((row, rowIndex) => {
      const previousRow = previousRows[rowIndex] ?? {};
      const nextRow = {};
      parsed.headers.forEach((column) => {
        nextRow[column] = coerceDataCellValue(column, row[column], previousRow[column]);
      });
      touchDataRow(nextRow);
      return nextRow;
    });
    if (state.data) state.data.updated = formatDateForFile();
    markDataPendingChanges();
    render();
  } catch (error) {
    window.alert(error?.message || "No se pudo importar el CSV de la tabla.");
  } finally {
    input.value = "";
  }
}

function persistPatientFiles() {
  const filesSnapshot = structuredClone(state.patientFiles);
  const activeFileSnapshot = state.currentFileId ?? "";
  localStorageSet(STORAGE_PATIENT_FILES, JSON.stringify(filesSnapshot));
  localStorageSet(STORAGE_ACTIVE_FILE, activeFileSnapshot);
  return Promise.all([
    idbSet(STORAGE_PATIENT_FILES, filesSnapshot),
    idbSet(STORAGE_ACTIVE_FILE, activeFileSnapshot),
  ]).then(() => {
    state.storageEngine = "IndexedDB + localStorage";
    state.lastStorageError = "";
    return true;
  }).catch((error) => {
    state.storageEngine = "localStorage";
    state.lastStorageError = error?.message || "IndexedDB no disponible";
    return false;
  });
}

function getPatientCloudToken() {
  return String(state.patientCloud?.token || "").trim();
}

function randomCloudSecret(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function applyPatientCloudWorkspaceFromHash() {
  if (typeof window === "undefined") return false;
  const hash = decodeURIComponent(window.location.hash || "").replace(/^#/, "");
  if (!hash.startsWith(PATIENT_CLOUD_HASH_PREFIX)) return false;
  const raw = hash.slice(PATIENT_CLOUD_HASH_PREFIX.length).trim();
  const [workspaceId, workspaceKey] = raw.split(".");
  if (!workspaceId || !workspaceKey) return false;
  state.patientCloud.workspaceId = workspaceId;
  state.patientCloud.workspaceKey = workspaceKey;
  localStorageSet(STORAGE_PATIENT_WORKSPACE_ID, workspaceId);
  localStorageSet(STORAGE_PATIENT_WORKSPACE_KEY, workspaceKey);
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  return true;
}

function ensurePatientCloudWorkspace() {
  if (!state.patientCloud.workspaceId) {
    state.patientCloud.workspaceId = localStorageGet(STORAGE_PATIENT_WORKSPACE_ID) || `ws_${randomCloudSecret(12)}`;
  }
  if (!state.patientCloud.workspaceKey) {
    state.patientCloud.workspaceKey = localStorageGet(STORAGE_PATIENT_WORKSPACE_KEY) || randomCloudSecret(32);
  }
  localStorageSet(STORAGE_PATIENT_WORKSPACE_ID, state.patientCloud.workspaceId);
  localStorageSet(STORAGE_PATIENT_WORKSPACE_KEY, state.patientCloud.workspaceKey);
}

function hasPatientCloudAccess() {
  return Boolean(state.patientCloud?.available && state.patientCloud?.status === "available");
}

function patientCloudShareUrl() {
  ensurePatientCloudWorkspace();
  const url = new URL(window.location.href);
  url.hash = `${PATIENT_CLOUD_HASH_PREFIX}${encodeURIComponent(`${state.patientCloud.workspaceId}.${state.patientCloud.workspaceKey}`)}`;
  return url.toString();
}

function patientCloudHeaders({ json = true, includeLegacyWorkspace = true } = {}) {
  const headers = {};
  const token = getPatientCloudToken();
  if (json) headers["content-type"] = "application/json";
  if (token) headers["x-admin-token"] = token;
  if (state.patientCloud.clinicalWorkspaceId) {
    headers["x-clinical-workspace-id"] = state.patientCloud.clinicalWorkspaceId;
  }
  if (includeLegacyWorkspace && state.patientCloud.workspaceId && state.patientCloud.workspaceKey) {
    headers["x-patient-workspace-id"] = state.patientCloud.workspaceId;
    headers["x-patient-workspace-key"] = state.patientCloud.workspaceKey;
  }
  return headers;
}

function setPatientCloudStatus({ syncing = state.patientCloud.syncing, message = state.patientCloud.message, error = "" } = {}) {
  state.patientCloud = {
    ...state.patientCloud,
    syncing,
    message,
    error,
  };
  const status = app?.querySelector("[data-patient-cloud-status]");
  if (status) {
    status.textContent = patientCloudStatusText();
  }
}

function patientCloudStatusText() {
  if (state.patientCloud.status === "checking") return "Comprobando nube clinica...";
  if (state.patientCloud.status === "not_configured") return "Nube clinica no configurada en este entorno.";
  if (state.patientCloud.status === "unauthorized") return "Inicia sesion autorizada para usar la nube clinica.";
  if (state.patientCloud.status === "error") return state.patientCloud.error || "Nube clinica no disponible.";
  if (state.patientCloud.status === "conflict") return "Conflicto pendiente de resolucion.";
  if (!hasPatientCloudAccess()) return "Nube clinica pendiente de conexion.";
  if (state.patientCloud.syncing) return "Guardando automaticamente en nube...";
  if (state.patientCloud.error) return `Nube con alerta: ${state.patientCloud.error}. El respaldo local sigue activo.`;
  if (state.patientCloud.lastSyncAt) return `Nube sincronizada ${formatDateTime(new Date(state.patientCloud.lastSyncAt))}.`;
  return state.patientCloud.message || "Nube clinica conectada; el respaldo local protege cambios sin conexion.";
}

async function loadClinicalStatus({ silent = true } = {}) {
  state.patientCloud.status = "checking";
  state.patientCloud.error = "";
  state.patientCloud.message = "Comprobando conexion clinica...";
  renderAutoSaveTicker();
  try {
    const response = await fetch("/api/clinical/status", {
      headers: patientCloudHeaders({ json: false, includeLegacyWorkspace: false }),
      cache: "no-store",
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok || !payload.available) {
      const status = payload.code === "CLINICAL_DB_NOT_CONFIGURED"
        ? "not_configured"
        : response.status === 401
          ? "unauthorized"
          : "error";
      state.patientCloud.available = false;
      state.patientCloud.status = status;
      state.patientCloud.error = payload.error || "La nube clinica no respondio correctamente.";
      state.patientCloud.message = "";
      renderAutoSaveTicker();
      if (!silent) render();
      return false;
    }
    state.patientCloud.available = true;
    state.patientCloud.status = "available";
    state.patientCloud.error = "";
    state.patientCloud.message = `Nube clinica conectada - esquema ${payload.schemaVersion || "?"}.`;
    state.patientCloud.clinicalWorkspaceId = payload.workspaceId || "";
    state.patientCloud.workspaceId = payload.workspaceId || state.patientCloud.workspaceId;
    if (payload.workspaceId) localStorageSet(STORAGE_PATIENT_WORKSPACE_ID, payload.workspaceId);
    renderAutoSaveTicker();
    if (!silent) render();
    return true;
  } catch (error) {
    state.patientCloud.available = false;
    state.patientCloud.status = "error";
    state.patientCloud.error = error?.message || "No se pudo consultar la nube clinica.";
    state.patientCloud.message = "";
    renderAutoSaveTicker();
    if (!silent) render();
    return false;
  }
}

function cloudPatientFileToLocal(row) {
  const payload = row?.file && typeof row.file === "object" ? row.file : row;
  if (!payload?.id) return null;
  return {
    ...payload,
    name: payload.name || row.name || "Expediente sin nombre",
    patientId: payload.patientId || row.patientId || "",
    patientNombre: payload.patientNombre || row.patientNombre || "Paciente sin nombre",
    planStartDate: payload.planStartDate || row.planStartDate || "",
    updatedAt: payload.updatedAt || row.updatedAt || row.lastSyncedAt || new Date().toISOString(),
    createdAt: payload.createdAt || row.createdAt || new Date().toISOString(),
    revision: Number(payload.revision ?? row.revision ?? 0) || 0,
    schemaVersion: Number(payload.schemaVersion ?? row.schemaVersion ?? 0) || 0,
    checksumSha256: payload.checksumSha256 || row.checksumSha256 || "",
    cloudSyncedAt: row.lastSyncedAt || new Date().toISOString(),
    source: "cloudflare_d1",
  };
}

function mergePatientFileCollections(localFiles, cloudRows) {
  const byId = new Map();
  [...(localFiles ?? [])].forEach((file) => {
    if (file?.id) byId.set(file.id, file);
  });
  (cloudRows ?? []).forEach((row) => {
    const file = cloudPatientFileToLocal(row);
    if (file?.id) byId.set(file.id, { ...(byId.get(file.id) ?? {}), ...file });
  });
  return [...byId.values()]
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")))
    .slice(0, MAX_STORED_FILES);
}

async function syncPatientFileToCloud(file, { silent = false } = {}) {
  ensurePatientCloudWorkspace();
  if (!hasPatientCloudAccess() || !file || !state.isOnline) return false;
  setPatientCloudStatus({ syncing: true, message: "Guardando expediente en nube...", error: "" });
  try {
    const response = await fetch("/api/patient-files", {
      method: "POST",
      headers: patientCloudHeaders(),
      body: JSON.stringify({
        file,
        baseRevision: Number.isFinite(Number(file.revision)) ? Number(file.revision) : 0,
        operationId: `op_${file.id}_${String(file.updatedAt || "").replace(/[^0-9A-Za-z]/g, "")}`,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok) {
      if (response.status === 409 || payload.code === "REVISION_CONFLICT") {
        state.patientCloud.conflict = {
          localFile: structuredClone(file),
          remoteFile: payload.file || null,
          currentRevision: payload.currentRevision,
          expectedRevision: payload.expectedRevision,
        };
        state.patientCloud.status = "conflict";
        state.patientCloud.error = "El expediente cambio en otro dispositivo. Revisa cual version conservar.";
        state.hasPendingChanges = true;
        state.saveStatus = "conflict";
        renderAutoSaveTicker();
        render();
        return false;
      }
      throw new Error(payload.error || "No se pudo guardar el expediente en nube.");
    }
    const cloudFile = cloudPatientFileToLocal(payload.file);
    if (cloudFile) {
      state.patientFiles = mergePatientFileCollections(state.patientFiles, [{ ...payload.file, file: cloudFile }]);
    }
    state.patientCloud.conflict = null;
    state.patientCloud.available = true;
    state.patientCloud.status = "available";
    state.patientCloud.lastSyncAt = new Date().toISOString();
    setPatientCloudStatus({ syncing: false, message: "Expediente guardado en nube.", error: "" });
    await persistPatientFiles();
    renderAutoSaveTicker();
    return true;
  } catch (error) {
    setPatientCloudStatus({ syncing: false, error: error?.message || "Error de nube." });
    if (!silent) window.alert(error?.message || "No se pudo sincronizar con la nube.");
    renderAutoSaveTicker();
    return false;
  }
}

async function loadPatientFilesFromCloud({ silent = false } = {}) {
  ensurePatientCloudWorkspace();
  if (!hasPatientCloudAccess()) {
    if (!silent) window.alert("No se pudo preparar la nube privada de expedientes.");
    return false;
  }
  setPatientCloudStatus({ syncing: true, message: "Consultando expedientes en nube...", error: "" });
  const activeFileId = state.currentFileId;
  const previousActiveUpdatedAt = getCurrentFile()?.updatedAt || "";
  try {
    const response = await fetch("/api/patient-files?includePayload=1&limit=500", {
      headers: patientCloudHeaders({ json: false }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok) {
      throw new Error(payload.error || "No se pudo cargar la nube de pacientes.");
    }
    state.patientFiles = mergePatientFileCollections(state.patientFiles, payload.files ?? []);
    state.patientCloud.lastSyncAt = new Date().toISOString();
    setPatientCloudStatus({ syncing: false, message: `${payload.count ?? state.patientFiles.length} expedientes disponibles en nube.`, error: "" });
    await persistPatientFiles();
    const refreshedActiveFile = activeFileId ? state.patientFiles.find((file) => file.id === activeFileId) : null;
    if (refreshedActiveFile?.data && !state.hasPendingChanges && refreshedActiveFile.updatedAt !== previousActiveUpdatedAt) {
      loadFileIntoState(refreshedActiveFile);
    } else if (!state.currentFileId && state.patientFiles[0]?.data) {
      loadFileIntoState(state.patientFiles[0]);
    }
    render();
    return true;
  } catch (error) {
    setPatientCloudStatus({ syncing: false, error: error?.message || "Error de nube." });
    if (!silent) window.alert(error?.message || "No se pudieron cargar los expedientes en nube.");
    return false;
  }
}

function resolvePatientCloudConflict(mode) {
  const conflict = state.patientCloud.conflict;
  if (!conflict) return;
  if (mode === "cloud" && conflict.remoteFile) {
    const remote = cloudPatientFileToLocal(conflict.remoteFile);
    if (remote?.id) {
      state.patientFiles = mergePatientFileCollections(state.patientFiles, [remote]);
      if (state.currentFileId === remote.id && remote.data) loadFileIntoState(remote);
    }
    state.patientCloud.conflict = null;
    state.patientCloud.status = "available";
    state.patientCloud.error = "";
    state.hasPendingChanges = false;
    state.saveStatus = state.isOnline ? "saved" : "offline";
    persistPatientFiles();
    render();
    return;
  }
  if (mode === "local" && conflict.localFile) {
    const local = structuredClone(conflict.localFile);
    local.revision = Number(conflict.currentRevision) || 0;
    state.patientFiles = state.patientFiles.map((item) => item.id === local.id ? local : item);
    state.patientCloud.conflict = null;
    state.patientCloud.status = "available";
    state.patientCloud.error = "";
    state.hasPendingChanges = false;
    state.saveStatus = "pending";
    persistPatientFiles();
    render();
    syncPatientFileToCloud(local, { silent: false });
  }
}

async function copyPatientCloudLink() {
  ensurePatientCloudWorkspace();
  const url = patientCloudShareUrl();
  try {
    await navigator.clipboard.writeText(url);
    setPatientCloudStatus({ message: "Enlace privado de nube copiado. Abre ese enlace en otro dispositivo para ver los expedientes.", error: "" });
  } catch {
    window.prompt("Copia este enlace privado para abrir los expedientes en otro dispositivo:", url);
    setPatientCloudStatus({ message: "Enlace privado de nube preparado.", error: "" });
  }
  render();
}

function getCurrentFile() {
  return state.patientFiles.find((item) => item.id === state.currentFileId) || null;
}

function markPendingChanges() {
  state.hasPendingChanges = true;
  state.saveStatus = state.isOnline ? "pending" : "offline";
  renderAutoSaveTicker();
  scheduleCloudAutoSave();
}

function assessmentMethodMode(method) {
  const value = String(method || "").toLowerCase();
  if (value === "cinco_componentes") return "five_components";
  if (value === "sumatoria_pliegues") return "sums";
  if (value === "durnin_siri" || value === "yuhasz" || value === "rfm") return "two_components";
  return value || "configured_method";
}

function currentAssessmentSnapshot({ id, sequenceNumber, previousAssessmentId = null, label } = {}) {
  const anthropometry = calculateAnthropometry(state.patient);
  const measurementNames = [...anthropometryInputFields];
  const measurements = Object.fromEntries(measurementNames.map((name) => [name, state.patient[name] ?? ""]));
  const sequence = Number(sequenceNumber || state.patient.numero_valoracion || 1) || 1;
  const reportConfig = selectAssessmentReportConfig({
    populationProfile: state.patient.population_profile,
    method: state.patient.metodo_antropometria,
    sequenceNumber: sequence,
  });
  return {
    id: id || generateEntityId("assessment"),
    patientId: getPatientIdentifier(state.patient),
    assessmentDate: state.patient.assessment_date || state.currentPlanStartDate || formatDateForFile(),
    sequenceNumber: sequence,
    label: label || (sequence === 1 ? "Valoración inicial" : `Control ${sequence - 1}`),
    populationProfile: state.patient.population_profile === "pediatric" ? "pediatric" : "adult",
    method: state.patient.metodo_antropometria || "yuhasz",
    reportMode: reportConfig.mode,
    formulaProfile: state.patient.formula_profile || "legacy_excel_2026",
    formulaVersion: state.patient.formula_version || ASSESSMENT_REPORT_VERSION,
    reportConfig,
    previousAssessmentId,
    measurements,
    results: structuredClone(anthropometry),
    patientSnapshot: structuredClone(state.patient),
    updatedAt: new Date().toISOString(),
  };
}

function syncActiveAssessmentSnapshot() {
  if (!state.activeAssessmentId || !state.patient) return;
  const index = state.assessments.findIndex((assessment) => assessment.id === state.activeAssessmentId);
  if (index < 0) return;
  const current = state.assessments[index];
  state.assessments[index] = currentAssessmentSnapshot({
    id: current.id,
    sequenceNumber: current.sequenceNumber,
    previousAssessmentId: current.previousAssessmentId,
    label: current.label,
  });
}

function createAssessmentRecord(kind = "control") {
  syncActiveAssessmentSnapshot();
  const previous = state.assessments.at(-1) || null;
  const maxSequence = state.assessments.reduce((max, item) => Math.max(max, Number(item.sequenceNumber) || 0), 0);
  const sequenceNumber = kind === "initial" && !state.assessments.length ? 1 : Math.max(2, maxSequence + 1);
  state.patient.numero_valoracion = sequenceNumber;
  state.patient.assessment_date = state.patient.assessment_date || formatDateForFile();
  const assessment = currentAssessmentSnapshot({
    sequenceNumber,
    previousAssessmentId: kind === "control" ? previous?.id || null : null,
    label: sequenceNumber === 1 ? "Valoración inicial" : `Control ${sequenceNumber - 1}`,
  });
  state.assessments = [...state.assessments, assessment];
  state.activeAssessmentId = assessment.id;
  markPendingChanges();
  render();
}

function loadAssessmentFromHistory(assessmentId) {
  syncActiveAssessmentSnapshot();
  const assessment = state.assessments.find((item) => item.id === assessmentId);
  if (!assessment) return;
  if (assessment.patientSnapshot) state.patient = normalizePatientProfile(structuredClone(assessment.patientSnapshot));
  state.activeAssessmentId = assessment.id;
  state.currentPlanStartDate = assessment.assessmentDate || state.currentPlanStartDate;
  syncDraftFromPatient(state.patient, true);
  hydrateGenerated();
  state.hasPendingChanges = false;
  state.saveStatus = state.isOnline ? "saved" : "offline";
  render();
}

function assessmentComparison(current, previous) {
  const comparison = buildAssessmentComparison(current, previous);
  if (!comparison.available) return "Sin valoración anterior para comparar.";
  return comparison.metrics
    .map((metric) => `${metric.label}: ${formatDecimal(metric.current, 1)} ${metric.unit} (${metric.delta >= 0 ? "+" : ""}${formatDecimal(metric.delta, 1)} ${metric.unit})`)
    .join(" · ") || "Sin resultados comparables todavía.";
}

function renderAssessmentHistory() {
  const assessments = [...state.assessments].sort((a, b) => (Number(a.sequenceNumber) || 0) - (Number(b.sequenceNumber) || 0));
  return `
    <div class="assessment-history">
      <div class="assessment-history__heading">
        <div>
          <span>Seguimiento longitudinal</span>
          <h4>Valoraciones y controles</h4>
          <small>Cada control conserva su método, fórmula, población y resultados; no sobrescribe la valoración anterior.</small>
        </div>
        <div class="file-actions">
          <button data-action="create-initial-assessment" ${assessments.length ? "disabled" : ""}>Registrar inicial</button>
          <button data-action="create-control-assessment" ${assessments.length ? "" : "disabled"}>Registrar control</button>
        </div>
      </div>
      ${assessments.length ? `<div class="assessment-history__list">
        ${assessments.map((assessment, index) => {
          const previous = assessments[index - 1];
          const active = assessment.id === state.activeAssessmentId;
          return `<article class="assessment-history__item ${active ? "is-active" : ""}">
            <div>
              <strong>${escapeHtml(assessment.label || `Valoración ${assessment.sequenceNumber}`)}</strong>
              <span>${escapeHtml(assessment.assessmentDate || "Fecha pendiente")} · ${escapeHtml(assessment.populationProfile === "pediatric" ? "Pediátrico" : "Adulto")} · ${escapeHtml(assessment.method || "Método pendiente")}</span>
              <small>Perfil ${escapeHtml(assessment.formulaProfile || "legacy_excel_2026")} v${escapeHtml(assessment.formulaVersion || "1.0.0")}</small>
              <small>${escapeHtml(assessmentComparison(assessment, previous))}</small>
            </div>
            <button data-action="load-assessment" data-assessment-id="${escapeHtml(assessment.id)}">${active ? "En edición" : "Abrir"}</button>
          </article>`;
        }).join("")}
      </div>` : `<div class="empty-state">Todavía no has registrado una valoración inicial.</div>`}
    </div>
  `;
}

function serializeForFile() {
  syncActiveAssessmentSnapshot();
  return {
    patient: structuredClone(state.patient),
    scheme: structuredClone(state.scheme),
    menu: structuredClone(state.menu),
    recommendations: structuredClone(state.recommendations),
    planStartDate: state.currentPlanStartDate,
    selectedSupplements: [...state.selectedSupplements],
    selectedProducts: [...state.selectedProducts],
    selectedRecipes: [...state.selectedRecipes],
    planProductLinks: structuredClone(state.planProducts.links),
    planProductDay: state.planProducts.day,
    planProductMealId: state.planProducts.mealId,
    planProductServingGrams: state.planProducts.servingGrams,
    planProductRetailer: state.planProducts.retailer,
    supplementFormat: state.supplementFormat,
    shoppingChecklist: [...state.shoppingChecklist],
    deliverableOptions: structuredClone(state.deliverableOptions),
    assessments: structuredClone(state.assessments),
    activeAssessmentId: state.activeAssessmentId || "",
  };
}

function saveCurrentFile() {
  if (!state.currentFileId) {
    if (state.patient) createNewPatientFile();
    return;
  }
  ensurePatientCloudWorkspace();
  const file = getCurrentFile();
  if (!file) return;
  state.saveStatus = "saving";
  renderAutoSaveTicker();
  const nextFile = {
    ...file,
    data: serializeForFile(),
    patientId: getPatientIdentifier(state.patient),
    patientNombre: state.patient?.nombre || file.patientNombre || "Paciente sin nombre",
    planStartDate: state.currentPlanStartDate || file.planStartDate || formatDateForFile(),
    workspaceId: state.patientCloud.workspaceId || file.workspaceId || "",
    updatedAt: new Date().toISOString(),
    hasChanges: false,
  };
  state.patientFiles = state.patientFiles.map((item) => (item.id === state.currentFileId ? nextFile : item));
  state.lastAutoSaveAt = nextFile.updatedAt;
  state.hasPendingChanges = false;
  state.nextAutoSaveAt = state.autoSaveIntervalMs ? Date.now() + state.autoSaveIntervalMs : null;
  persistPatientFiles().then(() => {
    state.saveStatus = state.hasPendingChanges ? "pending" : (state.isOnline ? "saved" : "offline");
    renderAutoSaveTicker();
    syncPatientFileToCloud(nextFile, { silent: true });
  });
  renderAutoSaveTicker();
}

function clearAutoSaveInterval() {
  if (state.autoSaveDebounceId !== null) {
    clearTimeout(state.autoSaveDebounceId);
    state.autoSaveDebounceId = null;
  }
  if (state.autoSaveIntervalId !== null) {
    clearInterval(state.autoSaveIntervalId);
    state.autoSaveIntervalId = null;
  }
  if (state.autoSaveTickerId !== null) {
    clearInterval(state.autoSaveTickerId);
    state.autoSaveTickerId = null;
  }
}

function startAutoSaveInterval() {
  clearAutoSaveInterval();
  if (!state.currentFileId || !state.autoSaveIntervalMs) return;
  state.nextAutoSaveAt = Date.now() + state.autoSaveIntervalMs;
  state.autoSaveIntervalId = window.setInterval(() => {
    if (!state.hasPendingChanges) return;
    saveCurrentFile();
  }, state.autoSaveIntervalMs);
  state.autoSaveTickerId = window.setInterval(renderAutoSaveTicker, 1000);
  renderAutoSaveTicker();
}

function scheduleCloudAutoSave() {
  if (typeof window === "undefined" || !state.patient) return;
  if (state.autoSaveDebounceId !== null) {
    clearTimeout(state.autoSaveDebounceId);
  }
  state.nextAutoSaveAt = Date.now() + CLOUD_AUTO_SAVE_DEBOUNCE_MS;
  state.autoSaveDebounceId = window.setTimeout(() => {
    state.autoSaveDebounceId = null;
    if (!state.hasPendingChanges && state.currentFileId) return;
    saveCurrentFile();
  }, CLOUD_AUTO_SAVE_DEBOUNCE_MS);
  renderAutoSaveTicker();
}

function loadFileIntoState(file) {
  state.patient = normalizePatientProfile(structuredClone(file.data?.patient ?? state.data.patients[0]));
  state.scheme = structuredClone(file.data?.scheme ?? buildDailyScheme(state.patient, state.data));
  state.menu = structuredClone(file.data?.menu ?? generateMenu(state.patient, state.data));
  state.recommendations = structuredClone(file.data?.recommendations ?? generateRecommendations(state.patient, state.data));
  state.selectedSupplements = new Set(file.data?.selectedSupplements ?? ["sup_whey"]);
  state.selectedProducts = new Set(file.data?.selectedProducts ?? ["prd_yogurt_griego"]);
  state.selectedRecipes = new Set(file.data?.selectedRecipes ?? ["rec_avena_banano"]);
  state.planProducts.links = normalizePlanProductLinks(file.data?.planProductLinks ?? file.data?.planProducts ?? []);
  state.planProducts.day = normalizePlanProductDay(file.data?.planProductDay ?? state.planProducts.day ?? "all");
  state.planProducts.mealId = file.data?.planProductMealId || state.planProducts.mealId || "desayuno";
  state.planProducts.servingGrams = normalizePlanProductServing(file.data?.planProductServingGrams ?? state.planProducts.servingGrams ?? 100);
  state.planProducts.retailer = file.data?.planProductRetailer || state.planProducts.retailer || "";
  state.supplementFormat = file.data?.supplementFormat ?? "destacado";
  state.shoppingChecklist = new Set(file.data?.shoppingChecklist ?? []);
  state.deliverableOptions = normalizeDeliverableOptions(file.data?.deliverableOptions);
  state.assessments = structuredClone(file.data?.assessments ?? []);
  state.activeAssessmentId = file.data?.activeAssessmentId || state.assessments.at(-1)?.id || "";
  state.currentPlanStartDate = file.planStartDate || file.data?.planStartDate || file.createdAt?.slice(0, 10) || formatDateForFile();
  state.autoSaveIntervalMs = DEFAULT_AUTO_SAVE_INTERVAL_MS;
  state.newFileDraft = {
    name: file.name || "",
    type: file.type || DEFAULT_FILE_TYPE,
  };
  state.currentFileId = file.id;
  state.lastAutoSaveAt = file.updatedAt || null;
  if (file.updatedAt) {
    const lastSaveAt = new Date(file.updatedAt).getTime();
    state.nextAutoSaveAt = Number.isFinite(lastSaveAt)
      ? lastSaveAt + state.autoSaveIntervalMs
      : Date.now() + state.autoSaveIntervalMs;
  } else {
    state.nextAutoSaveAt = state.autoSaveIntervalMs ? Date.now() + state.autoSaveIntervalMs : null;
  }
  state.hasPendingChanges = false;
  state.saveStatus = state.isOnline ? "saved" : "offline";
  startAutoSaveInterval();
}

function buildFileOptionLabel(file) {
  const lastUpdate = file.updatedAt ? ` · ${formatDateTime(new Date(file.updatedAt))}` : "";
  const patientLabel = file.patientNombre ? ` (${escapeHtml(cleanVisibleName(file.patientNombre) || "Paciente")})` : "";
  const typeLabel = file.type ? ` [${escapeHtml(file.type)}]` : "";
  return `${escapeHtml(file.name)}${typeLabel}${patientLabel}${lastUpdate}`;
}

function toggleCustomFileTypeInput(select) {
  const toolbar = select.closest(".file-toolbar");
  const customInput = toolbar?.querySelector("[data-new-file-type-custom]");
  if (!customInput) return;
  const isCustom = select.value === FILE_TYPE_CUSTOM_VALUE;
  customInput.classList.toggle("is-hidden", !isCustom);
}

function loadFileById(fileId) {
  const file = state.patientFiles.find((item) => item.id === fileId);
  if (!file) return;
  if (state.currentFileId === file.id) return;
  loadFileIntoState(file);
  render();
}

function createNewPatientFile() {
  if (state.currentFileId && state.hasPendingChanges) {
    saveCurrentFile();
  }
  ensurePatientCloudWorkspace();
  const normalizedType = normalizeFileType(state.newFileDraft.type);
  const nameHint = normalizedType ? ` ${normalizedType}` : "";
  const name = (state.newFileDraft.name || "").trim() || `${getVisiblePatientName(state.patient) || "Paciente"}${nameHint} ${formatDateForFile()}`;
  const type = normalizedType;
  state.shoppingChecklist = new Set();
  const now = new Date().toISOString();
  const planStartDate = state.currentPlanStartDate || formatDateForFile();
  const file = {
    id: `file_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    name,
    type,
    patientId: getPatientIdentifier(state.patient),
    planStartDate,
    createdAt: now,
    updatedAt: now,
    patientNombre: getVisiblePatientName(state.patient) || "Paciente",
    workspaceId: state.patientCloud.workspaceId || "",
    data: serializeForFile(),
  };
  const next = [file, ...state.patientFiles.filter((entry) => entry.id !== file.id)];
  state.patientFiles = next.slice(0, MAX_STORED_FILES);
  state.currentFileId = file.id;
  state.autoSaveIntervalMs = DEFAULT_AUTO_SAVE_INTERVAL_MS;
  state.currentPlanStartDate = planStartDate;
  state.newFileDraft.type = type;
  state.lastAutoSaveAt = now;
  state.saveStatus = state.isOnline ? "saved" : "offline";
  state.nextAutoSaveAt = Date.now() + state.autoSaveIntervalMs;
  state.hasPendingChanges = false;
  persistPatientFiles();
  syncPatientFileToCloud(file, { silent: true });
  startAutoSaveInterval();
  render();
}

function autoSaveStateLabel() {
  if (!state.isOnline) return "Sin conexión";
  if (state.saveStatus === "conflict" || state.patientCloud.status === "conflict") return "Conflicto";
  if (state.saveStatus === "saving") return "Guardando...";
  if (state.hasPendingChanges || state.saveStatus === "pending") return "Pendiente";
  return "Guardado";
}

function buildAutoSaveStatusText(currentFile) {
  const statusLabelNow = autoSaveStateLabel();
  const cloudLabelNow = hasPatientCloudAccess()
    ? "nube clínica conectada"
    : `respaldo local (${state.storageEngine}); ${patientCloudStatusText()}`;
  if (!currentFile) {
    return `${statusLabelNow} - crea un expediente para activar el guardado continuo.`;
  }
  if (!state.isOnline) {
    return `Sin conexion - cambios protegidos en respaldo local. Se sincronizaran al volver la conexion.`;
  }
  if (state.saveStatus === "saving") {
    return `Guardando automaticamente en ${cloudLabelNow}.`;
  }
  if (state.saveStatus === "conflict" || state.patientCloud.status === "conflict") {
    return "Conflicto de versión: conserva la versión local o la de la nube para continuar.";
  }
  if (!state.hasPendingChanges) {
    return state.lastAutoSaveAt
      ? `Guardado - ultimo guardado ${formatDateTime(new Date(state.lastAutoSaveAt))} - ${cloudLabelNow}`
      : "Guardado - nube lista y autoguardado activo.";
  }
  return state.nextAutoSaveAt
    ? `Pendiente - autoguardado automatico en ${formatSecondsLeft(state.nextAutoSaveAt - Date.now())}`
    : "Pendiente - autoguardado automatico preparado";
}

function renderAutoSaveTicker() {
  const statusTarget = app?.querySelector("[data-auto-save-status]");
  if (!statusTarget) return;
  const file = getCurrentFile();
  statusTarget.textContent = buildAutoSaveStatusText(file);
}

function saveOnDemand() {
  if (!state.currentFileId) {
    createNewPatientFile();
    return;
  }
  saveCurrentFile();
}

function resetPatientWorkspace(patient) {
  state.patient = normalizePatientProfile(patient);
  ensurePatientInCatalog(state.patient);
  state.currentFileId = null;
  state.selectedSupplements = new Set(["sup_whey"]);
  state.selectedProducts = new Set(["prd_yogurt_griego"]);
  state.selectedRecipes = new Set(["rec_avena_banano"]);
  state.planProducts.links = [];
  state.planProducts.search = "";
  state.planProducts.retailer = "";
  state.planProducts.day = "all";
  state.planProducts.mealId = "desayuno";
  state.planProducts.servingGrams = 100;
  state.planProducts.results = [];
  state.planProducts.message = "";
  state.planProducts.error = "";
  state.supplementFormat = "destacado";
  state.shoppingChecklist = new Set();
  state.assessments = [];
  state.activeAssessmentId = "";
  state.deliverableOptions = normalizeDeliverableOptions();
  state.newFileDraft.type = DEFAULT_FILE_TYPE;
  state.autoSaveIntervalMs = DEFAULT_AUTO_SAVE_INTERVAL_MS;
  state.currentPlanStartDate = formatDateForFile();
  state.hasPendingChanges = false;
  state.saveStatus = "saved";
  state.lastAutoSaveAt = null;
  state.nextAutoSaveAt = null;
  syncDraftFromPatient(state.patient, true);
  hydrateGenerated();
}

function createBlankPatientFile() {
  if (state.currentFileId && state.hasPendingChanges) {
    saveCurrentFile();
  }
  clearAutoSaveInterval();
  const patient = {
    ...DEFAULT_PATIENT_PROFILE,
    id: generateEntityId("paciente"),
    nombre: "Nuevo paciente",
  };
  resetPatientWorkspace(patient);
  createNewPatientFile();
}

function duplicateCurrentFile() {
  if (!state.currentFileId) {
    saveOnDemand();
  } else {
    saveCurrentFile();
  }
  const currentFile = getCurrentFile();
  if (!currentFile) return;
  const now = new Date().toISOString();
  const copy = {
    ...structuredClone(currentFile),
    id: generateEntityId("file"),
    name: `${currentFile.name} copia`,
    createdAt: now,
    updatedAt: now,
  };
  state.patientFiles = [copy, ...state.patientFiles.filter((file) => file.id !== copy.id)].slice(0, MAX_STORED_FILES);
  loadFileIntoState(copy);
  persistPatientFiles();
  syncPatientFileToCloud(copy, { silent: true });
  render();
}

async function deleteCurrentFile() {
  const currentFile = getCurrentFile();
  if (!currentFile) return;
  const cloudText = hasPatientCloudAccess() ? " de la nube privada y del respaldo local" : " de este navegador";
  const confirmed = window.confirm(`Eliminar el archivo "${currentFile.name}"${cloudText}?`);
  if (!confirmed) return;
  if (hasPatientCloudAccess()) {
    setPatientCloudStatus({ syncing: true, message: "Eliminando expediente en nube...", error: "" });
    try {
      const response = await fetch(`/api/patient-files/${encodeURIComponent(currentFile.id)}`, {
        method: "DELETE",
        headers: patientCloudHeaders({ json: false }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "No se pudo eliminar el expediente en nube.");
      }
      state.patientCloud.lastSyncAt = new Date().toISOString();
      setPatientCloudStatus({ syncing: false, message: "Expediente eliminado de la nube.", error: "" });
    } catch (error) {
      setPatientCloudStatus({ syncing: false, error: error?.message || "Error de nube." });
      window.alert(error?.message || "No se pudo eliminar el expediente en nube.");
      return;
    }
  }
  state.patientFiles = state.patientFiles.filter((file) => file.id !== currentFile.id);
  state.currentFileId = null;
  state.hasPendingChanges = false;
  state.saveStatus = "saved";
  state.lastAutoSaveAt = null;
  state.nextAutoSaveAt = null;
  state.currentPlanStartDate = formatDateForFile();
  clearAutoSaveInterval();
  persistPatientFiles();
  syncDraftFromPatient(state.patient, true);
  render();
}

function buildPatientFileExportPayload(file) {
  return {
    kind: PATIENT_FILE_EXPORT_KIND,
    exportedAt: new Date().toISOString(),
    app: "Sistema Digital de Nutrición",
    file,
  };
}

function downloadPatientFileExport() {
  if (!state.currentFileId) {
    createNewPatientFile();
  } else {
    saveCurrentFile();
  }
  const currentFile = getCurrentFile();
  if (!currentFile) return;
  const payload = buildPatientFileExportPayload(currentFile);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  downloadBlob(blob, `${slugify(currentFile.name)}_archivo_paciente.json`);
}

function readFileAsText(file) {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function normalizeImportedFilePayload(payload) {
  const sourceFile = payload?.kind === PATIENT_FILE_EXPORT_KIND ? payload.file : payload?.file;
  const fileCandidate = sourceFile && typeof sourceFile === "object" ? sourceFile : payload;
  const exportedData = payload?.patient
    ? {
      patient: payload.patient,
      scheme: payload.scheme,
      menu: payload.menu,
      recommendations: payload.recommendations,
      selectedSupplements: payload.supplements?.map((item) => item.id).filter(Boolean),
      selectedProducts: payload.products?.map((item) => item.id).filter(Boolean),
      selectedRecipes: payload.recipes?.map((item) => item.id).filter(Boolean),
      planProductLinks: payload.planProducts?.links ?? payload.planProducts ?? [],
      planProductDay: payload.planProducts?.defaultDay ?? "all",
      planProductMealId: payload.planProducts?.defaultMealId ?? "desayuno",
      planProductServingGrams: payload.planProducts?.defaultServingGrams ?? 100,
      planProductRetailer: payload.planProducts?.defaultRetailer ?? "",
      planStartDate: payload.metadata?.file?.planStartDate,
      shoppingChecklist: [],
      deliverableOptions: payload.deliverableOptions,
      assessments: payload.assessments ?? [],
      activeAssessmentId: payload.activeAssessmentId || payload.assessments?.at(-1)?.id || "",
    }
    : null;
  const data = fileCandidate?.data || exportedData;
  if (!data?.patient) {
    throw new Error("El JSON no contiene un archivo de paciente compatible.");
  }
  const now = new Date().toISOString();
  const patient = normalizePatientProfile(structuredClone(data.patient));
  if (!patient.id) patient.id = generateEntityId("paciente_importado");
  const planStartDate = fileCandidate?.planStartDate || data.planStartDate || payload?.metadata?.file?.planStartDate || now.slice(0, 10);
  return {
    id: generateEntityId("file_importado"),
    name: fileCandidate?.name || payload?.metadata?.file?.name || buildDefaultFileName(patient),
    type: fileCandidate?.type || payload?.metadata?.file?.type || DEFAULT_FILE_TYPE,
    patientId: getPatientIdentifier(patient),
    patientNombre: patient.nombre || "Paciente importado",
    planStartDate,
    createdAt: now,
    updatedAt: now,
    importedAt: now,
    data: {
      patient,
      scheme: structuredClone(data.scheme ?? []),
      menu: structuredClone(data.menu ?? []),
      recommendations: structuredClone(data.recommendations ?? []),
      selectedSupplements: data.selectedSupplements ?? ["sup_whey"],
      selectedProducts: data.selectedProducts ?? ["prd_yogurt_griego"],
      selectedRecipes: data.selectedRecipes ?? ["rec_avena_banano"],
      planProductLinks: normalizePlanProductLinks(data.planProductLinks ?? data.planProducts ?? []),
      planProductRetailer: data.planProductRetailer ?? "",
      planProductDay: normalizePlanProductDay(data.planProductDay ?? "all"),
      planProductMealId: data.planProductMealId ?? "desayuno",
      planProductServingGrams: normalizePlanProductServing(data.planProductServingGrams ?? 100),
      supplementFormat: data.supplementFormat ?? "destacado",
      planStartDate,
      shoppingChecklist: data.shoppingChecklist ?? [],
      deliverableOptions: normalizeDeliverableOptions(data.deliverableOptions),
      assessments: structuredClone(data.assessments ?? []),
      activeAssessmentId: data.activeAssessmentId || data.assessments?.at(-1)?.id || "",
    },
  };
}

async function importPatientFileFromInput(input) {
  const file = input.files?.[0];
  if (!file) return;
  try {
    const text = await readFileAsText(file);
    const payload = JSON.parse(text);
    const importedFile = normalizeImportedFilePayload(payload);
    state.patientFiles = [importedFile, ...state.patientFiles.filter((entry) => entry.id !== importedFile.id)].slice(0, MAX_STORED_FILES);
    loadFileIntoState(importedFile);
    ensurePatientInCatalog(state.patient);
    persistPatientFiles();
    render();
  } catch (error) {
    window.alert(error?.message || "No se pudo importar el archivo de paciente.");
  } finally {
    input.value = "";
  }
}

const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' fill='%23eceff1'/%3E%3Ccircle cx='40' cy='35' r='16' fill='%23b0bec5'/%3E%3Ctext x='40' y='64' font-size='10' text-anchor='middle' fill='%23828f96' font-family='Arial, Helvetica, sans-serif'%3EIMG%3C/text%3E%3C/svg%3E";

const imageAliases = new Map([
  ["ali_aguacate_001", "images/aguacate.png"],
  ["aguacate", "images/aguacate.png"],
  ["aguacate_v01.png", "images/aguacate.png"],
  ["ali_arroz_integral_001", "images/arroz.png"],
  ["arroz", "images/arroz.png"],
  ["arroz_integral", "images/arroz.png"],
  ["arroz_v01.png", "images/arroz.png"],
  ["ali_avena_001", "images/avena_producto.png"],
  ["avena", "images/avena_producto.png"],
  ["avena en hojuelas", "images/avena_producto.png"],
  ["avena_v01.png", "images/avena_producto.png"],
  ["rec_avena_banano", "images/avena_producto.png"],
  ["ali_banano_001", "images/banano.png"],
  ["banano", "images/banano.png"],
  ["banano_v01.png", "images/banano.png"],
  ["ali_huevo_001", "images/huevo.png"],
  ["huevo", "images/huevo.png"],
  ["huevo entero", "images/huevo.png"],
  ["huevo_v01.png", "images/huevo.png"],
  ["prd_yogurt_griego", "images/yogurt_griego.png"],
  ["yogurt griego", "images/yogurt_griego.png"],
  ["yogurt griego natural", "images/yogurt_griego.png"],
  ["yogurt_griego_v01.png", "images/yogurt_griego.png"],
  ["yogurth griego", "images/yogurt_griego.png"],
  ["prd_tostadas_arroz", "images/tostadas_arroz.png"],
  ["tostadas de arroz", "images/tostadas_arroz.png"],
  ["tostadas_arroz_v01.png", "images/tostadas_arroz.png"],
  ["sup_whey", "images/suplemento.png"],
  ["sup_creatina", "images/suplemento.png"],
  ["proteina whey", "images/suplemento.png"],
  ["creatina", "images/suplemento.png"],
  ["proteina_whey_v01.png", "images/suplemento.png"],
  ["creatina_v01.png", "images/suplemento.png"],
]);

function normalizeImageKey(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replaceAll("\\", "/")
    .split("/")
    .at(-1)
    .replace(/[_-]+/g, " ")
    .trim();
}

function getSafePublicImagePath(value) {
  const raw = String(value ?? "").trim().replaceAll("\\", "/");
  const normalized = raw.replace(/^\.?\//, "").replace(/^public\//, "");
  if (normalized.includes("..")) return null;
  if (/^images\/[a-z0-9][a-z0-9._/-]*\.(png|jpe?g|webp|gif|svg)$/i.test(normalized)) {
    return normalized;
  }
  return null;
}

function imageCandidates(itemOrId) {
  if (typeof itemOrId === "string") return [itemOrId];
  const item = itemOrId ?? {};
  return [
    item.id,
    item.nombre,
    item.imagen_asociada,
    item.imagen_opcional,
    item.ruta_publica,
    item.ruta,
    item.categoria,
    item.descripcion,
    item.ingredientes,
  ].filter(Boolean);
}

function getItemImage(itemOrId) {
  for (const candidate of imageCandidates(itemOrId)) {
    const raw = String(candidate ?? "").trim();
    const publicPath = getSafePublicImagePath(raw);
    if (publicPath) return publicPath;
    const basename = raw.replaceAll("\\", "/").split("/").at(-1);
    const keys = [
      raw,
      raw.toLowerCase(),
      basename,
      basename?.toLowerCase(),
      normalizeImageKey(raw),
      normalizeImageKey(basename),
    ].filter(Boolean);
    for (const key of keys) {
      if (imageAliases.has(key)) return imageAliases.get(key);
    }
  }
  const joined = normalizeImageKey(imageCandidates(itemOrId).join(" "));
  for (const [key, src] of imageAliases.entries()) {
    if (joined.includes(normalizeImageKey(key))) return src;
  }
  return fallbackImage;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatNumber(value, suffix = "") {
  if (!Number.isFinite(value) || value === 0) return `0${suffix}`;
  return `${new Intl.NumberFormat("es-CO").format(value)}${suffix}`;
}

function formatCurrencyCents(value, currency = "COP") {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "";
  return `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(number / 100)} ${currency || "COP"}`;
}

function formatOptionalNumber(value, suffix = "") {
  if (!Number.isFinite(value) || value <= 0) return "Sin dato";
  return formatNumber(Math.round(value), suffix);
}

function formatDelta(current, target, suffix = "") {
  if (!Number.isFinite(current) || current <= 0 || !Number.isFinite(target)) return "Sin dato";
  const delta = Math.round(current - target);
  if (delta === 0) return `0${suffix}`;
  return `${delta > 0 ? "+" : ""}${formatNumber(delta, suffix)}`;
}

function formatDecimal(value, decimals = 1) {
  if (!Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: Number.isInteger(value) ? 0 : Math.min(decimals, 1),
  }).format(value);
}

function updateTextByDataAttribute(attribute, value) {
  document.querySelectorAll(`[data-${attribute}]`).forEach((element) => {
    element.textContent = value;
  });
}

function updateCurrentIntakeDisplays() {
  const macros = calculateMacros(state.patient);
  const intake = calculateCurrentIntake(state.patient);
  updateTextByDataAttribute("intake-calories", formatOptionalNumber(intake.calories, " kcal"));
  updateTextByDataAttribute("intake-delta", formatDelta(intake.calories, macros.calories, " kcal"));
  updateTextByDataAttribute("intake-protein", formatOptionalNumber(intake.protein, " g"));
  updateTextByDataAttribute("intake-carbs-fat", `${formatOptionalNumber(intake.carbs, " g")} / ${formatOptionalNumber(intake.fat, " g")}`);
}

function formatDateForFile(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

function slugify(value) {
  return String(value || "plan")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70) || "plan";
}

function exportFileBase() {
  return `${slugify(state.patient?.id || state.patient?.nombre)}_${formatDateForFile()}`;
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

function escapeCsvCell(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  const source = String(text ?? "").replace(/^\ufeff/, "");
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const nextChar = source[index + 1];
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }
    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") index += 1;
      row.push(cell);
      if (row.some((value) => String(value).trim() !== "")) rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += char;
  }
  row.push(cell);
  if (row.some((value) => String(value).trim() !== "")) rows.push(row);
  return rows;
}

function csvRowsToObjects(rows) {
  const [headerRow, ...dataRows] = rows;
  const headers = (headerRow ?? []).map((header) => String(header ?? "").trim()).filter(Boolean);
  if (!headers.length) {
    throw new Error("El CSV no contiene encabezados.");
  }
  return {
    headers,
    rows: dataRows.map((row) => {
      const item = {};
      headers.forEach((header, index) => {
        item[header] = row[index] ?? "";
      });
      return item;
    }),
  };
}

const PRODUCT_ADMIN_EXPORT_COLUMNS = [
  "id",
  "displayName",
  "brand",
  "barcodeEAN",
  "categoryLevel1",
  "categoryLevel2",
  "foodGroup",
  "packageSize",
  "unit",
  "dataStatus",
  "isDemo",
  "isActive",
  "energyKcalPer100g",
  "proteinPer100g",
  "carbohydratesPer100g",
  "totalFatPer100g",
  "fiberPer100g",
  "sodiumPer100g",
  "nutritionSourceName",
  "nutritionSourceUrl",
  "retailerName",
  "productUrl",
  "priceCents",
  "currency",
  "availability",
  "exactMatchStatus",
  "sourceUrl",
  "description",
  "ingredients",
];

function defaultProductAdminForm() {
  return {
    id: "",
    displayName: "",
    brand: "",
    barcodeEAN: "",
    categoryLevel1: "",
    categoryLevel2: "",
    foodGroup: "",
    packageSize: "",
    unit: "g",
    dataStatus: "pending",
    isDemo: false,
    isActive: true,
    description: "",
    ingredients: "",
    energyKcalPer100g: "",
    proteinPer100g: "",
    carbohydratesPer100g: "",
    totalFatPer100g: "",
    fiberPer100g: "",
    sodiumPer100g: "",
    nutritionSourceName: "",
    nutritionSourceUrl: "",
    retailerName: "",
    productUrl: "",
    priceCents: "",
    currency: "COP",
    availability: "pending",
    exactMatchStatus: "manual_review",
    sourceUrl: "",
  };
}

function productAdminToken() {
  return String(state.productAdmin.adminToken || "").trim();
}

function productAdminHeaders({ json = true } = {}) {
  const headers = {};
  const token = productAdminToken();
  if (json) headers["content-type"] = "application/json";
  if (token) headers["x-admin-token"] = token;
  return headers;
}

function productAdminApiQuery() {
  const params = new URLSearchParams();
  const filters = state.productAdmin.filters;
  for (const [key, value] of Object.entries(filters)) {
    const text = String(value ?? "").trim();
    if (text) params.set(key, text);
  }
  params.set("limit", "80");
  return params.toString();
}

function productAdminSetStatus({ message = "", error = "" } = {}) {
  state.productAdmin.message = message;
  state.productAdmin.error = error;
}

function planProductSetStatus({ message = "", error = "" } = {}) {
  state.planProducts.message = message;
  state.planProducts.error = error;
}

function normalizePlanProductNumber(value) {
  if (value === "" || value === null || value === undefined) return "";
  const number = Number(value);
  return Number.isFinite(number) ? number : "";
}

function normalizePlanProductServing(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 100;
  return Math.round(number);
}

function normalizePlanProductDay(value) {
  if (value === "all") return "all";
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 && number <= 7 ? number : "all";
}

function planProductDayLabel(value) {
  const day = normalizePlanProductDay(value);
  return day === "all" ? "Todos los días" : `Día ${day}`;
}

function planProductTargetLabel(link) {
  return `${planProductDayLabel(link.day)} · ${mealLabel(link.mealId)}`;
}

function planProductDayOptions(selectedValue) {
  const selected = normalizePlanProductDay(selectedValue);
  const options = [{ value: "all", label: "Todos los días" }];
  for (let day = 1; day <= 7; day += 1) {
    options.push({ value: String(day), label: `Día ${day}` });
  }
  return options.map((option) => `<option value="${escapeHtml(option.value)}" ${String(selected) === option.value ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("");
}

function normalizePlanProductLink(link = {}) {
  const product = link.product ?? link;
  const productId = product.productId ?? product.id ?? "";
  if (!productId) return null;
  const nutrition = product.nutrition ?? {};
  const offer = product.offer ?? {};
  const offers = Array.isArray(product.offers) ? product.offers : [];
  const mealId = product.mealId && MEAL_TIME_BY_ID.has(product.mealId) ? product.mealId : "desayuno";
  const day = normalizePlanProductDay(product.day ?? product.planDay ?? product.menuDay ?? "all");
  const servingGrams = normalizePlanProductServing(product.servingGrams ?? product.serving_grams ?? product.portionGrams ?? 100);
  return {
    linkId: product.linkId || `${productId}_${day}_${mealId}`,
    productId,
    day,
    mealId,
    servingGrams,
    displayName: product.displayName ?? product.display_name ?? product.nombre ?? "Producto de la base",
    brand: product.brand ?? product.marca ?? "",
    categoryLevel1: product.categoryLevel1 ?? product.category_level1 ?? "",
    categoryLevel2: product.categoryLevel2 ?? product.category_level2 ?? "",
    foodGroup: product.foodGroup ?? product.food_group ?? "",
    packageSize: product.packageSize ?? product.package_size ?? "",
    unit: product.unit ?? product.unidad ?? "",
    dataStatus: product.dataStatus ?? product.data_status ?? "pending",
    isDemo: Boolean(Number(product.isDemo ?? product.is_demo ?? 0)),
    note: product.note ?? "",
    nutrition: {
      energyKcalPer100g: normalizePlanProductNumber(nutrition.energyKcalPer100g ?? product.energyKcalPer100g),
      proteinPer100g: normalizePlanProductNumber(nutrition.proteinPer100g ?? product.proteinPer100g),
      carbohydratesPer100g: normalizePlanProductNumber(nutrition.carbohydratesPer100g ?? product.carbohydratesPer100g),
      totalFatPer100g: normalizePlanProductNumber(nutrition.totalFatPer100g ?? product.totalFatPer100g),
      fiberPer100g: normalizePlanProductNumber(nutrition.fiberPer100g ?? product.fiberPer100g),
      sodiumPer100g: normalizePlanProductNumber(nutrition.sodiumPer100g ?? product.sodiumPer100g),
      nutritionSourceName: nutrition.nutritionSourceName ?? product.nutritionSourceName ?? "",
      nutritionSourceUrl: nutrition.nutritionSourceUrl ?? product.nutritionSourceUrl ?? "",
    },
    offer: {
      retailerName: offer.retailerName ?? product.retailerName ?? "",
      productUrl: offer.productUrl ?? product.productUrl ?? "",
      priceCents: normalizePlanProductNumber(offer.priceCents ?? product.priceCents ?? product.bestComparablePriceCents ?? product.bestExactPriceCents),
      currency: offer.currency ?? product.currency ?? "COP",
      availability: offer.availability ?? product.availability ?? "",
      exactMatchStatus: offer.exactMatchStatus ?? product.exactMatchStatus ?? "",
      sourceUrl: offer.sourceUrl ?? product.sourceUrl ?? "",
    },
    offers: offers.map((item) => ({
      retailerName: item.retailerName ?? "",
      productUrl: item.productUrl ?? "",
      priceCents: normalizePlanProductNumber(item.priceCents),
      currency: item.currency ?? "COP",
      availability: item.availability ?? "",
      exactMatchStatus: item.exactMatchStatus ?? "",
      sourceUrl: item.sourceUrl ?? "",
    })),
    source: product.source ?? product.sourceType ?? "Base en nube",
    addedAt: product.addedAt ?? new Date().toISOString(),
  };
}

function normalizePlanProductLinks(links = []) {
  return (Array.isArray(links) ? links : [])
    .map(normalizePlanProductLink)
    .filter(Boolean);
}

function currentPlanProductTarget() {
  return {
    day: normalizePlanProductDay(state.planProducts.day),
    mealId: MEAL_TIME_BY_ID.has(state.planProducts.mealId) ? state.planProducts.mealId : "desayuno",
    servingGrams: normalizePlanProductServing(state.planProducts.servingGrams),
  };
}

function planProductLinkFromListProduct(product, target) {
  return normalizePlanProductLink({
    ...product,
    productId: product.id,
    ...target,
    linkId: `${product.id}_${target.day}_${target.mealId}_${Date.now().toString(36)}`,
  });
}

function planProductLinkFromDetail(detail, target) {
  const product = detail?.product ?? {};
  const nutrition = detail?.nutrition ?? {};
  const offer = detail?.offers?.[0] ?? {};
  return normalizePlanProductLink({
    ...product,
    productId: product.id,
    ...target,
    linkId: `${product.id}_${target.day}_${target.mealId}_${Date.now().toString(36)}`,
    nutrition,
    offer,
    offers: detail?.offers ?? [],
  });
}

function planProductStatusLabel(status) {
  const labels = {
    pending: "Pendiente",
    incomplete: "Incompleto",
    verified: "Verificado",
    outdated: "Desactualizado",
  };
  return labels[status] || status || "Pendiente";
}

function formatPlanProductNutrition(nutrition = {}) {
  const energy = normalizePlanProductNumber(nutrition.energyKcalPer100g);
  const protein = normalizePlanProductNumber(nutrition.proteinPer100g);
  const carbs = normalizePlanProductNumber(nutrition.carbohydratesPer100g);
  const fat = normalizePlanProductNumber(nutrition.totalFatPer100g);
  const parts = [
    energy !== "" ? `${formatNumber(energy, " kcal/100g")}` : "",
    protein !== "" ? `${formatDecimal(protein, 1)} g proteina` : "",
    carbs !== "" ? `${formatDecimal(carbs, 1)} g CHO` : "",
    fat !== "" ? `${formatDecimal(fat, 1)} g grasa` : "",
  ].filter(Boolean);
  return parts.join(" · ") || "Nutrición pendiente de completar";
}

function calculatePlanProductServingNutrition(link) {
  const factor = normalizePlanProductServing(link.servingGrams) / 100;
  const nutrition = link.nutrition ?? {};
  const fields = {
    energyKcal: normalizePlanProductNumber(nutrition.energyKcalPer100g),
    protein: normalizePlanProductNumber(nutrition.proteinPer100g),
    carbohydrates: normalizePlanProductNumber(nutrition.carbohydratesPer100g),
    fat: normalizePlanProductNumber(nutrition.totalFatPer100g),
    fiber: normalizePlanProductNumber(nutrition.fiberPer100g),
    sodiumMg: normalizePlanProductNumber(nutrition.sodiumPer100g),
  };
  return Object.fromEntries(Object.entries(fields).map(([key, value]) => [
    key,
    value === "" ? "" : Math.round(value * factor * 10) / 10,
  ]));
}

function formatPlanProductServing(link) {
  const serving = normalizePlanProductServing(link.servingGrams);
  const nutrition = calculatePlanProductServingNutrition(link);
  const parts = [
    `${serving} g/ml`,
    nutrition.energyKcal !== "" ? `${formatNumber(Math.round(nutrition.energyKcal), " kcal")}` : "",
    nutrition.protein !== "" ? `${formatDecimal(nutrition.protein, 1)} g proteina` : "",
    nutrition.carbohydrates !== "" ? `${formatDecimal(nutrition.carbohydrates, 1)} g CHO` : "",
    nutrition.fat !== "" ? `${formatDecimal(nutrition.fat, 1)} g grasa` : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

function planProductProfessionalRows() {
  return state.planProducts.links.map((link) => ({
    productId: link.productId,
    product: link.displayName,
    brand: link.brand,
    target: planProductTargetLabel(link),
    day: link.day,
    mealId: link.mealId,
    meal: mealLabel(link.mealId),
    servingGrams: normalizePlanProductServing(link.servingGrams),
    nutritionPer100g: structuredClone(link.nutrition),
    nutritionPerServing: calculatePlanProductServingNutrition(link),
    offer: structuredClone(link.offer),
    exactOffers: (link.offers ?? []).filter((offer) => offer.exactMatchStatus === "exact"),
    packageSize: link.packageSize,
    dataStatus: link.dataStatus,
    note: link.note,
  }));
}

function formatPlanProductOffer(offer = {}) {
  const price = formatCurrencyCents(offer.priceCents, offer.currency || "COP");
  return [offer.retailerName, price, offer.exactMatchStatus ? `coincidencia ${offer.exactMatchStatus}` : ""]
    .filter(Boolean)
    .join(" · ") || "Oferta pendiente";
}

function planProductShoppingCategory(link) {
  const text = [
    link.categoryLevel1,
    link.categoryLevel2,
    link.foodGroup,
    link.displayName,
  ].join(" ").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (/(proteina|pollo|carne|atun|pescado|huevo|legumbre|lenteja|garbanzo)/.test(text)) return "Proteínas";
  if (/(arroz|avena|pan|pasta|arepa|cereal|granola|carbohidrato|tuberculo|papa|yuca)/.test(text)) return "Carbohidratos";
  if (/(fruta|verdura|vegetal|ensalada|hortaliza|banano|manzana|aguacate)/.test(text)) return "Frutas y verduras";
  if (/(aceite|nuez|mani|almendra|semilla|grasa)/.test(text)) return "Grasas saludables";
  if (/(lacteo|leche|yogur|yogurt|queso|kumis)/.test(text)) return "Lácteos";
  if (/(suplemento|whey|proteina en polvo|creatina|electrolito|barra)/.test(text)) return "Suplementos/productos";
  return "Otros";
}

function planProductShoppingItems() {
  return state.planProducts.links.map((link) => {
    const details = [
      planProductTargetLabel(link),
      formatPlanProductServing(link),
      link.packageSize || link.unit,
      link.note,
    ].filter(Boolean).join(" · ");
    const source = [
      "Producto de la base",
      planProductStatusLabel(link.dataStatus),
      link.offer?.retailerName,
    ].filter(Boolean).join(" · ");
    return {
      category: planProductShoppingCategory(link),
      name: link.brand ? `${link.displayName} (${link.brand})` : link.displayName,
      note: details,
      source,
    };
  });
}

function mergeShoppingListWithPlanProducts(baseGroups) {
  const groups = structuredClone(baseGroups);
  for (const item of planProductShoppingItems()) {
    let group = groups.find((entry) => entry.category === item.category);
    if (!group) {
      group = { category: item.category, items: [] };
      groups.push(group);
    }
    const exists = group.items.some((entry) => (
      entry.name === item.name && entry.source === item.source
    ));
    if (!exists) group.items.push({ name: item.name, note: item.note, source: item.source });
  }
  groups.forEach((group) => group.items.sort((a, b) => a.name.localeCompare(b.name, "es")));
  return groups.filter((group) => group.items.length > 0);
}

function bestExactOfferForLink(link) {
  const exactOffers = (link.offers ?? [])
    .filter((offer) => offer.exactMatchStatus === "exact" && offer.priceCents)
    .sort((a, b) => Number(a.priceCents) - Number(b.priceCents));
  if (exactOffers.length) return exactOffers[0];
  return link.offer?.exactMatchStatus === "exact" ? link.offer : null;
}

function sharedListItemFromPlanProduct(link) {
  const exactOffer = bestExactOfferForLink(link);
  const retailerName = exactOffer?.retailerName || link.offer?.retailerName || "Tienda por confirmar";
  return {
    category: planProductShoppingCategory(link),
    name: link.brand ? `${link.displayName} (${link.brand})` : link.displayName,
    note: link.note,
    target: planProductTargetLabel(link),
    serving: formatPlanProductServing(link),
    source: `Producto base · ${planProductStatusLabel(link.dataStatus)}`,
    retailerName,
    offer: exactOffer || link.offer || {},
    exactOffers: (link.offers ?? []).filter((offer) => offer.exactMatchStatus === "exact"),
    productUrl: exactOffer?.productUrl || link.offer?.productUrl || "",
  };
}

function sharedListItemFromShoppingItem(group, item) {
  return {
    category: group.category || "Otros",
    name: item.name,
    note: item.note,
    target: "",
    serving: "",
    source: item.source || "Lista general",
    retailerName: "Lista general",
    offer: {},
    exactOffers: [],
    productUrl: "",
  };
}

function groupSharedShoppingItems(items) {
  const map = new Map();
  for (const item of items) {
    const retailerName = item.retailerName || "Lista general";
    const category = item.category || "Otros";
    const key = `${retailerName}::${category}`;
    if (!map.has(key)) map.set(key, { retailerName, category, items: [] });
    map.get(key).items.push(item);
  }
  return [...map.values()]
    .map((group) => ({
      ...group,
      items: group.items.sort((a, b) => a.name.localeCompare(b.name, "es")),
    }))
    .sort((a, b) => `${a.retailerName} ${a.category}`.localeCompare(`${b.retailerName} ${b.category}`, "es"));
}

function buildSharedShoppingListPayload() {
  const exportPayload = buildExportPayload();
  const baseItems = exportPayload.shoppingList.flatMap((group) => (
    group.items
      .filter((item) => !String(item.source ?? "").startsWith("Producto base"))
      .map((item) => sharedListItemFromShoppingItem(group, item))
  ));
  const productItems = state.planProducts.links.map(sharedListItemFromPlanProduct);
  const dedupe = new Map();
  for (const item of [...baseItems, ...productItems]) {
    const key = [item.retailerName, item.category, item.name, item.target, item.serving].join("::");
    if (!dedupe.has(key)) dedupe.set(key, item);
  }
  return {
    title: state.deliverableOptions.title || "Lista de compras",
    groups: groupSharedShoppingItems([...dedupe.values()]),
  };
}

function catalogItemToPlanProduct(item = {}) {
  return {
    id: `catalog:${item.id}`,
    catalogItemId: item.id,
    displayName: item.displayName,
    brand: item.brand || "",
    categoryLevel1: item.categoryLevel1 || item.foodGroup || "Producto alimenticio",
    categoryLevel2: item.categoryLevel2 || "",
    foodGroup: item.foodGroup || "",
    packageSize: item.packageSize || "",
    unit: item.unit || "",
    dataStatus: "incomplete",
    source: "Catalogo multi-tienda",
    sourceKind: "catalog_item",
    retailerName: item.retailerName || "",
    productUrl: item.productUrl || "",
    priceCents: item.discountPriceCents || item.priceCents || "",
    currency: item.currency || "COP",
    availability: item.availability || "",
    exactMatchStatus: item.exactMatchStatus || "probable",
    bestComparablePriceCents: item.discountPriceCents || item.priceCents || "",
    bestRetailerName: item.retailerName || "",
    bestCity: item.city || "Nacional",
    imageUrl: item.imageUrl || "",
    nutrition: {
      nutritionSourceName: "Pendiente de etiqueta nutricional",
      nutritionSourceUrl: item.productUrl || "",
    },
  };
}

function mergePlanProductResults(products = [], catalogItems = []) {
  const merged = new Map();
  for (const product of products) {
    merged.set(`product:${product.id}`, { ...product, sourceKind: "product", source: "Productos profesionales" });
  }
  for (const item of catalogItems) {
    const mapped = catalogItemToPlanProduct(item);
    const key = [
      mapped.displayName,
      mapped.brand,
      mapped.packageSize,
      mapped.retailerName,
      mapped.priceCents,
    ].join("::").toLowerCase();
    if (!merged.has(key)) merged.set(key, mapped);
  }
  return [...merged.values()]
    .sort((a, b) => {
      const priceA = Number(a.bestComparablePriceCents || a.bestExactPriceCents || a.priceCents || Infinity);
      const priceB = Number(b.bestComparablePriceCents || b.bestExactPriceCents || b.priceCents || Infinity);
      if (priceA !== priceB) return priceA - priceB;
      return String(a.displayName || "").localeCompare(String(b.displayName || ""), "es");
    })
    .slice(0, 24);
}

async function searchPlanProducts() {
  state.planProducts.loading = true;
  planProductSetStatus({ message: "Buscando en productos profesionales y catalogos de tiendas..." });
  render();
  const params = new URLSearchParams();
  const catalogParams = new URLSearchParams();
  const query = String(state.planProducts.search ?? "").trim();
  if (query) params.set("q", query);
  if (query) catalogParams.set("q", query);
  const retailer = String(state.planProducts.retailer ?? "").trim();
  if (retailer) {
    params.set("retailer", retailer);
    catalogParams.set("retailer", retailer);
  }
  params.set("active", "active");
  params.set("limit", "24");
  params.set("sort", "price");
  catalogParams.set("foodOnly", "1");
  catalogParams.set("limit", "48");
  catalogParams.set("sort", "price");
  try {
    const liveParams = new URLSearchParams(catalogParams);
    liveParams.set("limit", "8");
    liveParams.set("totalLimit", "80");
    liveParams.set("maxSources", "12");
    const [productsResponse, catalogResponse, liveResponse] = await Promise.all([
      fetch(`/api/products?${params.toString()}`),
      fetch(`/api/catalog-items?${catalogParams.toString()}`),
      fetch(`/api/catalog-live-search?${liveParams.toString()}`),
    ]);
    const [productsPayload, catalogPayload, livePayload] = await Promise.all([
      productsResponse.json(),
      catalogResponse.json(),
      liveResponse.json(),
    ]);
    if (!productsResponse.ok || !productsPayload.ok) throw new Error(productsPayload.error || "No se pudieron buscar productos.");
    if (!catalogResponse.ok || !catalogPayload.ok) throw new Error(catalogPayload.error || "No se pudieron buscar catalogos.");
    if (!liveResponse.ok || !livePayload.ok) throw new Error(livePayload.error || "No se pudieron buscar tiendas en vivo.");
    state.planProducts.results = mergePlanProductResults(productsPayload.products ?? [], [...(catalogPayload.items ?? []), ...(livePayload.items ?? [])]);
    state.planProducts.loading = false;
    planProductSetStatus({ message: `${state.planProducts.results.length} resultado(s) multi-tienda disponibles para asociar al plan.` });
    render();
  } catch (error) {
    state.planProducts.loading = false;
    planProductSetStatus({ error: error?.message || "No se pudieron buscar productos en tiendas." });
    render();
  }
}

async function addPlanProductToPlan(productId) {
  const target = currentPlanProductTarget();
  const alreadyLinked = state.planProducts.links.some((link) => (
    link.productId === productId
    && link.mealId === target.mealId
    && String(link.day) === String(target.day)
  ));
  if (alreadyLinked) {
    planProductSetStatus({ message: "Ese producto ya está asociado a ese día y tiempo de comida." });
    render();
    return;
  }
  const fallback = state.planProducts.results.find((product) => product.id === productId)
    || state.productAdmin.products.find((product) => product.id === productId);
  if (fallback?.sourceKind === "catalog_item") {
    const link = planProductLinkFromListProduct(fallback, target);
    state.planProducts.links = [...state.planProducts.links, link];
    planProductSetStatus({ message: `${link.displayName} asociado desde catalogo de ${fallback.retailerName || "tienda"}.` });
    markPendingChanges();
    render();
    return;
  }
  state.planProducts.loading = true;
  planProductSetStatus({ message: "Asociando producto al plan..." });
  render();
  try {
    const response = await fetch(`/api/products/${encodeURIComponent(productId)}`);
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo leer el detalle del producto.");
    const link = planProductLinkFromDetail(payload, target);
    state.planProducts.links = [...state.planProducts.links, link];
    state.planProducts.loading = false;
    planProductSetStatus({ message: `${link.displayName} asociado a ${planProductTargetLabel(link)}.` });
    markPendingChanges();
    render();
  } catch (error) {
    if (!fallback) {
      state.planProducts.loading = false;
      planProductSetStatus({ error: error?.message || "No se pudo asociar el producto." });
      render();
      return;
    }
    const link = planProductLinkFromListProduct(fallback, target);
    state.planProducts.links = [...state.planProducts.links, link];
    state.planProducts.loading = false;
    planProductSetStatus({ message: `${link.displayName} asociado con datos resumidos.` });
    markPendingChanges();
    render();
  }
}

function removePlanProductFromPlan(linkId) {
  state.planProducts.links = state.planProducts.links.filter((link) => link.linkId !== linkId);
  planProductSetStatus({ message: "Producto retirado del plan." });
  markPendingChanges();
  render();
}

function updatePlanProductNote(linkId, value) {
  const link = state.planProducts.links.find((item) => item.linkId === linkId);
  if (!link) return;
  link.note = value;
  markPendingChanges();
}

function updatePlanProductServing(linkId, value) {
  const link = state.planProducts.links.find((item) => item.linkId === linkId);
  if (!link) return;
  link.servingGrams = normalizePlanProductServing(value);
  markPendingChanges();
}

function planProductsForMenuMeal(day, mealId) {
  return state.planProducts.links.filter((link) => (
    link.mealId === mealId
    && (link.day === "all" || Number(link.day) === Number(day))
  ));
}

function renderMealProductChips(day, mealId) {
  const links = planProductsForMenuMeal(day, mealId);
  if (!links.length) return "";
  return `
    <div class="meal-product-chips" aria-label="Productos asociados">
      ${links.map((link) => `
        <span title="${escapeHtml(formatPlanProductServing(link))}">
          ${escapeHtml(link.displayName)}
          <small>${escapeHtml(formatPlanProductServing(link))}</small>
        </span>
      `).join("")}
    </div>
  `;
}

function normalizeProductAdminDetail(detail) {
  const product = detail?.product ?? {};
  const nutrition = detail?.nutrition ?? {};
  const offer = detail?.offers?.[0] ?? {};
  return {
    id: product.id ?? "",
    displayName: product.displayName ?? product.display_name ?? "",
    brand: product.brand ?? "",
    barcodeEAN: product.barcodeEAN ?? product.barcode_ean ?? "",
    categoryLevel1: product.categoryLevel1 ?? product.category_level1 ?? "",
    categoryLevel2: product.categoryLevel2 ?? product.category_level2 ?? "",
    foodGroup: product.foodGroup ?? product.food_group ?? "",
    packageSize: product.packageSize ?? product.package_size ?? "",
    unit: product.unit ?? "g",
    dataStatus: product.dataStatus ?? product.data_status ?? "pending",
    isDemo: Boolean(Number(product.isDemo ?? product.is_demo ?? 0)),
    isActive: Boolean(Number(product.isActive ?? product.is_active ?? 1)),
    description: product.description ?? "",
    ingredients: product.ingredients ?? "",
    energyKcalPer100g: nutrition.energyKcalPer100g ?? "",
    proteinPer100g: nutrition.proteinPer100g ?? "",
    carbohydratesPer100g: nutrition.carbohydratesPer100g ?? "",
    totalFatPer100g: nutrition.totalFatPer100g ?? "",
    fiberPer100g: nutrition.fiberPer100g ?? "",
    sodiumPer100g: nutrition.sodiumPer100g ?? "",
    nutritionSourceName: nutrition.nutritionSourceName ?? "",
    nutritionSourceUrl: nutrition.nutritionSourceUrl ?? "",
    retailerName: offer.retailerName ?? "Carga manual / pendiente de tienda",
    productUrl: offer.productUrl ?? "",
    priceCents: offer.priceCents ?? "",
    currency: offer.currency ?? "COP",
    availability: offer.availability ?? "pending",
    exactMatchStatus: offer.exactMatchStatus ?? "manual_review",
    sourceUrl: offer.sourceUrl ?? "",
  };
}

function numberOrEmpty(value) {
  if (value === "" || value === null || value === undefined) return "";
  const number = Number(value);
  return Number.isFinite(number) ? number : "";
}

function productAdminPayloadFromForm() {
  const form = state.productAdmin.form;
  return {
    product: {
      id: form.id || undefined,
      displayName: form.displayName,
      brand: form.brand,
      barcodeEAN: form.barcodeEAN,
      categoryLevel1: form.categoryLevel1,
      categoryLevel2: form.categoryLevel2,
      foodGroup: form.foodGroup,
      packageSize: form.packageSize,
      unit: form.unit,
      dataStatus: form.dataStatus,
      isDemo: form.isDemo,
      isActive: form.isActive,
      description: form.description,
      ingredients: form.ingredients,
    },
    nutrition: {
      energyKcalPer100g: numberOrEmpty(form.energyKcalPer100g),
      proteinPer100g: numberOrEmpty(form.proteinPer100g),
      carbohydratesPer100g: numberOrEmpty(form.carbohydratesPer100g),
      totalFatPer100g: numberOrEmpty(form.totalFatPer100g),
      fiberPer100g: numberOrEmpty(form.fiberPer100g),
      sodiumPer100g: numberOrEmpty(form.sodiumPer100g),
      nutritionSourceName: form.nutritionSourceName,
      nutritionSourceUrl: form.nutritionSourceUrl,
    },
    offers: [{
      retailerName: form.retailerName,
      productUrl: form.productUrl,
      priceCents: numberOrEmpty(form.priceCents),
      currency: form.currency || "COP",
      availability: form.availability,
      exactMatchStatus: form.exactMatchStatus,
      sourceUrl: form.sourceUrl,
      isActive: true,
    }],
  };
}

async function loadProductAdminList({ selectId = state.productAdmin.selectedId } = {}) {
  state.productAdmin.loading = true;
  productAdminSetStatus();
  render();
  try {
    const response = await fetch(`/api/products?${productAdminApiQuery()}`);
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo cargar productos de la base.");
    state.productAdmin.products = payload.products ?? [];
    state.productAdmin.lastSyncAt = formatDateTime();
    state.productAdmin.loading = false;
    if (selectId) {
      const stillExists = state.productAdmin.products.some((product) => product.id === selectId);
      if (stillExists) {
        await loadProductAdminDetail(selectId, { skipListRefresh: true });
        return;
      }
    }
    productAdminSetStatus({ message: `Productos cargados: ${state.productAdmin.products.length}` });
    render();
  } catch (error) {
    state.productAdmin.loading = false;
    productAdminSetStatus({ error: error?.message || "No se pudo cargar productos de la base." });
    render();
  }
}

async function loadProductAdminDetail(id, { skipListRefresh = false } = {}) {
  if (!id) return;
  state.productAdmin.loading = true;
  state.productAdmin.selectedId = id;
  productAdminSetStatus();
  render();
  try {
    const response = await fetch(`/api/products/${encodeURIComponent(id)}`);
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo cargar el producto.");
    state.productAdmin.selected = payload;
    state.productAdmin.form = normalizeProductAdminDetail(payload);
    state.productAdmin.loading = false;
    state.productAdmin.lastSyncAt = formatDateTime();
    productAdminSetStatus({ message: `Producto activo: ${state.productAdmin.form.displayName}` });
    if (!skipListRefresh) {
      const found = state.productAdmin.products.some((product) => product.id === id);
      if (!found) await loadProductAdminList({ selectId: id });
      else render();
      return;
    }
    render();
  } catch (error) {
    state.productAdmin.loading = false;
    productAdminSetStatus({ error: error?.message || "No se pudo cargar el producto." });
    render();
  }
}

function resetProductAdminForm() {
  state.productAdmin.selectedId = "";
  state.productAdmin.selected = null;
  state.productAdmin.form = defaultProductAdminForm();
  productAdminSetStatus({ message: "Formulario limpio para nuevo producto." });
  render();
}

function updateProductAdminField(field, value, type = "text") {
  state.productAdmin.form[field] = type === "checkbox" ? Boolean(value) : value;
}

function updateProductAdminFilter(field, value) {
  state.productAdmin.filters[field] = value;
}

async function saveProductAdminProduct() {
  const form = state.productAdmin.form;
  if (!String(form.displayName ?? "").trim()) {
    productAdminSetStatus({ error: "Escribe el nombre del producto antes de guardar." });
    render();
    return;
  }
  if (!productAdminToken()) {
    productAdminSetStatus({ error: "Ingresa la clave de administración para guardar en la base." });
    render();
    return;
  }
  state.productAdmin.saving = true;
  productAdminSetStatus({ message: "Guardando producto en la base..." });
  render();
  try {
    const isUpdate = Boolean(state.productAdmin.selectedId || form.id);
    const targetId = state.productAdmin.selectedId || form.id;
    const response = await fetch(isUpdate ? `/api/products/${encodeURIComponent(targetId)}` : "/api/products", {
      method: isUpdate ? "PUT" : "POST",
      headers: productAdminHeaders(),
      body: JSON.stringify(productAdminPayloadFromForm()),
    });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo guardar el producto.");
    state.productAdmin.saving = false;
    const productId = payload.product?.id ?? targetId;
    productAdminSetStatus({ message: isUpdate ? "Producto actualizado con respaldo e historial." : "Producto creado con historial." });
    await loadProductAdminList({ selectId: productId });
  } catch (error) {
    state.productAdmin.saving = false;
    productAdminSetStatus({ error: error?.message || "No se pudo guardar el producto." });
    render();
  }
}

async function deactivateProductAdminProduct() {
  const id = state.productAdmin.selectedId || state.productAdmin.form.id;
  if (!id || !productAdminToken()) {
    productAdminSetStatus({ error: "Selecciona un producto y confirma la clave de administración." });
    render();
    return;
  }
  const confirmed = window.confirm("Desactivar este producto sin borrarlo fisicamente?");
  if (!confirmed) return;
  state.productAdmin.saving = true;
  productAdminSetStatus({ message: "Desactivando producto..." });
  render();
  try {
    const response = await fetch(`/api/products/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: productAdminHeaders({ json: false }),
    });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo desactivar el producto.");
    state.productAdmin.saving = false;
    productAdminSetStatus({ message: "Producto desactivado sin borrado fisico." });
    await loadProductAdminList({ selectId: id });
  } catch (error) {
    state.productAdmin.saving = false;
    productAdminSetStatus({ error: error?.message || "No se pudo desactivar el producto." });
    render();
  }
}

async function restoreProductAdminProduct() {
  state.productAdmin.form.isActive = true;
  await saveProductAdminProduct();
}

async function fetchProductAdminExport() {
  if (!productAdminToken()) throw new Error("Ingresa la clave de administración para exportar la base en nube.");
  const response = await fetch("/api/export/products?format=json", {
    headers: productAdminHeaders({ json: false }),
  });
  const payload = await response.json();
  if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo exportar productos.");
  return payload.products ?? [];
}

function productRowsToCsv(products) {
  const rows = [
    PRODUCT_ADMIN_EXPORT_COLUMNS,
    ...products.map((product) => PRODUCT_ADMIN_EXPORT_COLUMNS.map((column) => product[column] ?? "")),
  ];
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

async function exportProductAdminJson() {
  try {
    const products = await fetchProductAdminExport();
    const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), products }, null, 2)], { type: "application/json;charset=utf-8" });
    downloadBlob(blob, `productos_base_nube_${formatDateForFile()}.json`);
  } catch (error) {
    productAdminSetStatus({ error: error?.message || "No se pudo exportar JSON." });
    render();
  }
}

async function exportProductAdminCsv() {
  try {
    const products = await fetchProductAdminExport();
    const blob = new Blob([`\ufeff${productRowsToCsv(products)}`], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, `productos_base_nube_${formatDateForFile()}.csv`);
  } catch (error) {
    productAdminSetStatus({ error: error?.message || "No se pudo exportar CSV." });
    render();
  }
}

function xmlEscape(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function columnName(index) {
  let value = index + 1;
  let name = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
}

function crc32(bytes) {
  let crc = -1;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ -1) >>> 0;
}

function makeZip(files) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  const push16 = (view, pos, value) => view.setUint16(pos, value, true);
  const push32 = (view, pos, value) => view.setUint32(pos, value, true);
  for (const [name, content] of Object.entries(files)) {
    const nameBytes = encoder.encode(name);
    const dataBytes = encoder.encode(content);
    const crc = crc32(dataBytes);
    const local = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(local.buffer);
    push32(localView, 0, 0x04034b50);
    push16(localView, 4, 20);
    push16(localView, 6, 0);
    push16(localView, 8, 0);
    push16(localView, 10, 0);
    push16(localView, 12, 0);
    push32(localView, 14, crc);
    push32(localView, 18, dataBytes.length);
    push32(localView, 22, dataBytes.length);
    push16(localView, 26, nameBytes.length);
    push16(localView, 28, 0);
    local.set(nameBytes, 30);
    localParts.push(local, dataBytes);
    const central = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(central.buffer);
    push32(centralView, 0, 0x02014b50);
    push16(centralView, 4, 20);
    push16(centralView, 6, 20);
    push16(centralView, 8, 0);
    push16(centralView, 10, 0);
    push16(centralView, 12, 0);
    push16(centralView, 14, 0);
    push32(centralView, 16, crc);
    push32(centralView, 20, dataBytes.length);
    push32(centralView, 24, dataBytes.length);
    push16(centralView, 28, nameBytes.length);
    push16(centralView, 30, 0);
    push16(centralView, 32, 0);
    push16(centralView, 34, 0);
    push16(centralView, 36, 0);
    push32(centralView, 38, 0);
    push32(centralView, 42, offset);
    central.set(nameBytes, 46);
    centralParts.push(central);
    offset += local.length + dataBytes.length;
  }
  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  push32(endView, 0, 0x06054b50);
  push16(endView, 8, Object.keys(files).length);
  push16(endView, 10, Object.keys(files).length);
  push32(endView, 12, centralSize);
  push32(endView, 16, offset);
  return new Blob([...localParts, ...centralParts, end], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function productsToXlsxBlob(products) {
  const rows = [
    PRODUCT_ADMIN_EXPORT_COLUMNS,
    ...products.map((product) => PRODUCT_ADMIN_EXPORT_COLUMNS.map((column) => product[column] ?? "")),
  ];
  const sheetRows = rows.map((row, rowIndex) => `
    <row r="${rowIndex + 1}">
      ${row.map((cell, cellIndex) => `<c r="${columnName(cellIndex)}${rowIndex + 1}" t="inlineStr"><is><t>${xmlEscape(cell)}</t></is></c>`).join("")}
    </row>
  `).join("");
  const files = {
    "[Content_Types].xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`,
    "_rels/.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    "xl/workbook.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Productos" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    "xl/_rels/workbook.xml.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`,
    "xl/worksheets/sheet1.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRows}</sheetData></worksheet>`,
  };
  return makeZip(files);
}

async function inflateRawBytes(bytes) {
  if (!("DecompressionStream" in window)) {
    throw new Error("Este navegador no puede descomprimir XLSX comprimidos. Exporta el archivo como CSV o usa el XLSX generado por esta app.");
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

async function unzipXlsxEntries(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(bytes.buffer);
  const decoder = new TextDecoder();
  let endOffset = -1;
  for (let index = bytes.length - 22; index >= 0; index -= 1) {
    if (view.getUint32(index, true) === 0x06054b50) {
      endOffset = index;
      break;
    }
  }
  if (endOffset < 0) throw new Error("El archivo XLSX no tiene directorio ZIP valido.");
  const entryCount = view.getUint16(endOffset + 10, true);
  let centralOffset = view.getUint32(endOffset + 16, true);
  const entries = new Map();
  for (let entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
    if (view.getUint32(centralOffset, true) !== 0x02014b50) break;
    const method = view.getUint16(centralOffset + 10, true);
    const compressedSize = view.getUint32(centralOffset + 20, true);
    const nameLength = view.getUint16(centralOffset + 28, true);
    const extraLength = view.getUint16(centralOffset + 30, true);
    const commentLength = view.getUint16(centralOffset + 32, true);
    const localOffset = view.getUint32(centralOffset + 42, true);
    const name = decoder.decode(bytes.slice(centralOffset + 46, centralOffset + 46 + nameLength));
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;
    const compressed = bytes.slice(dataStart, dataStart + compressedSize);
    const data = method === 0 ? compressed : method === 8 ? await inflateRawBytes(compressed) : null;
    if (data) entries.set(name, decoder.decode(data));
    centralOffset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function parseXlsxRows(file) {
  const entries = await unzipXlsxEntries(file);
  const sheetXml = entries.get("xl/worksheets/sheet1.xml");
  if (!sheetXml) throw new Error("El XLSX no contiene la primera hoja esperada.");
  const parser = new DOMParser();
  const sheet = parser.parseFromString(sheetXml, "application/xml");
  const sharedStringsXml = entries.get("xl/sharedStrings.xml");
  const sharedStrings = sharedStringsXml
    ? [...parser.parseFromString(sharedStringsXml, "application/xml").querySelectorAll("si")]
      .map((item) => item.textContent ?? "")
    : [];
  const rows = [];
  sheet.querySelectorAll("sheetData row").forEach((rowNode) => {
    const row = [];
    rowNode.querySelectorAll("c").forEach((cellNode) => {
      const ref = cellNode.getAttribute("r") || "";
      const columnLetters = ref.replace(/[0-9]/g, "");
      const columnIndex = [...columnLetters].reduce((sum, letter) => (sum * 26) + letter.charCodeAt(0) - 64, 0) - 1;
      const type = cellNode.getAttribute("t");
      const rawValue = type === "inlineStr"
        ? cellNode.querySelector("is t")?.textContent ?? ""
        : cellNode.querySelector("v")?.textContent ?? "";
      const value = type === "s" ? sharedStrings[Number(rawValue)] ?? "" : rawValue;
      row[columnIndex] = value;
    });
    rows.push(row.map((value) => value ?? ""));
  });
  return rows.filter((row) => row.some((value) => String(value ?? "").trim() !== ""));
}

async function exportProductAdminXlsx() {
  try {
    const products = await fetchProductAdminExport();
    downloadBlob(productsToXlsxBlob(products), `productos_base_nube_${formatDateForFile()}.xlsx`);
  } catch (error) {
    productAdminSetStatus({ error: error?.message || "No se pudo exportar Excel." });
    render();
  }
}

function rowToProductImportPayload(row) {
  return {
    product: {
      id: row.id,
      displayName: row.displayName || row.display_name || row.nombre,
      brand: row.brand || row.marca,
      barcodeEAN: row.barcodeEAN || row.barcode_ean,
      categoryLevel1: row.categoryLevel1 || row.category_level1 || row.categoria,
      categoryLevel2: row.categoryLevel2 || row.category_level2 || row.subcategoria,
      foodGroup: row.foodGroup || row.food_group || row.grupo,
      packageSize: row.packageSize || row.package_size || row.presentacion,
      unit: row.unit || row.unidad,
      dataStatus: row.dataStatus || row.data_status || "pending",
      isDemo: row.isDemo || row.is_demo || false,
      isActive: row.isActive ?? row.is_active ?? true,
      description: row.description || row.descripcion,
      ingredients: row.ingredients || row.ingredientes,
    },
    nutrition: {
      energyKcalPer100g: row.energyKcalPer100g || row.energy_kcal_per_100g,
      proteinPer100g: row.proteinPer100g || row.protein_per_100g,
      carbohydratesPer100g: row.carbohydratesPer100g || row.carbohydrates_per_100g,
      totalFatPer100g: row.totalFatPer100g || row.total_fat_per_100g,
      fiberPer100g: row.fiberPer100g || row.fiber_per_100g,
      sodiumPer100g: row.sodiumPer100g || row.sodium_per_100g,
      nutritionSourceName: row.nutritionSourceName || row.nutrition_source_name,
      nutritionSourceUrl: row.nutritionSourceUrl || row.nutrition_source_url,
    },
    offers: [{
      retailerName: row.retailerName || row.retailer_name,
      productUrl: row.productUrl || row.product_url,
      priceCents: row.priceCents || row.price_cents,
      currency: row.currency || "COP",
      availability: row.availability || "pending",
      exactMatchStatus: row.exactMatchStatus || row.exact_match_status || "manual_review",
      sourceUrl: row.sourceUrl || row.source_url,
    }],
  };
}

async function importProductAdminFile(input) {
  const file = input.files?.[0];
  if (!file) return;
  if (!productAdminToken()) {
    productAdminSetStatus({ error: "Ingresa la clave de administración antes de importar." });
    input.value = "";
    render();
    return;
  }
  try {
    const lowerName = file.name.toLowerCase();
    let products;
    if (lowerName.endsWith(".xlsx")) {
      products = csvRowsToObjects(await parseXlsxRows(file)).rows.map(rowToProductImportPayload);
    } else if (lowerName.endsWith(".json")) {
      const text = await readFileAsText(file);
      const parsed = JSON.parse(text);
      products = parsed.products ?? parsed;
    } else {
      const text = await readFileAsText(file);
      products = csvRowsToObjects(parseCsv(text)).rows.map(rowToProductImportPayload);
    }
    const list = Array.isArray(products) ? products : [];
    if (!list.length) throw new Error("El archivo no contiene productos para importar.");
    const response = await fetch("/api/import/products", {
      method: "POST",
      headers: productAdminHeaders(),
      body: JSON.stringify({ products: list }),
    });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo importar productos.");
    productAdminSetStatus({ message: `Importación completada: ${payload.imported} productos procesados.` });
    await loadProductAdminList();
  } catch (error) {
    productAdminSetStatus({ error: error?.message || "No se pudo importar productos." });
    render();
  } finally {
    input.value = "";
  }
}

function normalizeShoppingText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function shoppingChecklistKey(category, name, source) {
  return `${normalizeShoppingText(category)}|${normalizeShoppingText(name)}|${normalizeShoppingText(source)}`;
}

function tableRows(name) {
  return state.data?.tables?.[name]?.rows ?? [];
}

function hydrateGenerated() {
  state.scheme = buildDailyScheme(state.patient, state.data);
  state.menu = generateMenu(state.patient, state.data);
  state.recommendations = generateRecommendations(state.patient, state.data);
}

function setPatientMealTimes(nextTimes) {
  const normalized = normalizeMealTimeSelection(nextTimes);
  state.patient.meal_times = normalized;
  markPendingChanges();
}

function togglePatientMealTime(mealTimeId, checked) {
  const activeTimes = new Set(getPatientMealTimes(state.patient));
  if (checked) {
    activeTimes.add(mealTimeId);
  } else {
    activeTimes.delete(mealTimeId);
    if (!activeTimes.size) {
      return false;
    }
  }
  setPatientMealTimes([...activeTimes]);
  return true;
}

function updateDeliverableOption(key, value) {
  if (!Object.prototype.hasOwnProperty.call(DEFAULT_DELIVERABLE_OPTIONS, key)) return;
  state.deliverableOptions = {
    ...state.deliverableOptions,
    [key]: typeof DEFAULT_DELIVERABLE_OPTIONS[key] === "boolean" ? Boolean(value) : String(value ?? ""),
  };
  markPendingChanges();
}

function updatePatient(field, value) {
  const numeric = [
    "edad",
    "peso_kg",
    "talla_cm",
    "estatura_sentado_cm",
    "envergadura_cm",
    "peso_ideal_kg",
    "peso_objetivo_kg",
    "porcentaje_grasa",
    "masa_muscular",
    "masa_libre_grasa_kg",
    "numero_tiempos_comida",
    "numero_valoracion",
    "pliegue_submandibular_mm",
    "pliegue_triceps_mm",
    "pliegue_biceps_mm",
    "pliegue_subescapular_mm",
    "pliegue_cresta_iliaca_mm",
    "pliegue_supraespinal_mm",
    "pliegue_abdominal_mm",
    "pliegue_muslo_mm",
    "pliegue_pantorrilla_mm",
    "perimetro_cefalico_cm",
    "perimetro_cuello_cm",
    "perimetro_brazo_relajado_cm",
    "perimetro_brazo_tenso_cm",
    "perimetro_antebrazo_cm",
    "perimetro_torax_cm",
    "perimetro_cintura_cm",
    "perimetro_abdomen_cm",
    "perimetro_cadera_cm",
    "perimetro_muslo_superior_cm",
    "perimetro_muslo_medio_cm",
    "perimetro_pantorrilla_cm",
    "diametro_biacromial_cm",
    "diametro_biliocrestal_cm",
    "diametro_humero_cm",
    "diametro_femur_cm",
    "consumo_calorias_actuales",
    "consumo_proteina_actual_g",
    "consumo_carbohidratos_actual_g",
    "consumo_grasas_actual_g",
    "naf_valor",
    "entrenamientos_semana",
    "duracion_entrenamiento_minutos",
    "kg_masa_adiposa_perder",
    "dias_restriccion",
    "kg_masa_muscular_ganar",
    "dias_adicion",
    "get_3",
    "proteina_gkg",
    "grasa_gkg",
    "carbohidrato_gkg",
    ...anthropometryGoalFieldNames.filter((fieldName) => fieldName !== "fuente_masa_libre_grasa"),
  ];
  const numericFields = new Set([
    ...numeric,
    ...anthropometrySectionOptions.flatMap((section) => section.fields
      .filter((item) => item.type === "number")
      .map((item) => item.name)),
  ]);
  state.patient[field] = numericFields.has(field) ? normalizeNumericInput(value) : value;
  if (field === "fecha_nacimiento" || field === "fecha_evaluacion") {
    syncCalculatedPatientAge();
  }
  if (field === "actividad" && !state.patient.naf_valor) {
    state.patient.naf_categoria = { bajo: "ligera", medio: "moderada", alto: "fuerte" }[value] ?? state.patient.naf_categoria;
  }
  if (field === "nombre") {
    syncDraftFromPatient(state.patient);
  }
  syncActiveAssessmentSnapshot();
  markPendingChanges();
}

function field(label, fieldName, type = "text", options = null) {
  const value = state.patient[fieldName] ?? "";
  const numericAttributes = type === "number"
    ? ` inputmode="${fieldName === "numero_tiempos_comida" ? "numeric" : "decimal"}" step="${fieldName === "numero_tiempos_comida" ? "1" : "any"}"`
    : "";
  if (options) {
    return `
      <div class="field">
        <label for="${fieldName}">${label}</label>
        <select id="${fieldName}" data-patient-field="${fieldName}">
          ${options.map((option) => `<option value="${escapeHtml(option.value)}" ${option.value === value ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
        </select>
      </div>
    `;
  }
  return `
      <div class="field">
        <label for="${fieldName}">${label}</label>
        ${type === "textarea"
      ? `<textarea id="${fieldName}" rows="3" data-patient-field="${fieldName}">${escapeHtml(value)}</textarea>`
      : `<input id="${fieldName}" type="${type}" value="${escapeHtml(value)}" data-patient-field="${fieldName}"${numericAttributes} />`}
      </div>
    `;
}

function calculatedField(label, fieldName, calculatedValue, detail = "") {
  const manualValue = state.patient[fieldName];
  const value = manualValue !== "" && manualValue !== undefined && manualValue !== null
    ? manualValue
    : (Number(calculatedValue) > 0 ? formatDecimal(calculatedValue, 2) : "");
  return `
    <div class="field field--calculated">
      <label for="${fieldName}">${label}</label>
    <input id="${fieldName}" type="number" inputmode="decimal" step="any" value="${escapeHtml(value)}" data-patient-field="${fieldName}" />
      ${detail ? `<small>${escapeHtml(detail)}</small>` : ""}
    </div>
  `;
}

function renderMealTimeControls() {
  const activeTimes = new Set(getPatientMealTimes(state.patient));
  const manualCount = Math.round(Number(state.patient.numero_tiempos_comida) || 0);
  const mismatch = manualCount > 0 && manualCount !== activeTimes.size;
  return `
    <div class="meal-time-controls">
      <label class="meal-time-title">Tiempos de comida del plan</label>
      <p class="meal-time-description">Selecciona los tiempos activos. El número manual de tiempos se define en Cálculos y se conserva por separado.</p>
      <div class="meal-time-grid">
        ${MEAL_TIME_CATALOG.map((meal) => `
          <label class="meal-time-option">
            <input
              type="checkbox"
              data-meal-time="${escapeHtml(meal.id)}"
              ${activeTimes.has(meal.id) ? "checked" : ""}
            />
            <span>${escapeHtml(meal.label)}</span>
          </label>
        `).join("")}
      </div>
      <small class="meal-time-count">Tiempos activos: ${activeTimes.size}${manualCount ? ` · Número manual: ${manualCount}` : ""}</small>
      ${mismatch ? `<div class="inline-warning">El número manual (${manualCount}) no coincide con los tiempos activos (${activeTimes.size}). Puedes ajustar uno de los dos antes de entregar.</div>` : ""}
    </div>
  `;
}

function renderAnthropometrySectionControls() {
  const activeSections = new Set(getActiveAnthropometrySections(state.patient));
  return `
    <div class="anthropometry-section-controls">
      <div class="anthropometry-section-controls__copy">
        <strong>Secciones de la valoracion</strong>
        <small>Activa solo las mediciones tomadas. Los calculos usan unicamente las secciones activas.</small>
      </div>
      <div class="anthropometry-section-toggle-grid">
        ${anthropometrySectionOptions.map((section) => `
          <label class="anthropometry-section-toggle ${activeSections.has(section.id) ? "is-active" : ""}">
            <input
              type="checkbox"
              data-anthro-section="${escapeHtml(section.id)}"
              ${activeSections.has(section.id) ? "checked" : ""}
            />
            <span>${escapeHtml(section.label)}</span>
          </label>
        `).join("")}
      </div>
    </div>
  `;
}

function renderCalculatedBasicValue(label, value, unit = "", detail = "") {
  const hasValue = Number(value) > 0;
  return `
    <div class="calculated-basic-card">
      <span>${escapeHtml(label)}</span>
      <strong>${hasValue ? `${formatDecimal(value, 1)}${unit}` : "Pendiente"}</strong>
      ${detail ? `<small>${escapeHtml(detail)}</small>` : ""}
    </div>
  `;
}

function renderBasicMeasuresSection(section, anthropometry) {
  const derived = anthropometry.derivedBasics ?? {};
  const lean = derived.leanMass ?? {};
  return `
    <article class="anthropometry-section-card anthropometry-section-card--basic" data-anthro-section-card="${escapeHtml(section.id)}">
      <div class="anthropometry-section-card__heading">
        <h4>${escapeHtml(section.label)}</h4>
        <p>${escapeHtml(section.description)}</p>
      </div>
      <div class="form-grid">
        ${section.fields.map((item) => field(item.label, item.name, item.type || "text", item.options || null)).join("")}
      </div>
      <div class="calculated-basic-grid" aria-label="Resultados automaticos de medidas basicas">
        ${renderCalculatedBasicValue("Peso ideal calculado", derived.idealWeightKg, " kg", derived.idealWeightSource || "Se calcula desde metas antropometricas o talla/genero.")}
        ${renderCalculatedBasicValue("Peso objetivo editable", state.patient.peso_objetivo_kg, " kg", "Este campo queda editable para el criterio profesional.")}
        ${renderCalculatedBasicValue("Porcentaje de grasa visible", derived.fatPercent, "%", derived.fatPercentSource ? `Fuente: ${derived.fatPercentSource}` : "Usa Yuhasz o Durnin + Siri, no cinco componentes.")}
        ${renderCalculatedBasicValue("Masa libre de grasa", lean.value, " kg", lean.label || "Selecciona la fuente de masa libre de grasa.")}
      </div>
    </article>
  `;
}

function renderAnthropometryFieldSections() {
  const activeSections = new Set(getActiveAnthropometrySections(state.patient));
  const sections = anthropometrySectionOptions.filter((section) => activeSections.has(section.id));
  const anthropometry = calculateAnthropometry(state.patient);
  if (!sections.length) {
    return `
      <div class="anthropometry-empty-state">
        Selecciona al menos una seccion para capturar datos antropometricos.
      </div>
    `;
  }
  return `
    <div class="anthropometry-section-stack">
      ${sections.map((section) => `
        ${section.id === "basicas" ? renderBasicMeasuresSection(section, anthropometry) : `<article class="anthropometry-section-card" data-anthro-section-card="${escapeHtml(section.id)}">
          <div class="anthropometry-section-card__heading">
            <h4>${escapeHtml(section.label)}</h4>
            <p>${escapeHtml(section.description)}</p>
          </div>
          <div class="form-grid">
            ${section.fields.map((item) => field(item.label, item.name, item.type || "text", item.options || null)).join("")}
          </div>
        </article>`}
      `).join("")}
    </div>
  `;
}

function renderDashboard(energy, macros, warnings) {
  return `
    <section class="section dashboard-section" id="inicio">
      <h3>Dashboard</h3>
      <div class="dashboard-grid">
        <div class="metric"><label>TMB Harris-Benedict</label><strong>${formatNumber(energy.harrisBenedict.value, " kcal")}</strong></div>
        <div class="metric"><label>GET1 con NAF</label><strong>${formatNumber(energy.total, " kcal")}</strong></div>
        <div class="metric"><label>GET3 / meta final</label><strong>${formatNumber(macros.calories, " kcal")}</strong></div>
        <div class="metric"><label>Advertencias</label><strong>${warnings.length}</strong></div>
      </div>
    </section>
  `;
}

function renderCalculatedAgeSummary() {
  const age = resolvePatientAge(state.patient, getAgeReferenceDate());
  return `
    <div class="calculated-age-card">
      <span>Edad calculada</span>
      <strong data-calculated-age>${escapeHtml(formatAgeLabel(age))}</strong>
      <small>Se actualiza con la fecha de nacimiento y la fecha actual del plan.</small>
    </div>
  `;
}

function renderAnthropometryMetric(label, value, detail = "") {
  const displayValue = value || value === 0 ? value : "Pendiente";
  return `
    <div class="anthro-metric">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(displayValue)}</strong>
      ${detail ? `<small>${escapeHtml(detail)}</small>` : ""}
    </div>
  `;
}

function renderSomatotypeFigure(type, x, y, scale = 1) {
  const figures = {
    mesomorph: `
      <circle class="somatochart-figure__body" cx="0" cy="-23" r="5.8"></circle>
      <path class="somatochart-figure__body" d="M -16 -13 Q 0 -20 16 -13 L 11 8 Q 7 13 0 13 Q -7 13 -11 8 Z"></path>
      <path class="somatochart-figure__limb" d="M -13 -10 L -22 3 M 13 -10 L 22 3 M -4 11 L -5 28 M 4 11 L 5 28"></path>
    `,
    endomorph: `
      <circle class="somatochart-figure__body" cx="0" cy="-23" r="5.8"></circle>
      <path class="somatochart-figure__body" d="M -12 -13 Q 0 -19 12 -13 Q 19 -3 16 11 Q 11 19 0 19 Q -11 19 -16 11 Q -19 -3 -12 -13 Z"></path>
      <path class="somatochart-figure__limb" d="M -14 -9 L -21 5 M 14 -9 L 21 5 M -6 16 L -7 28 M 6 16 L 7 28"></path>
    `,
    ectomorph: `
      <circle class="somatochart-figure__body" cx="0" cy="-23" r="5.4"></circle>
      <path class="somatochart-figure__body" d="M -7 -14 Q 0 -18 7 -14 L 6 12 Q 0 15 -6 12 Z"></path>
      <path class="somatochart-figure__limb" d="M -6 -10 L -11 8 M 6 -10 L 11 8 M -3 11 L -4 29 M 3 11 L 4 29"></path>
    `,
  };
  return `
    <g class="somatochart-silhouette somatochart-silhouette--${type}" transform="translate(${x} ${y}) scale(${scale})" aria-hidden="true">
      <circle class="somatochart-figure__halo" cx="0" cy="2" r="34"></circle>
      ${figures[type]}
    </g>
  `;
}

function renderSomatochart(somatotype) {
  if (!somatotype?.complete) {
    return `
      <div class="somatochart somatochart--empty">
        <strong>Somatocarta</strong>
        <span>Completa pliegues, diametros y perimetros para ubicar el punto X/Y.</span>
      </div>
    `;
  }
  const xMin = -8;
  const xMax = 8;
  const yMin = -10;
  const yMax = 16;
  const plot = { left: 80, top: 40, width: 520, height: 480 };
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const sx = (value) => plot.left + ((value - xMin) / (xMax - xMin)) * plot.width;
  const sy = (value) => plot.top + ((yMax - value) / (yMax - yMin)) * plot.height;
  const x = clamp(Number(somatotype.x), xMin, xMax);
  const y = clamp(Number(somatotype.y), yMin, yMax);
  const px = sx(x);
  const py = sy(y);
  const centerX = sx(0);
  const centerY = sy(0);
  const apexX = sx(0);
  const apexY = sy(12.1);
  const leftX = sx(-6);
  const leftY = sy(-6);
  const rightX = sx(6);
  const rightY = sy(-6);
  const outline = [
    `M ${apexX} ${apexY}`,
    `C ${sx(-2.9)} ${sy(10.4)}, ${sx(-6)} ${sy(4.8)}, ${sx(-6)} ${sy(-6)}`,
    `C ${sx(-3.5)} ${sy(-10)}, ${sx(3.5)} ${sy(-10)}, ${sx(6)} ${sy(-6)}`,
    `C ${sx(6)} ${sy(4.8)}, ${sx(2.9)} ${sy(10.4)}, ${apexX} ${apexY}`,
    "Z",
  ].join(" ");
  const formula = (somatotype.formula || "X = ectomorfia - endomorfia; Y = 2 × mesomorfia - (ectomorfia + endomorfia)")
    .replaceAll("*", "×");
  return `
    <div class="somatochart">
      <div class="somatochart-heading">
        <div>
          <strong>Somatocarta</strong>
          <small>Somatotipo Heath-Carter</small>
        </div>
        <span class="somatochart-coordinate"><small>Coordenadas</small><strong>X ${formatDecimal(somatotype.x, 1)} · Y ${formatDecimal(somatotype.y, 1)}</strong></span>
      </div>
      <div class="somatochart-stage">
      <svg width="680" height="560" viewBox="0 0 680 560" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Somatocarta: endomorfia ${formatDecimal(somatotype.endomorphy, 1)}, mesomorfia ${formatDecimal(somatotype.mesomorphy, 1)}, ectomorfia ${formatDecimal(somatotype.ectomorphy, 1)}">
        <title>Somatocarta con punto calculado</title>
        <desc>Relación entre endomorfia, mesomorfia y ectomorfia.</desc>
        <path class="somatochart-outline" d="${outline}"></path>
        <line class="somatochart-axis" x1="${centerX}" y1="${centerY}" x2="${apexX}" y2="${apexY}"></line>
        <line class="somatochart-axis" x1="${centerX}" y1="${centerY}" x2="${leftX}" y2="${leftY}"></line>
        <line class="somatochart-axis" x1="${centerX}" y1="${centerY}" x2="${rightX}" y2="${rightY}"></line>
        <line class="somatochart-guide" x1="${centerX}" y1="${centerY}" x2="${sx(-7.2)}" y2="${sy(5.5)}"></line>
        <line class="somatochart-guide" x1="${centerX}" y1="${centerY}" x2="${sx(7.2)}" y2="${sy(5.5)}"></line>
        <line class="somatochart-guide" x1="${centerX}" y1="${centerY}" x2="${centerX}" y2="${sy(-9.3)}"></line>
        ${renderSomatotypeFigure("mesomorph", apexX, 55, 0.9)}
        ${renderSomatotypeFigure("endomorph", 70, 425, 0.82)}
        ${renderSomatotypeFigure("ectomorph", 610, 425, 0.82)}
        <text class="somatochart-domain somatochart-domain--top" x="${apexX}" y="${apexY - 5}">MESOMORFIA</text>
        <text class="somatochart-domain somatochart-domain--left" x="130" y="478">ENDOMORFIA</text>
        <text class="somatochart-domain somatochart-domain--right" x="550" y="478">ECTOMORFIA</text>
        <path class="somatochart-point" d="M ${px} ${py - 8} L ${px + 8} ${py} L ${px} ${py + 8} L ${px - 8} ${py} Z"></path>
      </svg>
      </div>
      <div class="somatochart-data">
        <div class="somatochart-values" aria-label="Componentes del somatotipo">
          <article class="somatochart-component somatochart-component--endo"><span>Endomorfia</span><strong>${formatDecimal(somatotype.endomorphy, 1)}</strong><small>Adiposidad relativa</small></article>
          <article class="somatochart-component somatochart-component--meso"><span>Mesomorfia</span><strong>${formatDecimal(somatotype.mesomorphy, 1)}</strong><small>Desarrollo musculoesquelético</small></article>
          <article class="somatochart-component somatochart-component--ecto"><span>Ectomorfia</span><strong>${formatDecimal(somatotype.ectomorphy, 1)}</strong><small>Linealidad corporal</small></article>
        </div>
        <div class="somatochart-technical-grid" aria-label="Datos técnicos de la somatocarta">
          <div><span>Coordenada X</span><strong>${formatDecimal(somatotype.x, 1)}</strong></div>
          <div><span>Coordenada Y</span><strong>${formatDecimal(somatotype.y, 1)}</strong></div>
          <div><span>Σ3 pliegues</span><strong>${formatDecimal(somatotype.sum3, 1)} mm</strong></div>
          <div><span>Σ corregida</span><strong>${formatDecimal(somatotype.correctedSum, 1)} mm</strong></div>
          <div><span>Índice ponderal</span><strong>${formatDecimal(somatotype.ponderalIndex, 2)}</strong></div>
        </div>
        <div class="somatochart-classification">
          <div><span>Clasificación</span><strong>${escapeHtml(somatotype.classification || "Por revisar")}</strong></div>
          <small>${escapeHtml(formula)}</small>
        </div>
      </div>
    </div>
  `;
}

function getAnthropometryFieldLabel(fieldName) {
  for (const section of anthropometrySectionOptions) {
    const fieldConfig = section.fields.find((item) => item.name === fieldName);
    if (fieldConfig) return fieldConfig.label;
  }
  return fieldName.replace(/_/g, " ");
}

function renderSelectedAnthropometryCards(anthropometry) {
  const primary = anthropometry.primaryComposition;
  if (!primary) {
    return `
      <div class="anthro-metric-grid anthro-metric-grid--selected">
        ${renderAnthropometryMetric("Metodo seleccionado", "Pendiente", "Selecciona un metodo y registra las medidas necesarias.")}
      </div>
    `;
  }
  if (!primary.complete) {
    const missingFields = primary.missing || [];
    const missing = missingFields
      .slice(0, 8)
      .map((fieldName) => getAnthropometryFieldLabel(fieldName))
      .join(", ");
    return `
      <div class="anthro-metric-grid anthro-metric-grid--selected">
        ${renderAnthropometryMetric(primary.label, "Pendiente", missing ? `Falta: ${missing}${missingFields.length > 8 ? "..." : ""}` : "Faltan datos para completar este metodo.")}
      </div>
    `;
  }

  if (primary.method === "cinco_componentes") {
    return `
      <div class="anthro-metric-grid anthro-metric-grid--selected">
        ${renderAnthropometryMetric("Masa adiposa", `${formatDecimal(primary.adiposePercent, 1)}%`, `${formatDecimal(primary.adiposeKg, 1)} kg`)}
        ${renderAnthropometryMetric("Masa muscular", `${formatDecimal(primary.musclePercent, 1)}%`, `${formatDecimal(primary.muscleKg, 1)} kg`)}
        ${renderAnthropometryMetric("Masa ósea", `${formatDecimal(primary.bonePercent, 1)}%`, `${formatDecimal(primary.boneKg, 1)} kg`)}
        ${renderAnthropometryMetric("Masa residual", `${formatDecimal(primary.residualPercent, 1)}%`, `${formatDecimal(primary.residualKg, 1)} kg`)}
        ${renderAnthropometryMetric("Masa piel", `${formatDecimal(primary.skinPercent, 1)}%`, `${formatDecimal(primary.skinKg, 1)} kg`)}
        ${renderAnthropometryMetric("Indices", `IMO ${formatDecimal(primary.muscleBoneIndex, 2)}`, `IAM ${formatDecimal(primary.adiposeMuscleIndex, 2)} · total ${formatDecimal(primary.totalKg, 1)} kg`)}
      </div>
    `;
  }

  if (primary.method === "durnin_siri") {
    return `
      <div class="anthro-metric-grid anthro-metric-grid--selected">
        ${renderAnthropometryMetric("Durnin + Siri", `${formatDecimal(primary.fatPercent, 1)}% grasa`, `Σ4 pliegues ${formatDecimal(primary.sum4, 1)} mm`)}
        ${renderAnthropometryMetric("Densidad corporal", formatDecimal(primary.density, 4), primary.formula)}
        ${renderAnthropometryMetric("Masas", `${formatDecimal(primary.fatKg, 1)} kg grasa`, `${formatDecimal(primary.leanKg, 1)} kg masa libre`)}
      </div>
    `;
  }

  if (primary.method === "rfm") {
    return `
      <div class="anthro-metric-grid anthro-metric-grid--selected">
        ${renderAnthropometryMetric("Masa grasa relativa", `${formatDecimal(primary.fatPercent, 1)}% grasa`, anthropometry.waistToHeight ? `Cintura/talla ${formatDecimal(anthropometry.waistToHeight, 2)}` : "Requiere cintura y talla.")}
        ${renderAnthropometryMetric("Masas", `${formatDecimal(primary.fatKg, 1)} kg grasa`, `${formatDecimal(primary.leanKg, 1)} kg masa libre`)}
      </div>
    `;
  }

  if (primary.method === "sumatoria_pliegues") {
    return `
      <div class="anthro-metric-grid anthro-metric-grid--selected">
        ${renderAnthropometryMetric("Sumatoria total", `${formatDecimal(primary.totalSkinfolds, 1)} mm`, `${primary.measuredSkinfoldCount} pliegues medidos`)}
        ${renderAnthropometryMetric("Suma 6 Yuhasz", `${formatDecimal(primary.sum6Yuhasz, 1)} mm`, "Triceps, subescapular, supraespinal, abdominal, muslo y pantorrilla")}
        ${renderAnthropometryMetric("Suma 4 Durnin", `${formatDecimal(primary.sum4Durnin, 1)} mm`, "Triceps, biceps, subescapular y supraespinal")}
        ${renderAnthropometryMetric("Suma 3 somatotipo", `${formatDecimal(primary.sum3Somatotype, 1)} mm`, "Triceps, subescapular y supraespinal")}
      </div>
    `;
  }

  return `
    <div class="anthro-metric-grid anthro-metric-grid--selected">
      ${renderAnthropometryMetric("Yuhasz", `${formatDecimal(primary.fatPercent, 1)}% grasa`, `Σ6 pliegues ${formatDecimal(primary.sum6, 1)} mm`)}
      ${renderAnthropometryMetric("Masas", `${formatDecimal(primary.fatKg, 1)} kg grasa`, `${formatDecimal(primary.leanKg, 1)} kg masa libre`)}
    </div>
  `;
}

function anthropometryGoalFieldsForMethod(method) {
  if (method === "durnin_siri") {
    return [
      ["Sumatoria esperada 4 pliegues", "meta_durnin_suma_4_pliegues_mm"],
      ["Cambio muscular esperado kg", "meta_durnin_cambio_muscular_kg"],
    ];
  }
  if (method === "rfm") {
    return [
      ["Cintura ideal cm", "meta_rfm_cintura_ideal_cm"],
    ];
  }
  if (method === "cinco_componentes") {
    return [
      ["Suma de pliegues ideal teorica mm", "meta_5c_suma_pliegues_ideal_mm"],
      ["Objetivo IMO", "meta_5c_objetivo_imo"],
    ];
  }
  return [
    ["Suma esperada 6 pliegues Yuhasz", "meta_yuhasz_suma_6_pliegues_mm"],
    ["Cambio muscular esperado kg", "meta_yuhasz_cambio_muscular_kg"],
  ];
}

function renderAnthropometryTargets(anthropometry) {
  const method = state.patient.metodo_antropometria || "yuhasz";
  const target = anthropometry.targets?.selected ?? {};
  const fields = anthropometryGoalFieldsForMethod(method);
  return `
    <div class="anthropometry-targets">
      <div class="anthropometry-targets__heading">
        <div>
          <span>Metas antropometricas</span>
          <h4>${escapeHtml(target.label || "Metas del metodo seleccionado")}</h4>
        </div>
        <small>${target.complete ? "Calculadas con las formulas del Excel actualizado." : "Completa los campos azules/equivalentes para calcular la meta."}</small>
      </div>
      <div class="form-grid">
        ${fields.map(([label, name]) => field(label, name, "number")).join("")}
      </div>
      <div class="anthro-metric-grid anthropometry-targets__metrics">
        ${renderAnthropometryMetric("Grasa a perder", target.kgAdiposeToLose ? `${formatDecimal(target.kgAdiposeToLose, 1)} kg` : "", target.formula || "Se enviara al cuadro de restriccion energetica.")}
        ${renderAnthropometryMetric("Masa muscular a ganar", target.kgMuscleToGain ? `${formatDecimal(target.kgMuscleToGain, 1)} kg` : "", "Se enviara al cuadro de adicion energetica.")}
        ${renderAnthropometryMetric("Peso ideal/meta", target.competitionWeightKg || target.desiredWeightKg ? `${formatDecimal(target.competitionWeightKg || target.desiredWeightKg, 1)} kg` : "", "Este valor alimenta el peso ideal calculado.")}
      </div>
    </div>
  `;
}

function renderAnthropometryResults() {
  const anthropometry = calculateAnthropometry(state.patient);
  const primary = anthropometry.primaryComposition;
  const primaryLabel = primary
    ? `${primary.label}${primary.complete
      ? primary.fatPercent
        ? `: ${formatDecimal(primary.fatPercent, 1)}% grasa`
        : primary.summaryValue
          ? `: ${primary.summaryValue}`
          : ": completo"
      : ": pendiente"}`
    : "Pendiente";
  const primaryDetail = primary?.leanKg
    ? `Masa grasa ${formatDecimal(primary.fatKg, 1)} kg · masa libre ${formatDecimal(primary.leanKg, 1)} kg`
    : primary?.complete && primary.detail
      ? primary.detail
      : "Completa los datos del metodo seleccionado.";
  return `
    <div class="anthropometry-results">
      <div class="anthropometry-results-heading">
        <div>
          <span>Resultados calculados</span>
          <h4>Composición corporal y somatotipo</h4>
        </div>
        <small>Fuente: fórmulas del formato Excel actualizado de antropometría.</small>
      </div>
      <div class="anthro-metric-grid">
        ${renderAnthropometryMetric("IMC", anthropometry.bmi ? `${formatDecimal(anthropometry.bmi, 1)} kg/m²` : "", anthropometry.bmiClassification)}
        ${renderAnthropometryMetric("Método principal", primaryLabel, primaryDetail)}
        ${renderAnthropometryMetric("Somatotipo", anthropometry.somatotype.complete ? `${formatDecimal(anthropometry.somatotype.endomorphy, 1)}-${formatDecimal(anthropometry.somatotype.mesomorphy, 1)}-${formatDecimal(anthropometry.somatotype.ectomorphy, 1)}` : "", anthropometry.somatotype.classification || "Requiere diámetros, perímetros y pliegues.")}
      </div>
      ${renderSelectedAnthropometryCards(anthropometry)}
      ${renderAnthropometryTargets(anthropometry)}
      ${renderSomatochart(anthropometry.somatotype)}
    </div>
  `;
}

function refreshAnthropometryResultsDisplay() {
  const currentResults = app?.querySelector("#antropometricos .anthropometry-results");
  if (!currentResults) return;
  currentResults.outerHTML = renderAnthropometryResults();
}

function renderFileControls() {
  const currentFile = getCurrentFile();
  const currentPatientFiles = getCurrentPatientFiles();
  const otherPatientFiles = state.patientFiles.filter((file) => !isFileForPatient(file, state.patient));
  const currentPatientLabel = getVisiblePatientName(state.patient) || "Paciente actual";
  ensurePatientCloudWorkspace();
  const cloudEnabled = hasPatientCloudAccess();
  const cloudBadge = state.patientCloud.syncing
    ? "Guardando en nube"
    : state.patientCloud.status === "available"
      ? (currentFile?.cloudSyncedAt || state.patientCloud.lastSyncAt ? "Nube sincronizada" : "Nube conectada")
      : state.patientCloud.status === "checking"
        ? "Comprobando nube"
        : "Nube pendiente";
  const selectedFileType = FILE_TYPE_OPTIONS.includes(state.newFileDraft.type)
    ? state.newFileDraft.type
    : FILE_TYPE_CUSTOM_VALUE;
  const customFileTypeValue = FILE_TYPE_OPTIONS.includes(state.newFileDraft.type) || state.newFileDraft.type === FILE_TYPE_CUSTOM_VALUE
    ? ""
    : (state.newFileDraft.type || "");
  const lastSave = state.lastAutoSaveAt
    ? `Último guardado: ${formatDateTime(new Date(state.lastAutoSaveAt))}`
    : "Sin guardado automático registrado";
  const autoSaveStatus = buildAutoSaveStatusText(currentFile);
  const hasAnyFile = currentPatientFiles.length > 0 || otherPatientFiles.length > 0;
  const planStartDate = state.currentPlanStartDate || currentFile?.planStartDate || formatDateForFile();
  const fileOptions = hasAnyFile
    ? `<select data-load-file>
        <option value="">Seleccionar expediente...</option>
        <optgroup label="${escapeHtml(`Archivos de ${currentPatientLabel}`)}">
          ${currentPatientFiles.map((file) => `<option value="${file.id}" ${file.id === state.currentFileId ? "selected" : ""}>${buildFileOptionLabel(file)}</option>`).join("")}
        </optgroup>
        ${otherPatientFiles.length ? `<optgroup label="Otros pacientes">${otherPatientFiles.map((file) => `<option value="${file.id}">${buildFileOptionLabel(file)}</option>`).join("")}</optgroup>` : ""}
      </select>`
    : `<select data-load-file>
        <option value="">Aun no hay expedientes guardados</option>
      </select>`;

  return `
    <div class="file-workspace">
      <div class="workspace-heading">
        <div>
          <span>Gestión de pacientes</span>
          <h3>Expediente de trabajo</h3>
        </div>
        <strong>${escapeHtml(cloudBadge)}</strong>
      </div>
      <div class="file-toolbar">
        <label class="compact-field file-field--select">
          <span>Expediente</span>
          ${fileOptions}
        </label>
        <label class="compact-field file-field--name">
          <span>Nombre del expediente</span>
          <input class="compact" type="text" data-new-file-name value="${escapeHtml(state.newFileDraft.name)}" placeholder="Ej. Consulta inicial - Junio" />
        </label>
        <label class="compact-field file-field--type">
          <span>Tipo</span>
          <select class="compact" data-new-file-type>
            ${FILE_TYPE_OPTIONS.map((item) => `
              <option value="${escapeHtml(item)}" ${selectedFileType === item ? "selected" : ""}>${escapeHtml(item)}</option>
            `).join("")}
            <option value="${FILE_TYPE_CUSTOM_VALUE}" ${selectedFileType === FILE_TYPE_CUSTOM_VALUE ? "selected" : ""}>Personalizado</option>
          </select>
        </label>
        <label class="compact-field file-field--custom ${selectedFileType === FILE_TYPE_CUSTOM_VALUE ? "" : "is-hidden"}">
          <span>Tipo personalizado</span>
          <input class="compact" type="text" data-new-file-type-custom value="${escapeHtml(customFileTypeValue)}" placeholder="Tipo de expediente" />
        </label>
        <label class="compact-field file-field--date">
          <span>Inicio del plan</span>
          <input class="compact" type="date" data-plan-start-date value="${escapeHtml(planStartDate)}" />
        </label>
        <div class="file-actions file-actions--primary">
          <button class="primary" data-action="new-patient">Nuevo paciente</button>
          <button data-action="create-file">Nuevo expediente</button>
          <button data-action="save-file">Guardar</button>
        </div>
        <div class="file-actions file-actions--secondary">
          <button data-action="patient-cloud-save" ${currentFile && cloudEnabled ? "" : "disabled"}>Sincronizar</button>
          <button data-action="patient-cloud-copy-link" ${cloudEnabled ? "" : "disabled"}>Enlace nube</button>
          <button data-action="duplicate-file" ${currentFile ? "" : "disabled"}>Duplicar</button>
          <button data-action="export-file">Exportar</button>
          <button data-action="import-file">Importar</button>
          <button class="danger" data-action="delete-file" ${currentFile ? "" : "disabled"}>Eliminar</button>
        </div>
        <input class="file-import-input" type="file" accept=".json,application/json" data-import-patient-file />
      </div>
      <div class="file-status-grid">
        <span><strong>Paciente</strong>${escapeHtml(formatPatientSnapshot(state.patient))}</span>
        <span><strong>Inicio del plan</strong>${escapeHtml(planStartDate)}</span>
        <span><strong>Estado</strong><span data-auto-save-status>${escapeHtml(autoSaveStatus)}</span></span>
        <span><strong>Nube</strong><span data-patient-cloud-status>${escapeHtml(patientCloudStatusText())}</span></span>
        <span><strong>Respaldo local</strong>Caché de seguridad: ${escapeHtml(state.storageEngine)}${state.lastStorageError ? ` · ${escapeHtml(state.lastStorageError)}` : ""}</span>
        <span><strong>Último guardado</strong>${currentFile ? escapeHtml(lastSave.replace("Último guardado: ", "")) : "Todavía sin expediente guardado"}</span>
        ${currentFile ? `<span><strong>Expediente activo</strong>${escapeHtml(currentFile.name)} · ${escapeHtml(currentFile.type || DEFAULT_FILE_TYPE)}</span>` : ""}
      </div>
      ${state.patientCloud.conflict ? `
        <div class="cloud-conflict" role="alert">
          <strong>Hay una versión diferente en la nube.</strong>
          <span>Revisión local ${escapeHtml(String(state.patientCloud.conflict.localFile?.revision ?? "nueva"))} · revisión nube ${escapeHtml(String(state.patientCloud.conflict.currentRevision ?? "desconocida"))}.</span>
          <div class="file-actions">
            <button data-action="conflict-use-cloud">Usar versión de nube</button>
            <button data-action="conflict-use-local">Conservar versión local</button>
          </div>
        </div>
      ` : ""}
    </div>
  `;
}

function renderPatientForm() {
  return `
    <section class="section" id="paciente">
      <h3>Paciente o valoración</h3>
      <div class="panel">
        <div class="form-grid">
          ${field("Identificador", "nombre")}
          ${field("Fecha de nacimiento", "fecha_nacimiento", "date")}
          ${field("Sexo", "sexo", "text", [
            { value: "femenino", label: "Femenino" },
            { value: "masculino", label: "Masculino" },
          ])}
          ${field("Objetivo principal", "objetivo_principal", "text", [
            { value: "perdida_grasa", label: "Pérdida de grasa" },
            { value: "aumento_masa", label: "Aumento de masa muscular" },
            { value: "mantenimiento", label: "Mantenimiento" },
          ])}
          ${field("Objetivo secundario", "objetivo_secundario")}
          ${renderCalculatedAgeSummary()}
        </div>
      </div>
    </section>
  `;
}

function renderAnthropometricSection() {
  return `
    <section class="section" id="antropometricos">
      <h3>Antropometría y objetivos</h3>
      <div class="panel">
        <div class="form-grid anthropometry-meta-grid">
          ${field("Diagnóstico nutricional", "estado_nutricional", "text", nutritionalDiagnosisOptions.map((option) => ({ value: option, label: option })))}
          ${field("Método antropométrico principal", "metodo_antropometria", "text", anthropometryMethodOptions)}
          ${field("Perfil de población", "population_profile", "text", [
            { value: "adult", label: "Adulto" },
            { value: "pediatric", label: "Pediátrico" },
          ])}
          ${field("Número de valoración", "numero_valoracion", "number")}
          ${field("Fecha de valoración", "assessment_date", "date")}
          ${field("Etapa de entrenamiento", "etapa_entrenamiento")}
        </div>
        ${renderAnthropometrySectionControls()}
        ${renderAnthropometryFieldSections()}
        ${renderAssessmentHistory()}
        ${renderAnthropometryResults()}
      </div>
    </section>
  `;
}

function renderCurrentIntakeSection() {
  return `
    <section class="section" id="consumo_actual">
      <h3>Consumo actual estimado</h3>
      <div class="panel">
        <div class="form-grid">
          ${field("Calorías actuales estimadas", "consumo_calorias_actuales", "number")}
          ${field("Proteína actual g", "consumo_proteina_actual_g", "number")}
          ${field("Carbohidratos actuales g", "consumo_carbohidratos_actual_g", "number")}
          ${field("Grasas actuales g", "consumo_grasas_actual_g", "number")}
        </div>
      </div>
    </section>
  `;
}

function renderActivitySection() {
  return `
    <section class="section" id="actividad">
      <h3>Actividad y entrenamiento</h3>
      <div class="panel">
       <div class="form-grid">
          ${field("Nivel actividad ocupacional", "actividad_ocupacional")}
          ${field("Actividad", "actividad", "text", [
            { value: "bajo", label: "Bajo" },
            { value: "medio", label: "Medio" },
            { value: "alto", label: "Alto" },
          ])}
          ${field("NAF categoría", "naf_categoria", "text", [
            { value: "ligera", label: "Ligera (1.40-1.69)" },
            { value: "moderada", label: "Moderada (1.70-1.99)" },
            { value: "fuerte", label: "Fuerte / alta (2.00-2.40)" },
          ])}
          ${field("NAF manual editable", "naf_valor", "number")}
          ${field("Deporte", "deporte")}
            ${field("Entrenamientos semana", "entrenamientos_semana", "number")}
            ${field("Duración entrenamiento (min)", "duracion_entrenamiento_minutos", "number")}
            ${field("Intensidad entrenamiento", "intensidad_entrenamiento")}
            ${field("Horarios de entrenamiento", "horarios_entrenamiento")}
            ${field("Presupuesto", "presupuesto")}
          </div>
          ${renderMealTimeControls()}
        </div>
    </section>
  `;
}

function renderPreferencesSection() {
  return `
    <section class="section" id="preferencias">
      <h3>Preferencias, restricciones e intolerancias</h3>
      <div class="panel">
        <div class="form-grid form-grid--wide">
          ${field("Hábitos alimentarios", "habitos_alimentarios", "textarea")}
          ${field("Preferencias alimentarias", "preferencias_alimentarias", "textarea")}
          ${field("Intolerancias", "intolerancias", "textarea")}
          ${field("Alergias", "alergias", "textarea")}
          ${field("Condiciones", "condiciones", "textarea")}
          ${field("Enfermedades o condiciones", "enfermedades_condiciones", "textarea")}
          ${field("Alimentos que no consume", "alimentos_no_consume", "textarea")}
          ${field("Alimentos preferidos", "alimentos_preferidos", "textarea")}
          ${field("Restricciones culturales personales", "restricciones_culturales", "textarea")}
          ${field("Suplementos actuales", "suplementos_actuales", "textarea")}
          ${field("Observaciones profesionales", "observaciones_profesionales", "textarea")}
        </div>
      </div>
    </section>
  `;
}

function renderWarnings(warnings) {
  return `
    <section class="section" id="alertas">
      <h3>Alertas profesionales</h3>
      <div class="panel">
        ${warnings.length ? `<ul class="warning-list">${warnings.map((warning) => `<li>${escapeHtml(warning)}</li>`).join("")}</ul>` : `<div class="empty-state">Sin alertas activas para los datos actuales.</div>`}
      </div>
    </section>
  `;
}

function tableStats() {
  const tables = state.data?.tables ?? {};
  return Object.entries(tables).map(([name, table]) => {
    const rows = table.rows ?? [];
    const active = rows.filter((row) => row.estado === "activo").length;
    const updated = rows.map((row) => row.ultima_actualizacion).filter(Boolean).sort().at(-1) ?? state.data.updated;
    return { name, rows: rows.length, active, updated };
  });
}

function renderDataQualityPanel() {
  const audit = auditNutritionData(state.data);
  const topItems = audit.items.slice(0, 24);
  const statusText = audit.summary.error
    ? "Requiere correccion antes de usar esta base como referencia principal."
    : audit.summary.warning
      ? "Base utilizable con advertencias pendientes de revisión."
      : "Base sin problemas criticos detectados.";
  return `
    <div class="panel data-quality">
      <div class="data-quality-header">
        <div>
          <h4>Revision de calidad de base</h4>
          <p>${escapeHtml(statusText)}</p>
        </div>
        <div class="data-quality-summary">
          <span class="quality-pill quality-pill--error">Errores ${audit.summary.error}</span>
          <span class="quality-pill quality-pill--warning">Advertencias ${audit.summary.warning}</span>
          <span class="quality-pill quality-pill--info">Notas ${audit.summary.info}</span>
        </div>
      </div>
      ${topItems.length
    ? `<ul class="quality-list">
          ${topItems.map((item) => `
            <li class="quality-item quality-item--${item.level}">
              <strong>${escapeHtml(item.table)}</strong>
              <span>${escapeHtml(item.message)}</span>
              ${item.detail ? `<small>${escapeHtml(item.detail)}</small>` : ""}
            </li>
          `).join("")}
        </ul>
        ${audit.items.length > topItems.length ? `<small class="quality-extra">Mostrando ${topItems.length} de ${audit.items.length} hallazgos.</small>` : ""}`
    : `<div class="empty-state">No se detectaron problemas en columnas obligatorias, IDs, estados ni valores numericos.</div>`}
    </div>
  `;
}

function dataCellEditor(tableName, rowIndex, column, value) {
  const commonAttrs = `data-db-cell data-db-table="${escapeHtml(tableName)}" data-row-index="${rowIndex}" data-column="${escapeHtml(column)}"`;
  if (isLongDataField(column, value)) {
    return `<textarea class="data-cell data-cell--long" rows="2" ${commonAttrs}>${escapeHtml(value)}</textarea>`;
  }
  return `<input class="data-cell" value="${escapeHtml(value)}" ${commonAttrs} />`;
}

function renderDataTableEditor() {
  const tableNames = dataTableNames();
  const tableName = getSelectedDataTableName();
  const table = getSelectedDataTable();
  if (!tableNames.length || !table) {
    return `<div class="panel"><div class="empty-state">No hay tablas disponibles para editar.</div></div>`;
  }
  const columns = getDataTableColumns(table);
  const rows = table.rows ?? [];
  return `
    <div class="panel data-editor">
      <div class="data-editor-toolbar">
        <label class="compact-field">
          <span>Tabla editable</span>
          <select class="compact" data-db-table-select>
            ${tableNames.map((name) => `<option value="${escapeHtml(name)}" ${name === tableName ? "selected" : ""}>${escapeHtml(name)} (${state.data.tables[name]?.rows?.length ?? 0})</option>`).join("")}
          </select>
        </label>
        <button data-action="add-data-row">Agregar registro</button>
        <button data-action="save-data">Guardar base en navegador</button>
        <button data-action="export-table-csv">Exportar tabla CSV</button>
        <button data-action="import-table-csv">Importar tabla CSV</button>
        <input class="file-import-input" type="file" accept=".csv,text/csv" data-import-table-csv />
        <span class="data-editor-status" data-data-editor-status>
          ${state.dataHasPendingChanges ? "Cambios pendientes: guarda la base para recalcular y conservarla en este navegador." : `Base sincronizada: ${escapeHtml(state.dataSourceLabel)}`}
        </span>
      </div>
      <div class="table-wrap data-table-wrap">
        <table class="data-table-editor">
          <thead>
            <tr>
              <th>Acciones</th>
              ${columns.map((column) => `<th>${escapeHtml(formatColumnLabel(column))}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row, rowIndex) => `
              <tr>
                <td class="data-row-actions">
                  <button data-action="duplicate-data-row" data-row-index="${rowIndex}">Duplicar</button>
                  <button class="danger" data-action="delete-data-row" data-row-index="${rowIndex}">Eliminar</button>
                </td>
                ${columns.map((column) => `<td>${dataCellEditor(tableName, rowIndex, column, row?.[column] ?? "")}</td>`).join("")}
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function renderDataSourcePanel(stats) {
  const hasOverride = hasNutritionDataOverride();
  const projectStats = Object.values(state.defaultData?.tables ?? {}).reduce((sum, table) => sum + (table?.rows?.length ?? 0), 0);
  const loadedRows = stats.reduce((sum, row) => sum + row.rows, 0);
  const sourceWarning = hasOverride
    ? "Hay una base personalizada guardada en este navegador. Puede conservar ajustes propios, pero tambien puede ocultar tablas, pacientes o recetas nuevas publicadas en la base oficial."
    : "Este navegador esta usando la base oficial publicada con la app.";
  return `
    <div class="data-maintenance data-source-card">
      <div class="data-source-header">
        <div>
          <span class="data-source-kicker">Base activa</span>
          <strong>${escapeHtml(state.dataSourceLabel)}</strong>
        </div>
        <span class="data-source-badge ${hasOverride ? "data-source-badge--custom" : ""}">
          ${hasOverride ? "Personalizada local" : "Oficial"}
        </span>
      </div>
      <div class="data-source-grid">
        <div><label>Version activa</label><strong>${escapeHtml(dataVersionLabel(state.data))}</strong></div>
        <div><label>Version oficial</label><strong>${escapeHtml(dataVersionLabel(state.defaultData))}</strong></div>
        <div><label>Registros cargados</label><strong>${loadedRows}</strong></div>
        <div><label>Registros oficiales</label><strong>${projectStats}</strong></div>
      </div>
      <p class="data-source-alert">${escapeHtml(sourceWarning)}</p>
      <div class="data-actions">
        <button data-action="export-data">Exportar base JSON</button>
        <button data-action="import-data">Importar base JSON</button>
        <button class="primary" data-action="restore-data">Cargar base oficial</button>
        <button class="danger" data-action="clear-data-override" ${hasOverride ? "" : "disabled"}>Eliminar base local</button>
      </div>
      <input class="file-import-input" type="file" accept=".json,application/json" data-import-nutrition-data />
    </div>
  `;
}

function renderDataSettings() {
  const stats = tableStats();
  const totalRows = stats.reduce((sum, row) => sum + row.rows, 0);
  const activeRows = stats.reduce((sum, row) => sum + row.active, 0);
  return `
    <section class="section" id="base_datos">
      <h3>Configuracion de base de datos</h3>
      <div class="maintenance-grid">
        <div class="panel">
          <div class="summary-list summary-list--compact">
            <div><label>Tablas</label><strong>${stats.length}</strong></div>
            <div><label>Registros</label><strong>${totalRows}</strong></div>
            <div><label>Activos</label><strong>${activeRows}</strong></div>
            <div><label>Version</label><strong>${escapeHtml(state.data.updated)}</strong></div>
          </div>
          ${renderDataSourcePanel(stats)}
          <div class="path-list">
            <span>CSV <code>01_BASES_DATOS/csv/</code></span>
            <span>Excel <code>01_BASES_DATOS/base_nutricion_editable.xlsx</code></span>
            <span>JSON app <code>06_APP_WEB/frontend/src/data/base_nutricion.json</code></span>
          </div>
        </div>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Tabla</th><th>Registros</th><th>Activos</th><th>Actualizacion</th></tr></thead>
            <tbody>
              ${stats.map((row) => `
                <tr>
                  <td>${escapeHtml(row.name)}</td>
                  <td>${row.rows}</td>
                  <td>${row.active}</td>
                  <td>${escapeHtml(row.updated)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>
      </div>
      ${renderDataQualityPanel()}
      ${renderDataTableEditor()}
    </section>
  `;
}

function professionalValidationItemState(item) {
  const decision = String(item?.decision_profesional ?? "").trim().toLowerCase();
  if (PROFESSIONAL_VALIDATION_CLOSING_DECISIONS.has(decision)) {
    return professionalValidationHasClosureSignature(item) ? "closed" : "action";
  }
  if (PROFESSIONAL_VALIDATION_ACTION_DECISIONS.has(decision)) return "action";
  const stateName = String(item?.estado_actual ?? "").trim().toLowerCase();
  if (PROFESSIONAL_VALIDATION_CLOSED_STATES.has(stateName) || PROFESSIONAL_VALIDATION_COMPLETED_STATES.has(stateName)) {
    return professionalValidationHasClosureSignature(item) ? "closed" : "action";
  }
  if (PROFESSIONAL_VALIDATION_ACTION_STATES.has(stateName)) return "action";
  return "pending";
}

function professionalValidationHasClosureSignature(item) {
  return Boolean(String(item?.responsable ?? "").trim() && String(item?.fecha_revision ?? "").trim());
}

function isProfessionalValidationItemPending(item) {
  return professionalValidationItemState(item) !== "closed";
}

function professionalValidationBadgeClass(item) {
  const stateName = professionalValidationItemState(item);
  if (stateName === "closed") return "status-badge--ok";
  if (stateName === "action") return "status-badge--action";
  return "status-badge--warning";
}

function professionalValidationDisplayStatus(item) {
  const decision = String(item?.decision_profesional ?? "").trim().toLowerCase();
  if (PROFESSIONAL_VALIDATION_CLOSING_DECISIONS.has(decision) && !professionalValidationHasClosureSignature(item)) {
    return "falta_responsable_fecha";
  }
  const labels = {
    aprobado: "validado_local",
    aprobado_con_ajustes: "requiere_ajustes",
    rechazado: "requiere_retiro",
    requiere_fuente: "requiere_fuente",
    no_aplica: "no_aplica_local",
  };
  return labels[decision] || item?.estado_actual || "pendiente_revision";
}

function professionalValidationMatrixToCsv(items) {
  const columns = [
    "id",
    "area",
    "elemento",
    "fuente_actual",
    "evidencia_sistema",
    "criterio_validacion",
    "estado_actual",
    "decision_profesional",
    "responsable",
    "fecha_revision",
    "observaciones",
  ];
  const rows = [columns, ...items.map((item) => columns.map((column) => item?.[column] ?? ""))];
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

function downloadProfessionalValidationCsv() {
  const validation = getProfessionalValidationStatus();
  const csv = `\ufeff${professionalValidationMatrixToCsv(validation.items)}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `matriz_validacion_profesional_${formatDateForFile()}.csv`);
}

async function importProfessionalValidationCsvFromInput(input) {
  const file = input.files?.[0];
  if (!file) return;
  try {
    const text = await readFileAsText(file);
    const parsed = csvRowsToObjects(parseCsv(text));
    const validDecisionValues = new Set(PROFESSIONAL_VALIDATION_DECISIONS.map(([value]) => value));
    const validation = getProfessionalValidationStatus();
    const knownIds = new Set(validation.items.map((item) => item.id));
    const nextDecisions = { ...state.professionalValidationDecisions };
    let imported = 0;
    let ignored = 0;
    for (const row of parsed.rows) {
      const id = String(row.id ?? "").trim();
      if (!id || !knownIds.has(id)) {
        ignored += 1;
        continue;
      }
      const decision = String(row.decision_profesional ?? "").trim();
      if (decision && !validDecisionValues.has(decision)) {
        throw new Error(`Decision no valida para ${id}: ${decision}`);
      }
      const record = {
        decision_profesional: decision,
        responsable: String(row.responsable ?? "").trim(),
        fecha_revision: String(row.fecha_revision ?? "").trim(),
        observaciones: String(row.observaciones ?? "").trim(),
      };
      if (Object.values(record).some((value) => String(value ?? "").trim())) {
        nextDecisions[id] = record;
      } else {
        delete nextDecisions[id];
      }
      imported += 1;
    }
    state.professionalValidationDecisions = nextDecisions;
    persistProfessionalValidationDecisions();
    window.alert(`Decisiones importadas: ${imported}. Filas ignoradas: ${ignored}.`);
    render();
  } catch (error) {
    window.alert(error?.message || "No se pudo importar el CSV de validacion profesional.");
  } finally {
    input.value = "";
  }
}

function updateProfessionalValidationDecision(itemId, field, value) {
  const id = String(itemId ?? "").trim();
  const column = String(field ?? "").trim();
  if (!id || !["decision_profesional", "responsable", "fecha_revision", "observaciones"].includes(column)) return;
  const previous = state.professionalValidationDecisions[id] ?? {};
  const next = { ...previous, [column]: String(value ?? "").trim() };
  if (Object.values(next).some((item) => String(item ?? "").trim())) {
    state.professionalValidationDecisions[id] = next;
  } else {
    delete state.professionalValidationDecisions[id];
  }
  persistProfessionalValidationDecisions();
  renderAutoSaveTicker();
}

function professionalValidationDecisionSelect(item) {
  const value = String(item.decision_profesional ?? "");
  return `
    <select class="compact validation-control" data-validation-id="${escapeHtml(item.id)}" data-validation-field="decision_profesional">
      ${PROFESSIONAL_VALIDATION_DECISIONS.map(([optionValue, label]) => `
        <option value="${escapeHtml(optionValue)}" ${optionValue === value ? "selected" : ""}>${escapeHtml(label)}</option>
      `).join("")}
    </select>
  `;
}

function professionalValidationInput(item, field, type = "text") {
  return `<input class="compact validation-control" type="${type}" value="${escapeHtml(item[field] ?? "")}" data-validation-id="${escapeHtml(item.id)}" data-validation-field="${field}" />`;
}

function professionalValidationNotesInput(item) {
  return `<textarea class="validation-control validation-control--notes" rows="2" data-validation-id="${escapeHtml(item.id)}" data-validation-field="observaciones">${escapeHtml(item.observaciones ?? "")}</textarea>`;
}

function renderProfessionalValidationClosureChecklist(validation) {
  const missingSignatureCount = validation.items.filter((item) => {
    const decision = String(item?.decision_profesional ?? "").trim().toLowerCase();
    return PROFESSIONAL_VALIDATION_CLOSING_DECISIONS.has(decision) && !professionalValidationHasClosureSignature(item);
  }).length;
  const summary = getProfessionalClosureSummary(validation);
  if (summary.isClosed) return renderProfessionalClosureCertificate("panel");
  const checks = [
    ["Sin decision", validation.noDecisionCount],
    ["Con ajustes, retiro o fuente pendiente", validation.actionRequiredCount],
    ["Aprobados/no aplica sin responsable o fecha", missingSignatureCount],
  ];
  return `
    <div class="validation-closure-checklist" data-professional-closure-checklist>
      <div>
        <span>Cierre profesional</span>
        <strong>No listo para entrega final</strong>
      </div>
      <p>Para cerrar, cada fila debe tener decision profesional, responsable y fecha. Las decisiones con ajustes, rechazo o fuente pendiente deben resolverse antes de usar el sistema con pacientes reales.</p>
      <ul>
        ${checks.map(([label, count]) => `<li><strong>${count}</strong><span>${escapeHtml(label)}</span></li>`).join("")}
      </ul>
    </div>
  `;
}

function renderProfessionalValidationPanel() {
  const validation = getProfessionalValidationStatus();
  const pendingItems = validation.items.filter(isProfessionalValidationItemPending);
  const items = validation.items.length ? validation.items : [];
  return `
    <section class="section" id="validacion">
      <h3>Validacion profesional</h3>
      ${renderProfessionalValidationNotice("panel")}
      <div class="panel professional-validation-panel">
        <div class="professional-validation-header">
          <div class="summary-list summary-list--compact">
            <div><label>Total</label><strong>${validation.totalItems}</strong></div>
            <div><label>Sin decision</label><strong>${validation.noDecisionCount}</strong></div>
            <div><label>Requieren accion</label><strong>${validation.actionRequiredCount}</strong></div>
            <div><label>Listos</label><strong>${validation.closureReadyCount}</strong></div>
            <div><label>Areas</label><strong>${validation.pendingAreas.length || "-"}</strong></div>
          </div>
          <div class="professional-validation-actions">
            <button data-action="export-professional-validation">Exportar matriz CSV</button>
            <button data-action="import-professional-validation">Importar decisiones CSV</button>
            <code>${escapeHtml(validation.source)}</code>
          </div>
        </div>
        ${renderProfessionalValidationClosureChecklist(validation)}
        <input class="file-import-input" type="file" accept=".csv,text/csv" data-import-professional-validation-csv />
        ${items.length ? `
          <div class="table-wrap professional-validation-wrap">
            <table class="professional-validation-table">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Elemento</th>
                  <th>Criterio</th>
                  <th>Evidencia</th>
                  <th>Estado</th>
                  <th>Decision</th>
                  <th>Responsable</th>
                  <th>Fecha</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                ${items.map((item) => `
                  <tr class="is-${professionalValidationItemState(item)}">
                    <td>${escapeHtml(formatColumnLabel(item.area || ""))}</td>
                    <td><strong>${escapeHtml(item.elemento || item.id)}</strong><small>${escapeHtml(item.fuente_actual || "")}</small></td>
                    <td>${escapeHtml(item.criterio_validacion || "")}</td>
                    <td><code>${escapeHtml(item.evidencia_sistema || "")}</code></td>
                    <td><span class="status-badge ${professionalValidationBadgeClass(item)}">${escapeHtml(professionalValidationDisplayStatus(item))}</span></td>
                    <td>${professionalValidationDecisionSelect(item)}</td>
                    <td>${professionalValidationInput(item, "responsable")}</td>
                    <td>${professionalValidationInput(item, "fecha_revision", "date")}</td>
                    <td>${professionalValidationNotesInput(item)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
          ${pendingItems.length ? `<small class="professional-validation-footnote">${pendingItems.length} elementos aun no estan listos para cierre profesional: ${validation.noDecisionCount} sin decision y ${validation.actionRequiredCount} con acciones pendientes.</small>` : ""}`
    : `<div class="empty-state">La matriz profesional no esta cargada en la base publica.</div>`}
      </div>
    </section>
  `;
}

function renderUpdateGuide() {
  return `
    <section class="section" id="actualizacion">
      <h3>Guia para actualizar contenido</h3>
      <div class="update-grid">
        <div class="panel update-step">
          <span class="step-index">1</span>
          <strong>Editar base</strong>
          <code>01_BASES_DATOS/csv/</code>
          <code>01_BASES_DATOS/base_nutricion_editable.xlsx</code>
        </div>
        <div class="panel update-step">
          <span class="step-index">2</span>
          <strong>Regenerar JSON</strong>
          <code>node 06_APP_WEB/scripts/build_base_data.mjs</code>
        </div>
        <div class="panel update-step">
          <span class="step-index">3</span>
          <strong>Verificar app</strong>
          <code>python -m http.server 5173 --bind 127.0.0.1</code>
        </div>
        <div class="panel update-step">
          <span class="step-index">4</span>
          <strong>Publicar</strong>
          <code>https://sistema-digital-de-nutricion.pages.dev</code>
        </div>
        <div class="panel update-step">
          <span class="step-index">5</span>
          <strong>Entender el sistema</strong>
          <code>07_DOCUMENTACION/informe_funcionamiento/informe_funcionamiento_sistema.md</code>
        </div>
        <div class="panel update-step">
          <span class="step-index">6</span>
          <strong>Cerrar pendientes humanos</strong>
          <code>07_DOCUMENTACION/cierre_humano/guia_cierre_humano.md</code>
        </div>
      </div>
    </section>
  `;
}

function renderDeliverableOptions() {
  const options = state.deliverableOptions;
  const toggles = [
    ["showCurrentIntake", "Consumo actual estimado"],
    ["showWarnings", "Alertas profesionales"],
    ["showSupplements", "Suplementos"],
    ["showProducts", "Productos recomendados"],
    ["showRecipes", "Recetas"],
    ["showShoppingList", "Lista de compras"],
  ];
  return `
    <section class="section" id="entregable">
      <h3>Personalizacion del entregable</h3>
      <div class="panel deliverable-options">
        <div class="form-grid form-grid--wide">
          <div class="field">
            <label for="deliverable_title">Titulo de portada</label>
            <input id="deliverable_title" data-deliverable-field="title" value="${escapeHtml(options.title)}" />
          </div>
          <div class="field">
            <label for="deliverable_final_note">Nota profesional final</label>
            <textarea id="deliverable_final_note" rows="3" data-deliverable-field="finalNote">${escapeHtml(options.finalNote)}</textarea>
          </div>
        </div>
        <div class="deliverable-toggle-grid">
          ${toggles.map(([key, label]) => `
            <label class="deliverable-toggle">
              <input type="checkbox" data-deliverable-toggle="${key}" ${options[key] ? "checked" : ""} />
              <span>${escapeHtml(label)}</span>
            </label>
          `).join("")}
        </div>
      </div>
    </section>
  `;
}

function renderCalculations(energy, macros, intake) {
  const objective = objectiveRules[state.patient.objetivo_principal] ?? objectiveRules.mantenimiento;
  const nafCategory = nafCatalog[energy.naf.category] ?? nafCatalog.moderada;
  const notes = [...(energy.literalNotes ?? []), ...(macros.notes ?? [])];
  return `
    <section class="section" id="calculos">
      <h3>Cálculos energéticos y macronutrientes</h3>
      <div class="panel get-form-panel">
        <h4>Gasto energetico total</h4>
        <div class="form-grid">
          ${calculatedField("Kg masa adiposa a perder", "kg_masa_adiposa_perder", energy.adjustments.restriction.kgAdiposeToLose, `Fuente: ${energy.adjustments.restriction.source || "manual"}`)}
          ${field("Días restricción", "dias_restriccion", "number")}
          ${calculatedField("Kg masa muscular a ganar", "kg_masa_muscular_ganar", energy.adjustments.addition.kgMuscleToGain, `Fuente: ${energy.adjustments.addition.source || "manual"}`)}
          ${field("Días adición", "dias_adicion", "number")}
          ${field("GET3 kcal final editable", "get_3", "number")}
          ${field("Proteína g/kg", "proteina_gkg", "number")}
          ${field("Número de tiempos de comida", "numero_tiempos_comida", "number")}
          ${field("Grasa g/kg", "grasa_gkg", "number")}
          ${field("Carbohidrato g/kg", "carbohidrato_gkg", "number")}
        </div>
      </div>

      <div class="split split--three calculation-summary">
        <div class="panel">
          <div class="summary-list summary-list--stacked">
            <div><label>NAF usado</label><strong>${formatDecimal(energy.activityFactor, 2)} ${energy.naf.isManual ? "(manual)" : "(sugerido)"}</strong></div>
            <div><label>Categoria NAF</label><strong>${escapeHtml(nafCategory.label)} ${escapeHtml(nafCategory.range)}</strong></div>
            <div><label>Ajuste kcal/día</label><strong>${formatNumber(energy.adjustment, " kcal")}</strong></div>
            <div><label>Regla</label><strong>${escapeHtml(objective.label)}</strong></div>
          </div>
        </div>
        <div class="panel">
          <div class="summary-list summary-list--stacked">
            <div><label>GET1 Harris</label><strong>${formatNumber(energy.get1.harrisBenedict, " kcal")}</strong></div>
            <div><label>GET2 Harris</label><strong>${formatNumber(energy.get2.harrisBenedict, " kcal")}</strong></div>
            <div><label>GET3 final</label><strong>${formatNumber(energy.get3.final, " kcal")}</strong></div>
            <div><label>Fuente GET3</label><strong>${escapeHtml(energy.get3.source)}</strong></div>
            <div><label>Estado GET3</label><strong>${energy.get3.confirmed ? "Confirmado por profesional" : "Sugerido, pendiente de confirmación"}</strong></div>
          </div>
        </div>
        <div class="panel">
          <div class="summary-list summary-list--stacked">
            <div><label>Actual kcal</label><strong data-intake-calories>${formatOptionalNumber(intake.calories, " kcal")}</strong></div>
            <div><label>Diferencia kcal</label><strong data-intake-delta>${formatDelta(intake.calories, macros.calories, " kcal")}</strong></div>
            <div><label>Proteína actual</label><strong data-intake-protein>${formatOptionalNumber(intake.protein, " g")}</strong></div>
            <div><label>Carbohidratos / grasas</label><strong data-intake-carbs-fat>${formatOptionalNumber(intake.carbs, " g")} / ${formatOptionalNumber(intake.fat, " g")}</strong></div>
          </div>
        </div>
      </div>
      <div class="calculation-grid">
        <div class="panel calculation-card">
          <h4>Cuadro 1. NAF por categoría</h4>
          <div class="table-wrap table-wrap--compact">
            <table class="calculation-table">
              <thead><tr><th>Categoria</th><th>Rango</th><th>Criterio</th></tr></thead>
              <tbody>
                ${Object.entries(nafCatalog).map(([id, item]) => `
                  <tr class="${id === energy.naf.category ? "selected-row" : ""}">
                    <td>${escapeHtml(item.label)}</td>
                    <td>${escapeHtml(item.range)}</td>
                    <td>${escapeHtml(item.description)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
        </div>
        <div class="panel calculation-card">
          <h4>Cuadro 2. NAF sugerido por sexo</h4>
          <div class="table-wrap table-wrap--compact">
            <table class="calculation-table">
              <thead><tr><th>Categoria</th><th>Hombres</th><th>Mujeres</th></tr></thead>
              <tbody>
                ${Object.entries(nafCatalog).map(([id, item]) => `
                  <tr class="${id === energy.naf.category ? "selected-row" : ""}">
                    <td>${escapeHtml(item.label)}</td>
                    <td>${formatDecimal(item.male, 2)}</td>
                    <td>${formatDecimal(item.female, 2)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
        </div>
        <div class="panel calculation-card">
          <h4>TMB y GET1</h4>
          <div class="table-wrap table-wrap--compact">
            <table class="calculation-table">
              <thead><tr><th>Metodo</th><th>TMB</th><th>Dato base</th><th>GET1</th></tr></thead>
              <tbody>
                <tr>
                  <td>Cunningham</td>
                  <td>${formatNumber(energy.cunningham.value, " kcal")}</td>
                  <td>${formatDecimal(energy.cunningham.leanMassKg, 1)} kg MLG</td>
                  <td>${formatNumber(energy.get1.cunningham, " kcal")}</td>
                </tr>
                <tr>
                  <td>Harris-Benedict</td>
                  <td>${formatNumber(energy.harrisBenedict.value, " kcal")}</td>
                  <td>${formatDecimal(energy.harrisBenedict.idealWeightKg, 1)} kg ideal</td>
                  <td>${formatNumber(energy.get1.harrisBenedict, " kcal")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="panel calculation-card">
          <h4>Cuadro 3. Restriccion energetica</h4>
          <div class="summary-list summary-list--stacked">
            <div><label>Total deficit</label><strong>${formatNumber(energy.adjustments.restriction.totalKcal, " kcal")}</strong></div>
            <div><label>Déficit diario</label><strong>${formatNumber(energy.adjustments.restriction.dailyKcal, " kcal")}</strong></div>
            <div><label>Perdida estimada</label><strong>${formatNumber(energy.adjustments.restriction.gramsPerWeek, " g/sem")}</strong></div>
            <div><label>Formula</label><code>${escapeHtml(energy.adjustments.restriction.formula)}</code></div>
          </div>
        </div>
        <div class="panel calculation-card">
          <h4>Cuadro 4. Adicion energetica</h4>
          <div class="summary-list summary-list--stacked">
            <div><label>Total adicion</label><strong>${formatNumber(energy.adjustments.addition.totalKcal, " kcal")}</strong></div>
            <div><label>Adición diaria</label><strong>${formatNumber(energy.adjustments.addition.dailyKcal, " kcal")}</strong></div>
            <div><label>Ganancia estimada</label><strong>${formatNumber(energy.adjustments.addition.gramsPerWeek, " g/sem")}</strong></div>
            <div><label>Formula</label><code>${escapeHtml(energy.adjustments.addition.formula)}</code></div>
          </div>
        </div>
        <div class="panel calculation-card">
          <h4>Cuadro 5. GET2 y GET3</h4>
          <div class="summary-list summary-list--stacked">
            <div><label>GET2 Cunningham</label><strong>${formatNumber(energy.get2.cunningham, " kcal")}</strong></div>
            <div><label>GET2 Harris</label><strong>${formatNumber(energy.get2.harrisBenedict, " kcal")}</strong></div>
            <div><label>GET3 manual</label><strong>${formatOptionalNumber(energy.get3.manual, " kcal")}</strong></div>
            <div><label>GET3 final</label><strong>${formatNumber(energy.get3.final, " kcal")}</strong></div>
          </div>
        </div>
        <div class="panel calculation-card calculation-card--wide">
          <h4>Cuadro 6. Macronutrientes</h4>
          <div class="table-wrap table-wrap--compact">
            <table class="calculation-table">
              <thead><tr><th>Macro</th><th>g/kg</th><th>g/día</th><th>kcal/día</th><th>VCT %</th></tr></thead>
              <tbody>
                ${macros.table.map((row) => `
                  <tr>
                    <td>${escapeHtml(row.macro)}</td>
                    <td>${formatDecimal(row.gKg, 2)}</td>
                    <td>${formatNumber(row.grams, " g")}</td>
                    <td>${formatNumber(row.kcal, " kcal")}</td>
                    <td>${formatDecimal(row.vct, 1)}%</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
          <div class="summary-list">
            <div><label>Total macros</label><strong>${formatNumber(macros.totalMacroKcal, " kcal")}</strong></div>
            <div><label>Adecuacion kcal</label><strong>${formatDecimal(macros.adequacyKcalPercent, 1)}%</strong></div>
            <div><label>Proteína por comida</label><strong>${formatDecimal(macros.proteinPerMeal, 1)} g (${macros.mealCount} tiempos)</strong></div>
            <div><label>Tiempos activos</label><strong>${macros.activeMealCount}${macros.mealCountMismatch ? " · revisar diferencia" : ""}</strong></div>
          </div>
        </div>
      </div>
      ${notes.length ? `<div class="panel professional-note calculation-notes"><strong>Notas profesionales:</strong><ul>${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul></div>` : ""}
    </section>
  `;
}

function renderScheme() {
  return `
    <section class="section" id="esquema">
        <h3>Esquema general de alimentación diaria</h3>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Tiempo de comida</th>
              <th>Grupo fuente</th>
              <th>Opciones de alimentos</th>
              <th>Ejemplo con cantidad</th>
              <th>Porciones programadas</th>
            </tr>
          </thead>
          <tbody>
            ${state.scheme.map((row) => `
              <tr>
                <td>${escapeHtml(row.tiempo)}</td>
                <td>${escapeHtml(row.grupo)}</td>
                <td><textarea class="editable-cell" data-scheme-id="${row.id}" data-scheme-field="opciones">${escapeHtml(row.opciones)}</textarea></td>
                <td><textarea class="editable-cell" data-scheme-id="${row.id}" data-scheme-field="ejemplo">${escapeHtml(row.ejemplo)}</textarea></td>
                <td><input class="editable-cell" data-scheme-id="${row.id}" data-scheme-field="porciones" value="${escapeHtml(row.porciones)}" /></td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function renderMenu() {
  return `
    <section class="section" id="menu">
      <h3>Ciclo de menú de 7 días</h3>
      <div class="menu-grid">
        ${state.menu.map((day) => `
          <div class="day-column">
            <h4>Día ${day.day}</h4>
            ${day.meals.map((meal) => `
              <div class="meal-block">
                <label>${escapeHtml(meal.label)}</label>
                <textarea class="menu-textarea" data-day="${day.day}" data-meal="${meal.meal}">${escapeHtml(meal.text)}</textarea>
                ${renderMealProductChips(day.day, meal.meal)}
              </div>
            `).join("")}
          </div>
        `).join("")}
      </div>
    </section>
  `;
}

function choiceCard(item, selectedSet, kind) {
  const checked = selectedSet.has(item.id) ? "checked" : "";
  const img = getItemImage(item);
  const description = item.descripcion ?? item.motivo_recomendacion ?? item.recomendaciones ?? "";
  return `
    <article class="choice-card">
      <img src="${img}" alt="${escapeHtml(item.nombre)}" loading="lazy" />
      <div>
        <label>
          <input type="checkbox" data-choice-kind="${kind}" data-choice-id="${escapeHtml(item.id)}" ${checked} />
          ${escapeHtml(item.nombre)}
        </label>
        <p>${escapeHtml(description)}</p>
      </div>
    </article>
  `;
}

function visualItemCards(items, kind) {
  if (!items.length) {
    return kind === "recipe" ? "<p>No se seleccionaron recetas.</p>" : "<p>No se seleccionaron productos.</p>";
  }
  return `
    <div class="visual-item-grid">
      ${items.map((item) => {
        const image = getItemImage(item);
        const meta = kind === "recipe"
          ? [item.ingredientes, item.tiempo_estimado ? `Tiempo: ${item.tiempo_estimado}` : ""].filter(Boolean).join(" · ")
          : [item.marca, item.donde_se_consigue].filter(Boolean).join(" · ");
        const body = kind === "recipe"
          ? [item.preparacion, item.recomendaciones].filter(Boolean).join(" ")
          : [item.descripcion, item.motivo_recomendacion, item.observaciones].filter(Boolean).join(" ");
        return `
          <article class="visual-item-card">
            <img src="${image}" alt="${escapeHtml(item.nombre)}" loading="eager" />
            <div>
              <strong>${escapeHtml(item.nombre)}</strong>
              ${meta ? `<small>${escapeHtml(meta)}</small>` : ""}
              ${body ? `<p>${escapeHtml(body)}</p>` : ""}
            </div>
          </article>
        `;
      }).join("")}
    </div>
  `;
}

function renderPlanProductPreview(links) {
  if (!links.length) return "";
  return `
    <div class="table-wrap plan-product-preview">
      <table>
        <thead><tr><th>Producto</th><th>Programación</th><th>Porción y nutrición</th><th>Tienda/fuente</th></tr></thead>
        <tbody>
          ${links.map((link) => `
            <tr>
              <td><strong>${escapeHtml(link.displayName)}</strong><small>${escapeHtml([link.brand, link.packageSize, planProductStatusLabel(link.dataStatus)].filter(Boolean).join(" · "))}</small></td>
              <td>${escapeHtml(planProductTargetLabel(link))}</td>
              <td>${escapeHtml(formatPlanProductServing(link))}<small>${escapeHtml(formatPlanProductNutrition(link.nutrition))}</small></td>
              <td>${escapeHtml(formatPlanProductOffer(link.offer))}${link.note ? `<small>${escapeHtml(link.note)}</small>` : ""}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderRecommendations() {
  return `
    <section class="section" id="recomendaciones">
      <h3>Recomendaciones individualizadas</h3>
      <div class="panel">
        ${state.recommendations.map((rec) => `
          <div class="meal-block">
            <label>${escapeHtml(rec.title)}</label>
            <textarea class="menu-textarea" data-rec-id="${rec.id}">${escapeHtml(rec.text)}</textarea>
          </div>
        `).join("")}
      </div>
    </section>
  `;
}

function renderPlanProductProfessionalList() {
  const rows = planProductProfessionalRows();
  if (!rows.length) return "";
  return `
    <div class="table-wrap plan-product-professional-list">
      <table>
        <thead><tr><th>Producto</th><th>Uso en el plan</th><th>Porción</th><th>Nutrición estimada</th><th>Compra/fuente</th></tr></thead>
        <tbody>
          ${rows.map((row) => `
            <tr>
              <td><strong>${escapeHtml(row.product)}</strong><small>${escapeHtml([row.brand, row.packageSize, planProductStatusLabel(row.dataStatus)].filter(Boolean).join(" · "))}</small></td>
              <td>${escapeHtml(row.target)}</td>
              <td>${escapeHtml(`${row.servingGrams} g/ml`)}</td>
              <td>${escapeHtml([
                row.nutritionPerServing.energyKcal !== "" ? `${formatNumber(Math.round(row.nutritionPerServing.energyKcal), " kcal")}` : "",
                row.nutritionPerServing.protein !== "" ? `${formatDecimal(row.nutritionPerServing.protein, 1)} g proteina` : "",
                row.nutritionPerServing.carbohydrates !== "" ? `${formatDecimal(row.nutritionPerServing.carbohydrates, 1)} g CHO` : "",
                row.nutritionPerServing.fat !== "" ? `${formatDecimal(row.nutritionPerServing.fat, 1)} g grasa` : "",
              ].filter(Boolean).join(" · ") || "Pendiente")}</td>
              <td>${escapeHtml(formatPlanProductOffer(row.offer))}${row.note ? `<small>${escapeHtml(row.note)}</small>` : ""}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderPlanProductResults() {
  if (state.planProducts.loading) {
    return `<div class="empty-state">Cargando productos de la base en nube...</div>`;
  }
  if (!state.planProducts.results.length) {
    return `<div class="empty-state">Busca por nombre, marca o categoría para asociar productos reales al plan.</div>`;
  }
  return `
    <div class="plan-product-results">
      ${state.planProducts.results.map((product) => {
        const meta = [product.brand, product.packageSize, product.categoryLevel1, product.categoryLevel2].filter(Boolean).join(" · ");
        const nutrition = formatPlanProductNutrition(product);
        const status = planProductStatusLabel(product.dataStatus);
        const price = formatCurrencyCents(product.bestComparablePriceCents || product.bestExactPriceCents || product.priceCents, product.currency || "COP");
        const source = [product.bestRetailerName || product.retailerName, product.bestCity, product.source].filter(Boolean).join(" · ");
        return `
          <article class="plan-product-card">
            <div>
              <strong>${escapeHtml(product.displayName)}</strong>
              ${meta ? `<small>${escapeHtml(meta)}</small>` : ""}
              <p>${escapeHtml(nutrition)}</p>
              <em>${escapeHtml([status, price, source].filter(Boolean).join(" · "))}${Number(product.isDemo) ? " · demo/pending" : ""}</em>
            </div>
            <button class="primary" data-action="plan-product-add" data-product-id="${escapeHtml(product.id)}">Asociar</button>
          </article>
        `;
      }).join("")}
    </div>
  `;
}

function renderPlanProductLinks() {
  if (!state.planProducts.links.length) {
    return `<div class="empty-state">Todavia no hay productos de tienda asociados al plan activo.</div>`;
  }
  const grouped = new Map();
  for (const link of state.planProducts.links) {
    const list = grouped.get(link.mealId) ?? [];
    list.push(link);
    grouped.set(link.mealId, list);
  }
  return `
    <div class="plan-product-linked">
      ${[...grouped.entries()].map(([mealId, links]) => `
        <div class="plan-product-meal">
          <h5>${escapeHtml(mealLabel(mealId))} <span>${links.length}</span></h5>
          ${links.map((link) => `
            <article class="linked-product-card">
              <div>
                <strong>${escapeHtml(link.displayName)}</strong>
                <small>${escapeHtml([link.brand, link.packageSize, planProductStatusLabel(link.dataStatus)].filter(Boolean).join(" · "))}</small>
                <em>${escapeHtml(`${planProductTargetLabel(link)} · ${formatPlanProductServing(link)}`)}</em>
                <input type="number" min="1" step="5" data-plan-product-serving-edit="${escapeHtml(link.linkId)}" value="${escapeHtml(link.servingGrams)}" aria-label="Porción en gramos o mililitros" />
                <input data-plan-product-note="${escapeHtml(link.linkId)}" value="${escapeHtml(link.note)}" placeholder="Nota profesional para lista de compras o entrega" />
              </div>
              <button class="danger" data-action="plan-product-remove" data-link-id="${escapeHtml(link.linkId)}">Quitar</button>
            </article>
          `).join("")}
        </div>
      `).join("")}
    </div>
  `;
}

function renderPlanProductConnector() {
  const message = state.planProducts.error || state.planProducts.message || "Conecta productos reales de tiendas y la base en nube con comidas del plan.";
  const statusClass = state.planProducts.error ? "status-badge--action" : state.planProducts.loading ? "status-badge--warning" : "status-badge--ok";
  return `
    <div class="panel plan-product-connector" data-plan-product-connector>
      <div class="plan-product-heading">
        <div>
          <span class="data-source-kicker">Fase 4 · Productos y tiendas</span>
          <h4>Productos asociados al plan</h4>
          <p>Busca productos reales en productos profesionales y capturas de tiendas conectadas, revisa nutricion resumida y asocialos a una comida. La lista de compras y las exportaciones los incluyen automaticamente.</p>
        </div>
        <span class="status-badge ${statusClass}">${escapeHtml(message)}</span>
      </div>
      <div class="plan-product-toolbar">
        <label class="field">
          <span>Buscar producto</span>
          <input data-plan-product-search value="${escapeHtml(state.planProducts.search)}" placeholder="Ej. yogur, avena, whey, atun" />
        </label>
        <label class="field">
          <span>Tienda</span>
          <input data-plan-product-retailer value="${escapeHtml(state.planProducts.retailer)}" placeholder="Todas, Olimpica, D1, Jumbo..." />
        </label>
        <label class="field">
          <span>Dia</span>
          <select data-plan-product-day>
            ${planProductDayOptions(state.planProducts.day)}
          </select>
        </label>
        <label class="field">
          <span>Tiempo de comida</span>
          <select data-plan-product-meal>
            ${MEAL_TIME_CATALOG.map((meal) => `<option value="${escapeHtml(meal.id)}" ${meal.id === state.planProducts.mealId ? "selected" : ""}>${escapeHtml(meal.label)}</option>`).join("")}
          </select>
        </label>
        <label class="field">
          <span>Porción g/ml</span>
          <input type="number" min="1" step="5" data-plan-product-serving value="${escapeHtml(state.planProducts.servingGrams)}" />
        </label>
        <button class="primary" data-action="plan-product-search">Buscar en tiendas</button>
      </div>
      ${renderPlanProductResults()}
      <div class="plan-product-linked-wrap">
        <div class="product-admin-table-heading">
          <strong>Productos ya conectados al expediente</strong>
          <span>${state.planProducts.links.length} producto(s)</span>
        </div>
        ${renderPlanProductLinks()}
        ${renderPlanProductProfessionalList()}
      </div>
    </div>
  `;
}

function renderSupplementsProductsRecipes() {
  const supplements = tableRows("suplementos");
  const products = tableRows("productos_recomendados");
  const recipes = tableRows("recetas");
  return `
    <section class="section" id="suplementos">
      <h3>Suplementos</h3>
      <div class="panel">
        <div class="field" style="max-width: 280px; margin-bottom: 16px;">
          <label>Formato</label>
          <select data-supplement-format>
            <option value="destacado" ${state.supplementFormat === "destacado" ? "selected" : ""}>Destacado</option>
            <option value="simple" ${state.supplementFormat === "simple" ? "selected" : ""}>Simple</option>
          </select>
        </div>
        <div class="cards">
          ${supplements.map((item) => choiceCard(item, state.selectedSupplements, "supplement")).join("")}
        </div>
      </div>
    </section>
    <section class="section" id="productos">
      <h3>Productos recomendados</h3>
      <div class="cards">
        ${products.map((item) => choiceCard(item, state.selectedProducts, "product")).join("")}
      </div>
      ${renderPlanProductConnector()}
    </section>
    <section class="section" id="recetas">
      <h3>Recetas</h3>
      <div class="cards">
        ${recipes.map((item) => choiceCard(item, state.selectedRecipes, "recipe")).join("")}
      </div>
    </section>
  `;
}

function productAdminStatusClass() {
  if (state.productAdmin.error) return "status-badge--action";
  if (state.productAdmin.saving || state.productAdmin.loading) return "status-badge--warning";
  return "status-badge--ok";
}

function productAdminField(label, field, type = "text", options = null) {
  const value = state.productAdmin.form[field] ?? "";
  if (options) {
    return `
      <label class="field">
        <span>${escapeHtml(label)}</span>
        <select data-product-field="${escapeHtml(field)}">
          ${options.map((option) => `<option value="${escapeHtml(option.value)}" ${String(value) === String(option.value) ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
        </select>
      </label>
    `;
  }
  if (type === "textarea") {
    return `
      <label class="field">
        <span>${escapeHtml(label)}</span>
        <textarea rows="3" data-product-field="${escapeHtml(field)}">${escapeHtml(value)}</textarea>
      </label>
    `;
  }
  if (type === "checkbox") {
    return `
      <label class="product-admin-check">
        <input type="checkbox" data-product-field="${escapeHtml(field)}" ${value ? "checked" : ""} />
        <span>${escapeHtml(label)}</span>
      </label>
    `;
  }
  return `
    <label class="field">
      <span>${escapeHtml(label)}</span>
      <input type="${escapeHtml(type)}" data-product-field="${escapeHtml(field)}" value="${escapeHtml(value)}" />
    </label>
  `;
}

function productAdminFilter(label, field, type = "text", options = null) {
  const value = state.productAdmin.filters[field] ?? "";
  if (options) {
    return `
      <label class="field">
        <span>${escapeHtml(label)}</span>
        <select data-product-filter="${escapeHtml(field)}">
          ${options.map((option) => `<option value="${escapeHtml(option.value)}" ${String(value) === String(option.value) ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
        </select>
      </label>
    `;
  }
  return `
    <label class="field">
      <span>${escapeHtml(label)}</span>
      <input type="${escapeHtml(type)}" data-product-filter="${escapeHtml(field)}" value="${escapeHtml(value)}" />
    </label>
  `;
}

function renderProductAdminTable() {
  const products = state.productAdmin.products;
  if (!products.length) {
    return `<div class="empty-state">Todavia no hay productos para estos filtros o la API no se ha cargado.</div>`;
  }
  return `
    <div class="table-wrap product-admin-table-wrap">
      <table class="product-admin-table">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Categoria</th>
            <th>Estado</th>
            <th>Oferta</th>
            <th>Accion</th>
          </tr>
        </thead>
        <tbody>
          ${products.map((product) => {
            const active = Number(product.isActive) === 1;
            const selected = state.productAdmin.selectedId === product.id;
            return `
              <tr class="${selected ? "selected-row" : ""}">
                <td>
                  <strong>${escapeHtml(product.displayName)}</strong>
                  <small>${escapeHtml([product.brand, product.packageSize, product.unit].filter(Boolean).join(" · "))}</small>
                </td>
                <td>${escapeHtml([product.categoryLevel1, product.categoryLevel2, product.foodGroup].filter(Boolean).join(" · ") || "Sin categoría")}</td>
                <td>
                  <span class="status-badge ${active ? "status-badge--ok" : "status-badge--action"}">${active ? "Activo" : "Inactivo"}</span>
                  <small>${escapeHtml(product.dataStatus || "pending")}${Number(product.isDemo) ? " · demo" : ""}</small>
                </td>
                <td>${product.bestExactPriceCents ? escapeHtml(formatCurrencyCents(product.bestExactPriceCents, "COP")) : "Sin precio exacto"}<small>${product.offerCount ?? 0} oferta(s)</small></td>
                <td><button data-action="product-select" data-product-id="${escapeHtml(product.id)}">Editar</button></td>
              </tr>
            `;
          }).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderProductAdminPanel() {
  const form = state.productAdmin.form;
  const statusText = state.productAdmin.error
    || state.productAdmin.message
    || (state.productAdmin.loading ? "Cargando productos..." : "Administrador conectado a la base en nube.");
  const tokenReady = productAdminToken() ? "Clave local activa" : "Clave no ingresada";
  const selectedIsInactive = form.id && !form.isActive;
  return `
    <section class="section" id="productos_admin">
      <h3>Administrador de productos alimenticios</h3>
      <div class="product-admin">
        <div class="panel product-admin-hero">
          <div>
            <span class="data-source-kicker">Base en nube</span>
            <h4>Productos, nutricion, ofertas, historial y respaldos</h4>
            <p>Los cambios se guardan en la base real. Al editar o desactivar se crea respaldo automático antes del cambio.</p>
          </div>
          <div class="product-admin-status">
            <span class="status-badge ${productAdminStatusClass()}">${escapeHtml(statusText)}</span>
            <small>${escapeHtml(tokenReady)}${state.productAdmin.lastSyncAt ? ` · Ultima lectura: ${escapeHtml(state.productAdmin.lastSyncAt)}` : ""}</small>
          </div>
        </div>

        <div class="panel product-admin-security">
          <label class="field">
            <span>Clave de administración</span>
            <input type="password" autocomplete="off" data-product-admin-token value="${escapeHtml(state.productAdmin.adminToken)}" placeholder="Clave privada de Cloudflare" />
          </label>
          <div class="product-admin-actions">
            <button data-action="product-clear-token">Olvidar clave</button>
            <button data-action="product-reload">Cargar productos</button>
            <button data-action="product-new">Nuevo producto</button>
          </div>
        </div>

        <div class="panel product-admin-filters">
          ${productAdminFilter("Buscar", "q")}
          ${productAdminFilter("Estado de datos", "dataStatus", "text", [
            { value: "", label: "Todos" },
            { value: "pending", label: "Pendiente" },
            { value: "incomplete", label: "Incompleto" },
            { value: "verified", label: "Verificado" },
            { value: "outdated", label: "Desactualizado" },
          ])}
          ${productAdminFilter("Actividad", "active", "text", [
            { value: "all", label: "Activos e inactivos" },
            { value: "active", label: "Solo activos" },
          ])}
          ${productAdminFilter("Categoria", "category")}
          ${productAdminFilter("Tienda", "retailer")}
          <button class="primary" data-action="product-apply-filters">Filtrar</button>
        </div>

        <div class="product-admin-layout">
          <div class="panel">
            <div class="product-admin-table-heading">
              <strong>Catalogo de productos</strong>
              <span>${state.productAdmin.products.length} producto(s)</span>
            </div>
            ${renderProductAdminTable()}
          </div>

          <div class="panel product-admin-editor">
            <div class="product-admin-table-heading">
              <strong>${form.id ? "Editar producto" : "Nuevo producto"}</strong>
              <span>${form.id ? escapeHtml(form.id) : "ID automático si se deja vacío"}</span>
            </div>
            <div class="form-grid form-grid--wide">
              ${productAdminField("ID", "id")}
              ${productAdminField("Nombre visible", "displayName")}
              ${productAdminField("Marca", "brand")}
              ${productAdminField("Codigo EAN", "barcodeEAN")}
              ${productAdminField("Categoria", "categoryLevel1")}
              ${productAdminField("Subcategoría", "categoryLevel2")}
              ${productAdminField("Grupo alimentario", "foodGroup")}
              ${productAdminField("Presentacion", "packageSize")}
              ${productAdminField("Unidad", "unit")}
              ${productAdminField("Estado de datos", "dataStatus", "text", [
                { value: "pending", label: "Pendiente" },
                { value: "incomplete", label: "Incompleto" },
                { value: "verified", label: "Verificado" },
                { value: "outdated", label: "Desactualizado" },
              ])}
              ${productAdminField("Producto demo / pendiente", "isDemo", "checkbox")}
              ${productAdminField("Activo", "isActive", "checkbox")}
              ${productAdminField("Descripcion", "description", "textarea")}
              ${productAdminField("Ingredientes", "ingredients", "textarea")}
            </div>

            <h4>Nutrición por 100 g/ml</h4>
            <div class="form-grid">
              ${productAdminField("Kcal", "energyKcalPer100g", "number")}
              ${productAdminField("Proteína g", "proteinPer100g", "number")}
              ${productAdminField("Carbohidratos g", "carbohydratesPer100g", "number")}
              ${productAdminField("Grasa total g", "totalFatPer100g", "number")}
              ${productAdminField("Fibra g", "fiberPer100g", "number")}
              ${productAdminField("Sodio mg", "sodiumPer100g", "number")}
              ${productAdminField("Fuente nutricional", "nutritionSourceName")}
              ${productAdminField("URL fuente nutricional", "nutritionSourceUrl", "url")}
            </div>

            <h4>Oferta / tienda principal</h4>
            <div class="form-grid">
              ${productAdminField("Tienda", "retailerName")}
              ${productAdminField("URL producto", "productUrl", "url")}
              ${productAdminField("Precio en centavos", "priceCents", "number")}
              ${productAdminField("Moneda", "currency")}
              ${productAdminField("Disponibilidad", "availability", "text", [
                { value: "pending", label: "Pendiente" },
                { value: "available", label: "Disponible" },
                { value: "out_of_stock", label: "Agotado" },
                { value: "unknown", label: "Desconocida" },
              ])}
              ${productAdminField("Coincidencia", "exactMatchStatus", "text", [
                { value: "manual_review", label: "Revision manual" },
                { value: "probable", label: "Probable" },
                { value: "exact", label: "Exacta" },
              ])}
              ${productAdminField("URL fuente oferta", "sourceUrl", "url")}
            </div>

            <div class="product-admin-actions">
              <button class="primary" data-action="product-save" ${state.productAdmin.saving ? "disabled" : ""}>Guardar en la base</button>
              <button data-action="product-restore" ${selectedIsInactive ? "" : "disabled"}>Reactivar</button>
              <button class="danger" data-action="product-deactivate" ${form.id && form.isActive ? "" : "disabled"}>Desactivar</button>
            </div>
          </div>
        </div>

        <div class="panel product-admin-import-export">
          <div>
            <strong>Importar y exportar</strong>
            <p>JSON, CSV y XLSX sirven para carga masiva o respaldo. Los XLSX comprimidos requieren un navegador moderno.</p>
          </div>
          <div class="product-admin-actions">
            <button data-action="product-export-json">Exportar JSON</button>
            <button data-action="product-export-csv">Exportar CSV</button>
            <button data-action="product-export-xlsx">Exportar XLSX</button>
            <button data-action="product-import">Importar JSON/CSV/XLSX</button>
            <input class="file-import-input" type="file" accept=".json,.csv,.xlsx,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-import-products />
          </div>
        </div>
      </div>
    </section>
  `;
}

function selectedRows(tableName, selectedSet) {
  return tableRows(tableName).filter((row) => selectedSet.has(row.id));
}

function renderSupplementPreview(items) {
  if (!items.length) return "<p>No se seleccionaron suplementos.</p>";
  if (state.supplementFormat === "simple") {
    return `<ul>${items.map((item) => `<li><strong>${escapeHtml(item.nombre)}:</strong> ${escapeHtml(item.indicacion_consumo)}</li>`).join("")}</ul>`;
  }
  return `
    <div class="table-wrap">
      <table>
        <thead><tr><th>Nombre</th><th>Presentacion</th><th>Indicacion</th><th>Observaciones</th></tr></thead>
        <tbody>${items.map((item) => `
          <tr>
            <td>${escapeHtml(item.nombre)}</td>
            <td>${escapeHtml(item.presentacion)}</td>
            <td>${escapeHtml(item.indicacion_consumo)}</td>
            <td>${escapeHtml(item.observaciones)}</td>
          </tr>
        `).join("")}</tbody>
      </table>
    </div>
  `;
}

function renderShoppingList(groups) {
  if (!groups.length) {
    return `<div class="empty-state">La lista de compras se generará al completar el menú o seleccionar productos.</div>`;
  }
  return `
    <div class="shopping-checklist">
      ${groups.map((group) => `
        <section class="shopping-group">
          <h5>${escapeHtml(group.category)} <span>${group.items.length}</span></h5>
          <ul>
            ${group.items.map((item) => {
              const key = shoppingChecklistKey(group.category, item.name, item.source);
              const checked = state.shoppingChecklist.has(key) ? "checked" : "";
              return `
              <li>
                <label class="shopping-item-row">
                  <input type="checkbox" data-shopping-item="${escapeHtml(key)}" aria-label="Marcar ${escapeHtml(item.name)}" ${checked} />
                  <div>
                    <strong>${escapeHtml(item.name)}</strong>
                    ${item.note ? `<small>${escapeHtml(item.note)}</small>` : ""}
                    <em>${escapeHtml(item.source)}</em>
                  </div>
                </label>
              </li>
                `.trim();
            }).join("")}
            `).join("")}
          </ul>
        </section>
      </div>
  `;
}

function renderSharedShoppingListPanel() {
  const statusText = state.sharedShoppingList.error
    || state.sharedShoppingList.message
    || "Crea una lista independiente para el usuario final, sin datos clínicos del paciente.";
  const statusClass = state.sharedShoppingList.error
    ? "status-badge--action"
    : state.sharedShoppingList.creating ? "status-badge--warning" : "status-badge--ok";
  return `
    <div class="shared-shopping-panel" data-shared-shopping-panel>
      <div>
        <span class="data-source-kicker">Fase 5 · lista final</span>
        <strong>Lista compartible para usuario final</strong>
        <p>${escapeHtml(statusText)}</p>
        ${state.sharedShoppingList.url ? `<a class="shared-shopping-link" href="${escapeHtml(state.sharedShoppingList.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(state.sharedShoppingList.url)}</a>` : ""}
      </div>
      <div class="shared-shopping-actions">
        <span class="status-badge ${statusClass}">${state.sharedShoppingList.creating ? "Creando..." : "Lista final"}</span>
        <button class="primary" data-action="create-shared-shopping-list" ${state.sharedShoppingList.creating ? "disabled" : ""}>Crear enlace</button>
        <button data-action="copy-shared-shopping-list" ${state.sharedShoppingList.url ? "" : "disabled"}>Copiar enlace</button>
      </div>
    </div>
  `;
}

function getSectionLabel(sectionId = state.activeSectionId) {
  return sectionNavItems.find(([id]) => id === sectionId)?.[1] || "Dashboard";
}

function getUrlSectionId() {
  const hashSection = decodeURIComponent(window.location.hash || "").replace(/^#/, "").trim();
  if (sectionNavItems.some(([id]) => id === hashSection)) return hashSection;
  const paramSection = new URLSearchParams(window.location.search).get("section");
  return sectionNavItems.some(([id]) => id === paramSection) ? paramSection : "";
}

function syncSectionFromUrl() {
  const sectionId = getUrlSectionId();
  if (!sectionId) return;
  state.activeSectionId = sectionId;
  window.requestAnimationFrame(() => {
    updateSidebarActiveSection();
    scrollToSection(sectionId);
  });
}

function updateSidebarActiveSection() {
  if (!app) return;
  app.querySelectorAll(".nav button[data-scroll]").forEach((button) => {
    const isActive = button.dataset.scroll === state.activeSectionId;
    button.classList.toggle("active", isActive);
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-current", isActive ? "location" : "false");
  });
  document.querySelectorAll("main .section[id]").forEach((section) => {
    section.classList.toggle("is-active-section", section.id === state.activeSectionId);
  });
  const activeChip = app.querySelector("[data-active-section-label]");
  if (activeChip) activeChip.textContent = getSectionLabel();
}

function detectActiveSectionFromViewport() {
  const sectionElements = [...document.querySelectorAll("main .section[id]")];
  if (!sectionElements.length) return state.activeSectionId;
  const topOffset = Math.min(120, window.innerHeight * 0.18);
  let activeSection = sectionElements[0].id;
  let nearestSection = activeSection;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const section of sectionElements) {
    const rect = section.getBoundingClientRect();
    const distance = Math.abs(rect.top - topOffset);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestSection = section.id;
    }
    if (rect.top <= topOffset && rect.bottom > topOffset) {
      return section.id;
    }
    if (rect.top <= topOffset) {
      activeSection = section.id;
    }
  }
  return activeSection || nearestSection;
}

function refreshActiveSectionFromViewport() {
  state.activeSectionId = detectActiveSectionFromViewport();
  updateSidebarActiveSection();
}

function scrollToSection(sectionId) {
  const section = document.getElementById(sectionId);
  if (!section) return;
  const targetTop = Math.max(0, window.scrollY + section.getBoundingClientRect().top - 12);
  window.scrollTo({ top: targetTop, behavior: "smooth" });
}

function bindIntersectionScrollSpy() {
  const sectionElements = [...document.querySelectorAll("main .section[id]")];
  if (!sectionElements.length || !("IntersectionObserver" in window)) return;
  scrollSpyObserver?.disconnect();
  scrollSpyObserver = new IntersectionObserver((entries) => {
    const visibleEntries = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
    if (!visibleEntries.length) return;
    state.activeSectionId = visibleEntries[0].target.id;
    updateSidebarActiveSection();
  }, {
    root: null,
    rootMargin: "-18% 0px -62% 0px",
    threshold: [0, 0.1, 0.35],
  });
  sectionElements.forEach((section) => scrollSpyObserver.observe(section));
}

function bindScrollSpy() {
  const onScroll = () => {
    if (scrollRafId !== null) return;
    scrollRafId = window.requestAnimationFrame(() => {
      scrollRafId = null;
      refreshActiveSectionFromViewport();
    });
  };
  if (state.scrollSpyEnabled) {
    bindIntersectionScrollSpy();
    refreshActiveSectionFromViewport();
    return;
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  state.scrollSpyEnabled = true;
  bindIntersectionScrollSpy();
  refreshActiveSectionFromViewport();
}

function bindSectionReveal() {
  const sectionElements = [...document.querySelectorAll("main .section[id]")];
  if (!sectionElements.length) return;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  sectionRevealObserver?.disconnect();
  sectionElements.forEach((section) => {
    section.classList.toggle("section-animated", !reducedMotion);
    section.classList.add("section-visible");
  });
  if (reducedMotion || !("IntersectionObserver" in window)) return;
  sectionElements.forEach((section) => section.classList.remove("section-visible"));
  sectionRevealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("section-visible");
      }
    });
  }, {
    root: null,
    rootMargin: "0px 0px -12% 0px",
    threshold: [0.05, 0.2],
  });
  sectionElements.forEach((section) => sectionRevealObserver.observe(section));
}

let scrollRafId = null;
let scrollSpyObserver = null;
let sectionRevealObserver = null;
let patientRefreshTimer = null;

function schedulePatientQuietRefresh() {
  if (patientRefreshTimer !== null) {
    window.clearTimeout(patientRefreshTimer);
  }
  patientRefreshTimer = window.setTimeout(() => {
    patientRefreshTimer = null;
    const activePatientField = document.activeElement?.matches("[data-patient-field]")
      ? document.activeElement.dataset.patientField
      : "";
    if (activePatientField) {
      renderAutoSaveTicker();
      refreshCalculatedAgeDisplay();
      if (anthropometryInputFields.has(activePatientField)) {
        refreshAnthropometryResultsDisplay();
      }
      return;
    }
    hydrateGenerated();
    render();
  }, 260);
}

function renderPreview(energy, macros, warnings, intake) {
  const supplements = selectedRows("suplementos", state.selectedSupplements);
  const products = selectedRows("productos_recomendados", state.selectedProducts);
  const recipes = selectedRows("recetas", state.selectedRecipes);
  const planProductLinks = state.planProducts.links;
  const shopping = mergeShoppingListWithPlanProducts(generateShoppingList(state.menu, { products, supplements, recipes }));
  const options = state.deliverableOptions;
  const includeAnthropometry = Boolean(state.activeExportOptions?.includeAnthropometry);
  return `
    <section class="section" id="vista_previa">
      <h3>Vista previa del entregable</h3>
      <div class="preview" id="printable">
        <div class="preview-cover">
          <div>
            <h2 data-preview-title>${escapeHtml(options.title)}</h2>
            <p>${escapeHtml(state.patient.nombre)} · ${escapeHtml(objectiveRules[state.patient.objetivo_principal]?.label ?? "Objetivo individual")}</p>
          </div>
          <div class="food-strip">
            <img src="${getItemImage("aguacate")}" alt="Aguacate" />
            <img src="${getItemImage("banano")}" alt="Banano" />
            <img src="${getItemImage("huevo")}" alt="Huevo" />
          </div>
        </div>
        <div class="summary-list">
          <div><label>GET3 / Calorías</label><strong>${formatNumber(macros.calories, " kcal")}</strong></div>
          <div><label>TMB Harris</label><strong>${formatNumber(energy.harrisBenedict.value, " kcal")}</strong></div>
          <div><label>GET1 Harris</label><strong>${formatNumber(energy.get1.harrisBenedict, " kcal")}</strong></div>
          <div><label>GET2 Harris</label><strong>${formatNumber(energy.get2.harrisBenedict, " kcal")}</strong></div>
          <div><label>NAF</label><strong>${formatDecimal(energy.activityFactor, 2)}</strong></div>
          <div><label>Proteína</label><strong>${macros.protein} g</strong></div>
          <div><label>Carbohidratos</label><strong>${macros.carbs} g</strong></div>
          <div><label>Grasas</label><strong>${macros.fat} g</strong></div>
          <div><label>Adecuacion kcal</label><strong>${formatDecimal(macros.adequacyKcalPercent, 1)}%</strong></div>
        </div>
        <h4>Macronutrientes calculados</h4>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Macro</th><th>g/kg</th><th>g/día</th><th>kcal/día</th><th>VCT %</th></tr></thead>
            <tbody>${macros.table.map((row) => `<tr><td>${escapeHtml(row.macro)}</td><td>${formatDecimal(row.gKg, 2)}</td><td>${formatNumber(row.grams, " g")}</td><td>${formatNumber(row.kcal, " kcal")}</td><td>${formatDecimal(row.vct, 1)}%</td></tr>`).join("")}</tbody>
          </table>
        </div>
        ${energy.literalNotes?.length ? `<p class="professional-note"><strong>Nota GET:</strong> ${escapeHtml(energy.literalNotes[0])}</p>` : ""}
        ${includeAnthropometry ? renderAnthropometryExportSection() : ""}
        ${options.showCurrentIntake ? `
          <h4>Consumo actual estimado</h4>
          <div class="summary-list">
            <div><label>Actual kcal</label><strong data-intake-calories>${formatOptionalNumber(intake.calories, " kcal")}</strong></div>
            <div><label>Brecha vs meta</label><strong data-intake-delta>${formatDelta(intake.calories, macros.calories, " kcal")}</strong></div>
            <div><label>Proteína actual</label><strong data-intake-protein>${formatOptionalNumber(intake.protein, " g")}</strong></div>
            <div><label>Carbohidratos / grasas</label><strong data-intake-carbs-fat>${formatOptionalNumber(intake.carbs, " g")} / ${formatOptionalNumber(intake.fat, " g")}</strong></div>
          </div>
        ` : ""}
        ${options.showWarnings && warnings.length ? `<h4>Alertas</h4><ul>${warnings.map((warning) => `<li>${escapeHtml(warning)}</li>`).join("")}</ul>` : ""}
        <h4>Esquema diario</h4>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Tiempo</th><th>Grupo</th><th>Opciones</th><th>Ejemplo</th><th>Porciones</th></tr></thead>
            <tbody>${state.scheme.map((row) => `<tr><td>${escapeHtml(row.tiempo)}</td><td>${escapeHtml(row.grupo)}</td><td>${escapeHtml(row.opciones)}</td><td>${escapeHtml(row.ejemplo)}</td><td>${escapeHtml(row.porciones)}</td></tr>`).join("")}</tbody>
          </table>
        </div>
        <h4>Menú de 7 días</h4>
        ${state.menu.map((day) => `<h5>Día ${day.day}</h5><ul>${day.meals.map((meal) => `<li><strong>${escapeHtml(meal.label)}:</strong> ${escapeHtml(meal.text)}</li>`).join("")}</ul>`).join("")}
        <h4>Recomendaciones</h4>
        <ol>${state.recommendations.map((rec) => `<li><strong>${escapeHtml(rec.title)}.</strong> ${escapeHtml(rec.text)}</li>`).join("")}</ol>
        ${options.showSupplements ? `<h4>Suplementos</h4>${renderSupplementPreview(supplements)}` : ""}
        ${options.showProducts ? `<h4>Productos recomendados</h4>${visualItemCards(products, "product")}${renderPlanProductPreview(planProductLinks)}` : ""}
        ${options.showRecipes ? `<h4>Recetas</h4>${visualItemCards(recipes, "recipe")}` : ""}
        ${options.showShoppingList ? `<h4>Lista de compras</h4>${renderSharedShoppingListPanel()}${renderShoppingList(shopping)}` : ""}
        ${options.finalNote ? `<p class="professional-note"><strong>Nota profesional:</strong> <span data-preview-final-note>${escapeHtml(options.finalNote)}</span></p>` : ""}
      </div>
    </section>
  `;
}

function renderAnthropometryExportSection() {
  const selected = state.activeExportOptions?.assessmentId
    ? state.assessments.find((item) => item.id === state.activeExportOptions.assessmentId)
    : null;
  const anthropometry = selected?.results || calculateAnthropometry(state.patient);
  const assessmentLabel = selected?.label || "Valoración actual";
  const primary = anthropometry.primaryComposition;
  return `
    <section class="export-anthropometry">
      <h4>Antropometría · ${escapeHtml(assessmentLabel)}</h4>
      <p class="professional-note">Población: ${escapeHtml(selected?.populationProfile === "pediatric" ? "pediátrica" : "adulta")} · Método: ${escapeHtml(selected?.method || state.patient.metodo_antropometria || "pendiente")} · Perfil: ${escapeHtml(selected?.formulaProfile || state.patient.formula_profile || "legacy_excel_2026")} v${escapeHtml(selected?.formulaVersion || state.patient.formula_version || "1.0.0")}</p>
      <div class="summary-list">
        <div><label>IMC</label><strong>${anthropometry.bmi ? `${formatDecimal(anthropometry.bmi, 1)} kg/m²` : "Pendiente"}</strong></div>
        <div><label>Método principal</label><strong>${escapeHtml(primary?.label || "Pendiente")}</strong></div>
        <div><label>Composición</label><strong>${primary?.fatPercent ? `${formatDecimal(primary.fatPercent, 1)}% grasa` : "Pendiente"}</strong></div>
        <div><label>Masa libre de grasa</label><strong>${primary?.leanKg ? `${formatDecimal(primary.leanKg, 1)} kg` : "Pendiente"}</strong></div>
      </div>
      ${renderSelectedAnthropometryCards(anthropometry)}
      ${renderSomatochart(anthropometry.somatotype)}
    </section>
  `;
}

function requestExport(format) {
  state.exportDialog = {
    format,
    includeAnthropometry: format !== "csv" && Boolean(state.activeExportOptions?.includeAnthropometry),
    assessmentId: state.activeExportOptions?.assessmentId || state.assessments.at(-1)?.id || "",
  };
  render();
}

function renderExportDialog() {
  const dialog = state.exportDialog;
  if (!dialog) return "";
  const assessmentOptions = state.assessments.map((assessment) => `<option value="${escapeHtml(assessment.id)}" ${assessment.id === dialog.assessmentId ? "selected" : ""}>${escapeHtml(assessment.label || assessment.id)} · ${escapeHtml(assessment.assessmentDate || "sin fecha")}</option>`).join("");
  const formatLabel = { pdf: "PDF", html: "HTML", json: "JSON", csv: "CSV de compras" }[dialog.format] || dialog.format;
  return `
    <div class="export-dialog-backdrop" role="presentation">
      <section class="export-dialog" role="dialog" aria-modal="true" aria-labelledby="export-dialog-title">
        <button class="icon-toggle export-dialog__close" data-action="export-cancel" aria-label="Cerrar exportación" title="Cerrar exportación">×</button>
        <span>Preparar exportación</span>
        <h3 id="export-dialog-title">Exportar ${escapeHtml(formatLabel)}</h3>
        ${dialog.format === "csv"
          ? `<p class="export-dialog__hint">Este archivo contiene únicamente la lista de compras estructurada.</p>`
          : `<fieldset>
            <legend>Contenido</legend>
            <label><input type="radio" name="export-content" value="plan" data-export-content ${dialog.includeAnthropometry ? "" : "checked"} /> Plan nutricional</label>
            <label><input type="radio" name="export-content" value="anthropometry" data-export-content ${dialog.includeAnthropometry ? "checked" : ""} /> Plan + antropometría y gráficas</label>
          </fieldset>`}
        ${dialog.includeAnthropometry && assessmentOptions ? `<label class="compact-field"><span>Valoración que se incluirá</span><select data-export-assessment>${assessmentOptions}</select></label>` : ""}
        <div class="file-actions">
          <button data-action="export-cancel">Cancelar</button>
          <button class="primary" data-action="export-confirm" data-export-format="${escapeHtml(dialog.format)}">Continuar</button>
        </div>
      </section>
    </div>
  `;
}

function render() {
  if (state.patient && state.currentPlanStartDate && !state.patient.fecha_evaluacion) {
    state.patient.fecha_evaluacion = state.currentPlanStartDate;
  }
  syncCalculatedPatientAge();
  const energy = calculateEnergy(state.patient);
  const macros = calculateMacros(state.patient);
  const intake = calculateCurrentIntake(state.patient);
  const warnings = validatePatient(state.patient);
  const visiblePatientName = getVisiblePatientName(state.patient);
  const topbarTitle = visiblePatientName === "Nuevo paciente" && !state.currentFileId
    ? "Selecciona o crea un paciente"
    : visiblePatientName;
  const planDateLabel = state.currentPlanStartDate ? ` · Inicio ${state.currentPlanStartDate}` : "";
  const mobileNavLabel = state.mobileNavOpen ? "Cerrar panel de navegación" : "Mostrar panel de navegación";
  const desktopNavLabel = state.sidebarCollapsed ? "Mostrar panel de navegación" : "Minimizar panel de navegación";
  const shellClasses = [
    "app-shell",
    state.sidebarCollapsed ? "sidebar-collapsed" : "",
    state.mobileNavOpen ? "nav-open" : "",
  ].filter(Boolean).join(" ");
  app.innerHTML = `
    <div class="${shellClasses}">
      <button type="button" class="mobile-nav-toggle icon-toggle" data-action="toggle-mobile-nav" aria-expanded="${state.mobileNavOpen ? "true" : "false"}" aria-label="${mobileNavLabel}" title="${mobileNavLabel}"><span class="hamburger-icon" aria-hidden="true"><span></span><span></span><span></span></span></button>
      <button type="button" class="nav-backdrop" data-action="toggle-mobile-nav" aria-label="Cerrar navegación"></button>
      <aside class="sidebar">
        <button type="button" class="sidebar-toggle icon-toggle" data-action="toggle-sidebar" aria-expanded="${state.sidebarCollapsed ? "false" : "true"}" aria-label="${desktopNavLabel}" title="${desktopNavLabel}"><span class="hamburger-icon" aria-hidden="true"><span></span><span></span><span></span></span></button>
        <div class="brand">
          <h1>Sistema Digital de Nutrición</h1>
          <span>Planificación clínica, deportiva y educativa</span>
        </div>
        <div class="sidebar-status">
          <span>Estás en</span>
          <strong data-active-section-label>${escapeHtml(getSectionLabel())}</strong>
        </div>
        <div class="sidebar-links" aria-label="Accesos rápidos">
          <a href="#inicio" data-section-link="inicio">Plan</a>
          <a href="#base_datos" data-section-link="base_datos">Base editable</a>
          <a href="#productos_admin" data-section-link="productos_admin">Base productos</a>
          <a href="./base-datos" target="_blank" rel="noopener noreferrer">Base de datos</a>
          <a href="./lista" target="_blank" rel="noopener noreferrer">Lista final</a>
        </div>
        <nav class="nav">
          ${sectionNavItems.map(([id, label], index) => {
            const isActive = state.activeSectionId === id;
            return `<button type="button" class="ghost${isActive ? " active" : ""}" data-scroll="${id}" aria-current="${isActive ? "location" : "false"}" style="--nav-index:${index}">${label}</button>`;
          }).join("")}
        </nav>
      </aside>
      <main class="main">
        <div class="topbar">
          <div class="topbar-title">
            <h2>${escapeHtml(topbarTitle)}</h2>
        <p class="topbar-meta">Plan nutricional activo · ${escapeHtml(objectiveRules[state.patient.objetivo_principal]?.label ?? "Objetivo por definir")}${escapeHtml(planDateLabel)}</p>
          </div>
          <div class="actions">
            <label class="patient-switch">
              <span>Paciente</span>
              <select data-load-patient>
                ${patientSelectOptions().map((patient) => `<option value="${patient.id}" ${patient.id === state.patient.id ? "selected" : ""}>${escapeHtml(getVisiblePatientName(patient))}</option>`).join("")}
              </select>
            </label>
            <button data-action="regenerate">Regenerar</button>
            <a class="utility-link" href="#productos_admin" data-section-link="productos_admin">Base productos</a>
            <a class="utility-link" href="./base-datos" target="_blank" rel="noopener noreferrer">Base de datos</a>
            <a class="utility-link" href="./lista" target="_blank" rel="noopener noreferrer">Lista final</a>
            <button class="primary" data-action="pdf">Exportar PDF</button>
            <button data-action="html">Exportar HTML</button>
            <button data-action="json">Exportar JSON</button>
            <button data-action="csv">Exportar CSV compras</button>
          </div>
        </div>
        ${renderFileControls()}
        ${renderDashboard(energy, macros, warnings)}
        ${renderPatientForm()}
        ${renderAnthropometricSection()}
        ${renderCurrentIntakeSection()}
        ${renderActivitySection()}
        ${renderPreferencesSection()}
        ${renderWarnings(warnings)}
        ${renderCalculations(energy, macros, intake)}
        ${renderScheme()}
        ${renderMenu()}
        ${renderRecommendations()}
        ${renderSupplementsProductsRecipes()}
        ${renderProductAdminPanel()}
        ${renderDataSettings()}
        ${renderUpdateGuide()}
        ${renderDeliverableOptions()}
        ${renderPreview(energy, macros, warnings, intake)}
      </main>
    </div>
    ${renderExportDialog()}
  `;
  bindScrollSpy();
  bindSectionReveal();
  const fileTypeSelect = app.querySelector("[data-new-file-type]");
  if (fileTypeSelect) {
    toggleCustomFileTypeInput(fileTypeSelect);
  }
  renderAutoSaveTicker();
  window.requestAnimationFrame(updateSidebarActiveSection);
}

function isMobileNavigationViewport() {
  return window.matchMedia("(max-width: 1100px)").matches;
}

function closeMobileNavigation() {
  state.mobileNavOpen = false;
  const shell = app.querySelector(".app-shell");
  shell?.classList.remove("nav-open");
  const toggle = app.querySelector("[data-action='toggle-mobile-nav']");
  toggle?.setAttribute("aria-expanded", "false");
  toggle?.setAttribute("aria-label", "Mostrar panel de navegación");
  toggle?.setAttribute("title", "Mostrar panel de navegación");
}

function buildExportPayload() {
  const energy = calculateEnergy(state.patient);
  const macros = calculateMacros(state.patient);
  const intake = calculateCurrentIntake(state.patient);
  const anthropometry = calculateAnthropometry(state.patient);
  const includeAnthropometry = Boolean(state.activeExportOptions?.includeAnthropometry);
  const warnings = validatePatient(state.patient);
  const currentFile = getCurrentFile();
  const supplements = selectedRows("suplementos", state.selectedSupplements);
  const products = selectedRows("productos_recomendados", state.selectedProducts);
  const recipes = selectedRows("recetas", state.selectedRecipes);
  const planProductLinks = structuredClone(state.planProducts.links);
  const rawShoppingList = mergeShoppingListWithPlanProducts(generateShoppingList(state.menu, { products, supplements, recipes }));
  const shoppingList = rawShoppingList.map((group) => ({
    category: group.category,
    items: group.items.map((item) => {
      const key = shoppingChecklistKey(group.category, item.name, item.source);
      return {
        ...item,
        checked: state.shoppingChecklist.has(key),
      };
    }),
  }));
  return {
    metadata: {
      app: "Sistema Digital de Nutrición",
      version: "0.1.0",
      exportedAt: new Date().toISOString(),
      exportFormats: ["pdf", "html", "json", "csv"],
      content: includeAnthropometry ? "plan_plus_anthropometry" : "plan_nutricional",
      assessmentId: includeAnthropometry ? (state.activeExportOptions?.assessmentId || "") : "",
      file: currentFile
        ? {
          id: currentFile.id,
          name: currentFile.name,
          type: currentFile.type,
          patientId: currentFile.patientId,
          planStartDate: currentFile.planStartDate || state.currentPlanStartDate,
          updatedAt: currentFile.updatedAt,
        }
        : null,
    },
    patient: state.patient,
    warnings,
    calculations: includeAnthropometry ? { energy, macros, intake, anthropometry } : { energy, macros, intake },
    assessments: includeAnthropometry ? structuredClone(state.assessments) : [],
    deliverableOptions: structuredClone(state.deliverableOptions),
    scheme: state.scheme,
    menu: state.menu,
    recommendations: state.recommendations,
    supplements,
    products,
    planProducts: {
      source: "Base en nube",
      defaultDay: state.planProducts.day,
      defaultMealId: state.planProducts.mealId,
      defaultServingGrams: state.planProducts.servingGrams,
      defaultRetailer: state.planProducts.retailer,
      links: planProductLinks,
      professionalList: planProductProfessionalRows(),
      shoppingItems: planProductShoppingItems(),
    },
    recipes,
    shoppingList,
  };
}

function downloadJsonExport() {
  const payload = buildExportPayload();
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  downloadBlob(blob, `${exportFileBase()}_entregable.json`);
}

function shoppingListToCsv(groups) {
  const rows = [["categoria", "item", "nota", "origen"]];
  for (const group of groups) {
    for (const item of group.items) {
      rows.push([group.category, item.name, item.note, item.source]);
    }
  }
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

function downloadShoppingCsv() {
  const payload = buildExportPayload();
  const csv = `\ufeff${shoppingListToCsv(payload.shoppingList)}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `${exportFileBase()}_lista_compras.csv`);
}

async function createSharedShoppingList() {
  if (!productAdminToken()) {
    state.sharedShoppingList.error = "Ingresa la clave de administración en Admin productos para publicar la lista final.";
    state.sharedShoppingList.message = "";
    render();
    return;
  }
  state.sharedShoppingList.creating = true;
  state.sharedShoppingList.error = "";
  state.sharedShoppingList.message = "Creando lista en la base en nube...";
  render();
  try {
    const response = await fetch("/api/shopping-lists", {
      method: "POST",
      headers: productAdminHeaders(),
      body: JSON.stringify(buildSharedShoppingListPayload()),
    });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || "No se pudo crear la lista compartible.");
    const url = new URL(payload.url, window.location.origin).href;
    state.sharedShoppingList.creating = false;
    state.sharedShoppingList.url = url;
    state.sharedShoppingList.message = "Lista final creada. El enlace no incluye datos clínicos ni nombre del paciente.";
    state.sharedShoppingList.error = "";
    render();
  } catch (error) {
    state.sharedShoppingList.creating = false;
    state.sharedShoppingList.error = error?.message || "No se pudo crear la lista compartible.";
    state.sharedShoppingList.message = "";
    render();
  }
}

async function copySharedShoppingListUrl() {
  if (!state.sharedShoppingList.url) return;
  try {
    await navigator.clipboard.writeText(state.sharedShoppingList.url);
    state.sharedShoppingList.message = "Enlace copiado al portapapeles.";
    state.sharedShoppingList.error = "";
  } catch {
    state.sharedShoppingList.message = "El enlace quedo visible para copiarlo manualmente.";
  }
  render();
}

async function assetToDataUrl(src) {
  try {
    const response = await fetch(new URL(src, window.location.href));
    if (!response.ok) return src;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return src;
  }
}

async function clonePrintableHtml() {
  const printable = document.querySelector("#printable");
  if (!printable) throw new Error("No se encontro la vista imprimible.");
  const clone = printable.cloneNode(true);
  clone.removeAttribute("id");
  const images = [...clone.querySelectorAll("img")];
  await Promise.all(images.map(async (img) => {
    img.src = await assetToDataUrl(img.getAttribute("src"));
  }));
  return clone.outerHTML;
}

async function collectExportCss() {
  const paths = ["src/styles/tokens.css", "src/styles/app.css", "src/styles/print.css"];
  const files = await Promise.all(paths.map(async (path) => {
    try {
      const response = await fetch(new URL(path, window.location.href));
      return response.ok ? response.text() : "";
    } catch {
      return "";
    }
  }));
  return `${files.join("\n").replaceAll(/@import\s+[^;]+;/g, "")}
    body { background: #fffaf3; }
    .export-page { max-width: 1180px; margin: 0 auto; padding: 24px; }
    .preview { box-shadow: none; }
    @media print {
      .export-page { max-width: none; padding: 0; }
      .preview { border: 0; padding: 0; }
    }
  `;
}

function buildStandaloneHtml(printableHtml, css) {
  const title = `Sistema Digital de Nutrición - ${state.patient.nombre}`;
  return `<!doctype html>
<html lang="es">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${escapeHtml(title)}</title>
    <style>${css}</style>
  </head>
  <body>
    <main class="export-page">${printableHtml}</main>
  </body>
</html>`;
}

async function buildStandaloneExportHtml() {
  const [printableHtml, css] = await Promise.all([clonePrintableHtml(), collectExportCss()]);
  return buildStandaloneHtml(printableHtml, css);
}

async function downloadHtmlExport() {
  const html = await buildStandaloneExportHtml();
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  downloadBlob(blob, `${exportFileBase()}_entregable.html`);
}

async function openPdfExport() {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    window.print();
    return;
  }
  printWindow.document.write("<p>Preparando vista PDF...</p>");
  const html = await buildStandaloneExportHtml();
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 500);
}

app.addEventListener("change", (event) => {
  const target = event.target;
  if (target.matches("[data-import-products]")) {
    importProductAdminFile(target);
    return;
  }
  if (target.matches("[data-product-filter]")) {
    updateProductAdminFilter(target.dataset.productFilter, target.value);
    return;
  }
  if (target.matches("[data-product-field]")) {
    updateProductAdminField(target.dataset.productField, target.type === "checkbox" ? target.checked : target.value, target.type);
    return;
  }
  if (target.matches("[data-import-patient-file]")) {
    importPatientFileFromInput(target);
    return;
  }
  if (target.matches("[data-import-nutrition-data]")) {
    importNutritionDataFromInput(target);
    return;
  }
  if (target.matches("[data-import-table-csv]")) {
    importSelectedTableCsvFromInput(target);
    return;
  }
  if (target.matches("[data-import-professional-validation-csv]")) {
    importProfessionalValidationCsvFromInput(target);
    return;
  }
  if (target.matches("[data-db-table-select]")) {
    state.selectedDataTableName = target.value;
    render();
    return;
  }
  if (target.matches("[data-validation-field]")) {
    updateProfessionalValidationDecision(target.dataset.validationId, target.dataset.validationField, target.value);
    if (target.dataset.validationField === "decision_profesional") {
      render();
    }
    return;
  }
  if (target.matches("[data-shopping-item]")) {
    const key = target.dataset.shoppingItem;
    if (target.checked) state.shoppingChecklist.add(key);
    else state.shoppingChecklist.delete(key);
    markPendingChanges();
    renderAutoSaveTicker();
    return;
  }
  if (target.matches("[data-deliverable-toggle]")) {
    updateDeliverableOption(target.dataset.deliverableToggle, target.checked);
    render();
    return;
  }
  if (target.matches("[data-export-content]")) {
    if (state.exportDialog) {
      state.exportDialog.includeAnthropometry = target.value === "anthropometry";
      state.exportDialog.assessmentId = state.exportDialog.includeAnthropometry
        ? (state.exportDialog.assessmentId || state.assessments.at(-1)?.id || "")
        : "";
      render();
    }
    return;
  }
  if (target.matches("[data-export-assessment]")) {
    if (state.exportDialog) state.exportDialog.assessmentId = target.value;
    return;
  }
  if (target.matches("[data-anthro-section]")) {
    const activeSections = new Set(getActiveAnthropometrySections(state.patient));
    if (target.checked) activeSections.add(target.dataset.anthroSection);
    else activeSections.delete(target.dataset.anthroSection);
    state.patient.antropometria_secciones_activas = normalizeAnthropometrySections([...activeSections]);
    markPendingChanges();
    schedulePatientQuietRefresh();
    return;
  }
  if (target.matches("[data-patient-field]")) {
    const fieldName = target.dataset.patientField;
    updatePatient(fieldName, target.value);
    refreshCalculatedAgeDisplay();
    if (target.tagName === "SELECT") {
      hydrateGenerated();
      render();
      return;
    }
    schedulePatientQuietRefresh();
    return;
  }
  if (target.matches("[data-new-file-name]")) {
    state.newFileDraft.name = target.value.trim();
    const currentFile = getCurrentFile();
    if (currentFile && state.newFileDraft.name) {
      currentFile.name = state.newFileDraft.name;
      markPendingChanges();
    }
  }
  if (target.matches("[data-new-file-type]")) {
    if (target.value === FILE_TYPE_CUSTOM_VALUE) {
      state.newFileDraft.type = "";
      toggleCustomFileTypeInput(target);
      return;
    }
    state.newFileDraft.type = target.value;
    const currentFile = getCurrentFile();
    if (currentFile) {
      currentFile.type = state.newFileDraft.type;
      markPendingChanges();
    }
    toggleCustomFileTypeInput(target);
  }
  if (target.matches("[data-new-file-type-custom]")) {
    state.newFileDraft.type = target.value.trim();
    const currentFile = getCurrentFile();
    if (currentFile) {
      currentFile.type = state.newFileDraft.type || DEFAULT_FILE_TYPE;
      markPendingChanges();
    }
  }
  if (target.matches("[data-meal-time]")) {
    const changed = togglePatientMealTime(target.dataset.mealTime, target.checked);
    if (!changed) {
      render();
      return;
    }
    hydrateGenerated();
    render();
    return;
  }
  if (target.matches("[data-plan-start-date]")) {
    state.currentPlanStartDate = target.value || formatDateForFile();
    state.patient.fecha_evaluacion = state.currentPlanStartDate;
    syncCalculatedPatientAge();
    const currentFile = getCurrentFile();
    if (currentFile) currentFile.planStartDate = state.currentPlanStartDate;
    markPendingChanges();
    renderAutoSaveTicker();
    return;
  }
  if (target.matches("[data-load-file]")) {
    if (!target.value) {
      if (state.currentFileId) {
        saveCurrentFile();
        clearAutoSaveInterval();
      }
      state.currentFileId = null;
      state.hasPendingChanges = false;
      state.saveStatus = "saved";
      state.lastAutoSaveAt = null;
      state.shoppingChecklist = new Set();
      state.planProducts.links = [];
      state.planProducts.results = [];
      state.planProducts.message = "";
      state.planProducts.error = "";
      state.currentPlanStartDate = formatDateForFile();
      persistPatientFiles();
      syncDraftFromPatient(state.patient, true);
      render();
      return;
    }
    if (state.currentFileId && state.currentFileId !== target.value) {
      saveCurrentFile();
      clearAutoSaveInterval();
    }
    loadFileById(target.value);
    return;
  }
  if (target.matches("[data-load-patient]")) {
    const patient = patientSelectOptions().find((item) => item.id === target.value);
    if (!patient) return;
    if (state.currentFileId) {
      saveCurrentFile();
      clearAutoSaveInterval();
    }
    resetPatientWorkspace(structuredClone(patient));
    persistPatientFiles();
    render();
  }
  if (target.matches("[data-choice-kind]")) {
    const map = {
      supplement: state.selectedSupplements,
      product: state.selectedProducts,
      recipe: state.selectedRecipes,
    };
    const set = map[target.dataset.choiceKind];
    if (target.checked) set.add(target.dataset.choiceId);
    else set.delete(target.dataset.choiceId);
    markPendingChanges();
    render();
  }
  if (target.matches("[data-supplement-format]")) {
    state.supplementFormat = target.value;
    markPendingChanges();
    render();
  }
  if (target.matches("[data-plan-product-meal]")) {
    state.planProducts.mealId = target.value;
    markPendingChanges();
  }
  if (target.matches("[data-plan-product-day]")) {
    state.planProducts.day = normalizePlanProductDay(target.value);
    markPendingChanges();
  }
  if (target.matches("[data-plan-product-retailer]")) {
    state.planProducts.retailer = target.value;
    markPendingChanges();
  }
});

app.addEventListener("input", (event) => {
  const target = event.target;
  if (target.matches("[data-plan-product-search]")) {
    state.planProducts.search = target.value;
    return;
  }
  if (target.matches("[data-plan-product-retailer]")) {
    state.planProducts.retailer = target.value;
    return;
  }
  if (target.matches("[data-plan-product-note]")) {
    updatePlanProductNote(target.dataset.planProductNote, target.value);
    return;
  }
  if (target.matches("[data-plan-product-serving]")) {
    state.planProducts.servingGrams = normalizePlanProductServing(target.value);
    markPendingChanges();
    return;
  }
  if (target.matches("[data-plan-product-serving-edit]")) {
    updatePlanProductServing(target.dataset.planProductServingEdit, target.value);
    return;
  }
  if (target.matches("[data-product-admin-token]")) {
    state.productAdmin.adminToken = target.value;
    localStorageSet(STORAGE_PRODUCT_ADMIN_TOKEN, target.value);
    return;
  }
  if (target.matches("[data-patient-cloud-token]")) {
    state.patientCloud.token = target.value;
    if (target.value.trim()) {
      localStorageSet(STORAGE_PATIENT_CLOUD_TOKEN, target.value.trim());
      setPatientCloudStatus({ message: "Clave guardada en este navegador. Ya puedes cargar o guardar nube.", error: "" });
    } else {
      localStorageRemove(STORAGE_PATIENT_CLOUD_TOKEN);
      setPatientCloudStatus({ message: "", error: "" });
    }
    return;
  }
  if (target.matches("[data-product-filter]")) {
    updateProductAdminFilter(target.dataset.productFilter, target.value);
    return;
  }
  if (target.matches("[data-product-field]")) {
    updateProductAdminField(target.dataset.productField, target.type === "checkbox" ? target.checked : target.value, target.type);
    return;
  }
  if (target.matches("[data-db-cell]")) {
    updateDataCell(target.dataset.dbTable, target.dataset.rowIndex, target.dataset.column, target.value);
    return;
  }
  if (target.matches("[data-patient-field]")) {
    const fieldName = target.dataset.patientField;
    updatePatient(fieldName, target.value);
    if (fieldName === "fecha_nacimiento" || fieldName === "fecha_evaluacion") {
      refreshCalculatedAgeDisplay();
    }
    if (fieldName.startsWith("consumo_")) {
      updateCurrentIntakeDisplays();
    }
    if (anthropometryInputFields.has(fieldName)) {
      schedulePatientQuietRefresh();
    }
    return;
  }
  if (target.matches("[data-scheme-id]")) {
    const row = state.scheme.find((item) => item.id === target.dataset.schemeId);
    if (row) row[target.dataset.schemeField] = target.value;
    markPendingChanges();
  }
  if (target.matches("[data-day][data-meal]")) {
    const day = state.menu.find((item) => item.day === Number(target.dataset.day));
    const meal = day?.meals.find((item) => item.meal === target.dataset.meal);
    if (meal) {
      meal.text = target.value;
      markPendingChanges();
    }
  }
  if (target.matches("[data-rec-id]")) {
    const rec = state.recommendations.find((item) => item.id === target.dataset.recId);
    if (rec) {
      rec.text = target.value;
      markPendingChanges();
    }
  }
  if (target.matches("[data-deliverable-field]")) {
    updateDeliverableOption(target.dataset.deliverableField, target.value);
    if (target.dataset.deliverableField === "title") {
      app.querySelector("[data-preview-title]")?.replaceChildren(document.createTextNode(state.deliverableOptions.title || DEFAULT_DELIVERABLE_OPTIONS.title));
    }
    if (target.dataset.deliverableField === "finalNote") {
      app.querySelector("[data-preview-final-note]")?.replaceChildren(document.createTextNode(state.deliverableOptions.finalNote));
    }
    renderAutoSaveTicker();
  }
  if (target.matches("[data-validation-field]")) {
    updateProfessionalValidationDecision(target.dataset.validationId, target.dataset.validationField, target.value);
  }
});

app.addEventListener("click", (event) => {
  const sectionLink = event.target.closest("a[data-section-link]");
  if (sectionLink) {
    event.preventDefault();
    const sectionId = sectionLink.dataset.sectionLink;
    state.activeSectionId = sectionId;
    updateSidebarActiveSection();
    if (isMobileNavigationViewport()) {
      closeMobileNavigation();
    }
    history.replaceState(null, "", `#${sectionId}`);
    window.requestAnimationFrame(() => scrollToSection(sectionId));
    return;
  }
  const target = event.target.closest("button");
  if (!target) return;
  if (target.dataset.scroll) {
    const sectionId = target.dataset.scroll;
    state.activeSectionId = sectionId;
    updateSidebarActiveSection();
    if (isMobileNavigationViewport()) {
      closeMobileNavigation();
    }
    history.replaceState(null, "", `#${sectionId}`);
    window.requestAnimationFrame(() => scrollToSection(sectionId));
  }
  if (target.dataset.action === "toggle-sidebar") {
    state.sidebarCollapsed = !state.sidebarCollapsed;
    render();
  }
  if (target.dataset.action === "toggle-mobile-nav") {
    state.mobileNavOpen = !state.mobileNavOpen;
    render();
  }
  if (target.dataset.action === "product-reload") {
    loadProductAdminList();
  }
  if (target.dataset.action === "product-apply-filters") {
    loadProductAdminList({ selectId: "" });
  }
  if (target.dataset.action === "product-new") {
    resetProductAdminForm();
  }
  if (target.dataset.action === "product-select") {
    loadProductAdminDetail(target.dataset.productId);
  }
  if (target.dataset.action === "product-save") {
    saveProductAdminProduct();
  }
  if (target.dataset.action === "product-deactivate") {
    deactivateProductAdminProduct();
  }
  if (target.dataset.action === "product-restore") {
    restoreProductAdminProduct();
  }
  if (target.dataset.action === "product-clear-token") {
    state.productAdmin.adminToken = "";
    localStorageRemove(STORAGE_PRODUCT_ADMIN_TOKEN);
    productAdminSetStatus({ message: "Clave local eliminada de este navegador." });
    render();
  }
  if (target.dataset.action === "product-export-json") exportProductAdminJson();
  if (target.dataset.action === "product-export-csv") exportProductAdminCsv();
  if (target.dataset.action === "product-export-xlsx") exportProductAdminXlsx();
  if (target.dataset.action === "product-import") {
    app.querySelector("[data-import-products]")?.click();
  }
  if (target.dataset.action === "plan-product-search") {
    searchPlanProducts();
  }
  if (target.dataset.action === "plan-product-add") {
    addPlanProductToPlan(target.dataset.productId);
  }
  if (target.dataset.action === "plan-product-remove") {
    removePlanProductFromPlan(target.dataset.linkId);
  }
  if (target.dataset.action === "create-shared-shopping-list") {
    createSharedShoppingList();
  }
  if (target.dataset.action === "copy-shared-shopping-list") {
    copySharedShoppingListUrl();
  }
  if (target.dataset.action === "regenerate") {
    hydrateGenerated();
    markPendingChanges();
    render();
  }
  if (target.dataset.action === "new-patient") {
    createBlankPatientFile();
  }
  if (target.dataset.action === "create-file") {
    createNewPatientFile();
  }
  if (target.dataset.action === "save-file") {
    saveOnDemand();
    render();
  }
  if (target.dataset.action === "patient-cloud-save") {
    if (!state.currentFileId) saveOnDemand();
    else saveCurrentFile();
    const currentFile = getCurrentFile();
    if (currentFile) syncPatientFileToCloud(currentFile);
  }
  if (target.dataset.action === "patient-cloud-copy-link") {
    copyPatientCloudLink();
  }
  if (target.dataset.action === "patient-cloud-load") {
    loadPatientFilesFromCloud();
  }
  if (target.dataset.action === "conflict-use-cloud") {
    resolvePatientCloudConflict("cloud");
  }
  if (target.dataset.action === "conflict-use-local") {
    resolvePatientCloudConflict("local");
  }
  if (target.dataset.action === "create-initial-assessment") {
    createAssessmentRecord("initial");
  }
  if (target.dataset.action === "create-control-assessment") {
    createAssessmentRecord("control");
  }
  if (target.dataset.action === "load-assessment") {
    loadAssessmentFromHistory(target.dataset.assessmentId);
  }
  if (target.dataset.action === "duplicate-file") {
    duplicateCurrentFile();
  }
  if (target.dataset.action === "export-file") {
    downloadPatientFileExport();
  }
  if (target.dataset.action === "import-file") {
    app.querySelector("[data-import-patient-file]")?.click();
  }
  if (target.dataset.action === "delete-file") {
    deleteCurrentFile();
  }
  if (target.dataset.action === "export-data") {
    exportNutritionData();
  }
  if (target.dataset.action === "import-data") {
    app.querySelector("[data-import-nutrition-data]")?.click();
  }
  if (target.dataset.action === "restore-data") {
    restoreDefaultNutritionData();
  }
  if (target.dataset.action === "clear-data-override") {
    clearSavedNutritionDataOverride();
  }
  if (target.dataset.action === "add-data-row") {
    addDataRow();
  }
  if (target.dataset.action === "duplicate-data-row") {
    duplicateDataRow(target.dataset.rowIndex);
  }
  if (target.dataset.action === "delete-data-row") {
    deleteDataRow(target.dataset.rowIndex);
  }
  if (target.dataset.action === "save-data") {
    saveEditedNutritionData();
  }
  if (target.dataset.action === "export-table-csv") {
    exportSelectedTableCsv();
  }
  if (target.dataset.action === "import-table-csv") {
    app.querySelector("[data-import-table-csv]")?.click();
  }
  if (target.dataset.action === "export-professional-validation") {
    downloadProfessionalValidationCsv();
  }
  if (target.dataset.action === "import-professional-validation") {
    app.querySelector("[data-import-professional-validation-csv]")?.click();
  }
  if (target.dataset.action === "export-cancel") {
    state.exportDialog = null;
    render();
  }
  if (target.dataset.action === "export-confirm") {
    const dialog = state.exportDialog;
    if (!dialog) return;
    state.activeExportOptions = {
      includeAnthropometry: Boolean(dialog.includeAnthropometry),
      assessmentId: dialog.includeAnthropometry ? (dialog.assessmentId || state.assessments.at(-1)?.id || "") : "",
    };
    state.exportDialog = null;
    render();
    window.setTimeout(() => {
      const task = dialog.format === "pdf"
        ? openPdfExport()
        : dialog.format === "html"
          ? downloadHtmlExport()
          : dialog.format === "json"
            ? downloadJsonExport()
            : downloadShoppingCsv();
      if (task?.catch) task.catch((error) => window.alert(error?.message || "No se pudo preparar la exportación."));
    }, 0);
  }
  if (target.dataset.action === "pdf") requestExport("pdf");
  if (target.dataset.action === "html") requestExport("html");
  if (target.dataset.action === "json") requestExport("json");
  if (target.dataset.action === "csv") requestExport("csv");
});

async function bootstrap() {
  try {
    const response = await fetch(dataUrl);
    const defaultData = validateNutritionDataPayload(await response.json());
    state.defaultData = structuredClone(defaultData);
    const storedData = readNutritionDataOverrideFromStorage();
    state.data = storedData || structuredClone(defaultData);
    state.dataSourceLabel = storedData ? "Base personalizada guardada en este navegador" : "Base del proyecto";
    state.professionalValidationDecisions = readProfessionalValidationDecisionsFromStorage();
    state.productAdmin.adminToken = localStorageGet(STORAGE_PRODUCT_ADMIN_TOKEN) || "";
    state.patientCloud.token = localStorageGet(STORAGE_PATIENT_CLOUD_TOKEN) || "";
    state.patientCloud.workspaceId = localStorageGet(STORAGE_PATIENT_WORKSPACE_ID) || "";
    state.patientCloud.workspaceKey = localStorageGet(STORAGE_PATIENT_WORKSPACE_KEY) || "";
    applyPatientCloudWorkspaceFromHash();
    ensurePatientCloudWorkspace();
    state.patientFiles = await readPatientFilesFromPersistentStorage();
    const activeFileId = await readActiveFileIdFromPersistentStorage();
    const activeFile = activeFileId ? state.patientFiles.find((item) => item.id === activeFileId) : null;
    if (activeFile) {
      loadFileIntoState(activeFile);
    } else {
      state.patient = normalizePatientProfile({
        ...DEFAULT_PATIENT_PROFILE,
        id: generateEntityId("paciente"),
        nombre: "Nuevo paciente",
      });
      state.currentPlanStartDate = formatDateForFile();
      ensurePatientInCatalog(state.patient);
      hydrateGenerated();
      syncDraftFromPatient(state.patient, true);
    }
    if (!state.currentFileId && !state.newFileDraft.name) {
      syncDraftFromPatient(state.patient, true);
    }
    render();
    syncSectionFromUrl();
    loadProductAdminList();
    loadClinicalStatus({ silent: true }).then((available) => {
      if (!available) {
        render();
        return;
      }
      loadPatientFilesFromCloud({ silent: true }).then(() => {
        const currentFile = getCurrentFile();
        if (currentFile) syncPatientFileToCloud(currentFile, { silent: true });
      });
    });
  } catch (error) {
    app.innerHTML = `<div class="main"><div class="panel"><h1>No se pudo cargar la base de datos</h1><p>${escapeHtml(error.message)}</p></div></div>`;
  }
}

window.addEventListener("beforeunload", () => {
  saveCurrentFile();
  clearAutoSaveInterval();
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    saveCurrentFile();
  }
});

window.addEventListener("online", () => {
  state.isOnline = true;
  state.saveStatus = state.hasPendingChanges ? "pending" : "saved";
  renderAutoSaveTicker();
  loadClinicalStatus({ silent: true }).then((available) => {
    if (!available) return;
    loadPatientFilesFromCloud({ silent: true }).then(() => {
      const currentFile = getCurrentFile();
      if (currentFile) syncPatientFileToCloud(currentFile, { silent: true });
    });
  });
});

window.addEventListener("offline", () => {
  state.isOnline = false;
  state.saveStatus = "offline";
  renderAutoSaveTicker();
});

window.addEventListener("hashchange", syncSectionFromUrl);

await bootstrap();

